import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import Autocomplete from '@material-ui/lab/Autocomplete';
import MuiAlert from '@material-ui/lab/Alert';
import MenuItem from '@material-ui/core/MenuItem';
import {
  Box,
  Button,
  Card,
  CardContent,
  Tabs,
  Tab,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  Grid,
  TextField,
  Typography,
  makeStyles,
  Divider,
  CardHeader,
  Avatar,
  IconButton,
  InputAdornment,
  Snackbar,
  Popover,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Badge,
} from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import EventIcon from '@material-ui/icons/Event';
import Add from '@material-ui/icons/Add';
import Remove from '@material-ui/icons/Remove';
import CloseIcon from '@material-ui/icons/Close';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import PersonIcon from '@material-ui/icons/Person';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import FilterListIcon from '@material-ui/icons/FilterList';
import Tooltip from '@material-ui/core/Tooltip';
import VideoCallIcon from '@material-ui/icons/VideoCall';
import { useSelector } from 'react-redux';
import { Calendar, momentLocalizer, Views } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import './calendarClinic.custom.css';
import SplitBillingPatientPaymentBox from './SplitBillingPatientPaymentBox';
import VideoCallModal from '../../../../components/VideoCall/VideoCallModal';

import {
  calculateSplitBillingPatientAmount,
  getInsuranceCoveragePerAppointment,
  hasInsuranceSplitBilling,
  parseOptionalNumber,
} from './calendarBillingSplitUtils';

const ACTIVE_VIDEO_CALL_SESSION_KEY = 'virtus.videoCall.active';

const isVideoCallAlreadyActive = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return window.sessionStorage.getItem(ACTIVE_VIDEO_CALL_SESSION_KEY) === '1';
  } catch (error) {
    return false;
  }
};

const warnIfVideoCallAlreadyActive = () => {
  if (!isVideoCallAlreadyActive()) {
    return false;
  }

  window.alert('A videocall is already open. Close the current call before starting another one.');
  return true;
};

moment.updateLocale('en', {
  week: {
    dow: 1,
    doy: 4,
  },
});

const localizer = momentLocalizer(moment);
const DnDCalendar = withDragAndDrop(Calendar);
const DEFAULT_BOOKING_COLOR = '#3174ad';
const CLASS_ATTENDANCE_NOTE_MARKER = '[CLASS_ATTENDANCE_RECORD]';
const EMPTY_PROFILES = [];
const getDefaultBookingWindow = () => ({
  start_date: moment().startOf('week').subtract(1, 'week').startOf('day').toISOString(),
  end_date: moment().startOf('week').add(1, 'week').endOf('week').toISOString(),
});
const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (character) => ({
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
}[character]));
const ALLOCATION_COLOR_PALETTE = [
  '#2F6B99',
  '#4A7C59',
  '#8A5A44',
  '#6B5B95',
  '#9B4F64',
  '#2B7A78',
  '#7A6A4F',
  '#5C6F91',
];

const normalizeAllocationColor = (colorValue) => {
  const sanitized = String(colorValue || '').trim();
  if (!sanitized) {
    return '';
  }

  const prefixed = sanitized.startsWith('#') ? sanitized : `#${sanitized}`;
  const expanded = prefixed.slice(1).length === 3
    ? prefixed.slice(1).split('').map((part) => `${part}${part}`).join('')
    : prefixed.slice(1);

  if (!/^[0-9a-fA-F]{6}$/.test(expanded)) {
    return '';
  }

  return `#${expanded.toUpperCase()}`;
};

const getAllocationDefaultColor = (seedValue) => {
  const normalizedSeed = String(seedValue || '').trim();
  if (!normalizedSeed) {
    return ALLOCATION_COLOR_PALETTE[0];
  }

  const hashValue = normalizedSeed.split('').reduce((accumulator, character) => ((accumulator * 31) + character.charCodeAt(0)) >>> 0, 0);
  return ALLOCATION_COLOR_PALETTE[hashValue % ALLOCATION_COLOR_PALETTE.length];
};

const backendBaseUrl = (
  process.env.NODE_ENV === 'development'
    ? 'http://127.0.0.1:8000'
    : process.env.REACT_APP_BACKEND_URL || 'https://api.virtus-one.com'
).replace(/\/$/, '');

const normalizeAppointmentType = (booking) => String(
  booking?.appointment_type
  || booking?.appointmentType
  || booking?.appointment_type_raw
  || booking?.booking_kind
  || '',
)
  .trim()
  .toLowerCase()
  .replace(/\s+/g, '_');

const isVideoConsultationBooking = (booking) => normalizeAppointmentType(booking) === 'video_consultation';

const SELECT_MENU_PROPS = {
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left',
  },
  getContentAnchorEl: null,
};

let stripePromise;

const getStripePromise = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_default');
  }
  return stripePromise;
};
const WEEKDAY_OPTIONS = [
  { value: 0, label: 'Mon' },
  { value: 1, label: 'Tue' },
  { value: 2, label: 'Wed' },
  { value: 3, label: 'Thu' },
  { value: 4, label: 'Fri' },
  { value: 5, label: 'Sat' },
  { value: 6, label: 'Sun' },
];

const WEEKDAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const DEFAULT_OPENING_HOURS_DAY = {
  enabled: false,
  start: '09:00',
  end: '17:00',
};

const INSURANCE_PAYMENT_METHOD_OPTIONS = {
  MAIL: 'Invoice (mail)',
  EAN: 'Invoice (EAN)',
  SPLIT_MAIL: 'Split billing (mail)',
  SPLIT_EAN: 'Split billing (EAN)',
};

const PATIENT_PAYMENT_METHOD_OPTIONS = [
  { value: 'MobilePay', label: 'MobilePay' },
  { value: 'Card', label: 'Card (terminal)' },
  { value: 'CardAutopay', label: 'Card (stripe)' },
  { value: 'Cash', label: 'Cash' },
];

const getBookingPaymentMethodOptions = ({ bookingMode, selectedPayer, payerReferralOptions, bookedPrice }) => {
  const baseOptions = [
    ...PATIENT_PAYMENT_METHOD_OPTIONS,
    { value: 'Invoice', label: 'Invoice' },
  ];

  if (bookingMode !== 'patient' || selectedPayer === 'patient') {
    return baseOptions;
  }

  if (String(selectedPayer || '').startsWith('other:')) {
    return [
      { value: INSURANCE_PAYMENT_METHOD_OPTIONS.MAIL, label: INSURANCE_PAYMENT_METHOD_OPTIONS.MAIL },
      { value: INSURANCE_PAYMENT_METHOD_OPTIONS.EAN, label: INSURANCE_PAYMENT_METHOD_OPTIONS.EAN },
    ];
  }

  const selectedInsuranceOption = (Array.isArray(payerReferralOptions) ? payerReferralOptions : []).find((option) => option.id === selectedPayer) || null;
  const coveragePerAppointment = getInsuranceCoveragePerAppointment(selectedInsuranceOption);
  const isSplitBilling = hasInsuranceSplitBilling(bookedPrice, coveragePerAppointment);

  if (isSplitBilling) {
    return [
      { value: INSURANCE_PAYMENT_METHOD_OPTIONS.SPLIT_MAIL, label: INSURANCE_PAYMENT_METHOD_OPTIONS.SPLIT_MAIL },
      { value: INSURANCE_PAYMENT_METHOD_OPTIONS.SPLIT_EAN, label: INSURANCE_PAYMENT_METHOD_OPTIONS.SPLIT_EAN },
    ];
  }

  return [
    { value: INSURANCE_PAYMENT_METHOD_OPTIONS.MAIL, label: INSURANCE_PAYMENT_METHOD_OPTIONS.MAIL },
    { value: INSURANCE_PAYMENT_METHOD_OPTIONS.EAN, label: INSURANCE_PAYMENT_METHOD_OPTIONS.EAN },
  ];
};

const BookingCalendarGrid = React.memo(function BookingCalendarGrid({
  calendarBodyClassName,
  events,
  backgroundEvents,
  calendarComponents,
  calendarView,
  calendarViews,
  onView,
  calendarDate,
  onNavigate,
  eventPropGetter,
  backgroundEventPropGetter,
  tooltipAccessor,
  calendarFormats,
  onSelectEvent,
  onDoubleClickEvent,
  onSelectSlot,
  onEventDrop,
  onEventResize,
  calendarResources,
}) {
  return (
    <DnDCalendar
      localizer={localizer}
      events={events}
      backgroundEvents={backgroundEvents}
      startAccessor="start"
      endAccessor="end"
      className={calendarBodyClassName}
      components={calendarComponents}
      views={calendarViews}
      view={calendarView}
      onView={onView}
      date={calendarDate}
      onNavigate={onNavigate}
      eventPropGetter={eventPropGetter}
      backgroundEventPropGetter={backgroundEventPropGetter}
      tooltipAccessor={tooltipAccessor}
      formats={calendarFormats}
      selectable
      onSelectEvent={onSelectEvent}
      onDoubleClickEvent={onDoubleClickEvent}
      onSelectSlot={onSelectSlot}
      onEventDrop={onEventDrop}
      onEventResize={onEventResize}
      resizable
      step={30}
      timeslots={2}
      min={new Date(1970, 1, 1, 5, 0)}
      max={new Date(1970, 1, 1, 21, 0)}
      {...(calendarView === Views.DAY && calendarResources ? {
        resources: calendarResources,
        resourceIdAccessor: 'id',
        resourceTitleAccessor: 'title',
      } : {})}
      messages={{
        month: 'Month',
        week: 'Week',
        day: 'Day',
        today: 'Today',
        previous: '<',
        next: '>',
      }}
    />
  );
});

const CALENDAR_VISIBLE_START_HOUR = 5;
const CALENDAR_VISIBLE_END_HOUR = 21;

const createClosedOpeningHours = () => WEEKDAY_KEYS.reduce((accumulator, key) => ({
  ...accumulator,
  [key]: { ...DEFAULT_OPENING_HOURS_DAY },
}), {});

const normalizeClinicOpeningHours = (openingHours = {}) => WEEKDAY_KEYS.reduce((accumulator, key) => {
  const rawDay = openingHours && typeof openingHours === 'object' ? openingHours[key] : null;
  accumulator[key] = {
    enabled: rawDay?.enabled !== undefined ? Boolean(rawDay.enabled) : false,
    start: String(rawDay?.start || DEFAULT_OPENING_HOURS_DAY.start),
    end: String(rawDay?.end || DEFAULT_OPENING_HOURS_DAY.end),
  };
  return accumulator;
}, createClosedOpeningHours());

const openingHoursHasActiveDay = (openingHours = {}) => WEEKDAY_KEYS.some((weekdayKey) => Boolean(openingHours?.[weekdayKey]?.enabled));

const normalizeStaffOpeningHours = (staffOpeningHours = {}, fallbackOpeningHours = createClosedOpeningHours()) => (
  Object.entries(staffOpeningHours && typeof staffOpeningHours === 'object' ? staffOpeningHours : {}).reduce((accumulator, [staffId, openingHours]) => {
    accumulator[staffId] = normalizeClinicOpeningHours(openingHours || fallbackOpeningHours);
    return accumulator;
  }, {})
);

const parseTimeToMinutes = (timeValue) => {
  const [hoursPart, minutesPart] = String(timeValue || '').trim().split(':');
  const hours = Number(hoursPart);
  const minutes = Number(minutesPart);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return null;
  }
  return (hours * 60) + minutes;
};

const buildClosedHoursBackgroundEvents = (date, openingHours, resourceId = null) => {
  const dayStart = moment(date).startOf('day');
  const dayEndMinutes = CALENDAR_VISIBLE_END_HOUR * 60;
  const dayStartMinutes = CALENDAR_VISIBLE_START_HOUR * 60;
  const weekdayKey = WEEKDAY_KEYS[dayStart.day()];
  const daySchedule = openingHours?.[weekdayKey];

  const addBackgroundEvent = (startMinutes, endMinutes, suffix) => {
    if (!Number.isFinite(startMinutes) || !Number.isFinite(endMinutes) || endMinutes <= startMinutes) {
      return null;
    }

    const startLabel = dayStart.clone().add(startMinutes, 'minutes').format('h:mm A');
    const endLabel = dayStart.clone().add(endMinutes, 'minutes').format('h:mm A');

    return {
      id: `closed-hours-${dayStart.format('YYYY-MM-DD')}-${resourceId || 'clinic'}-${suffix}-${startMinutes}-${endMinutes}`,
      title: `Closed ${startLabel} - ${endLabel}`,
      start: dayStart.clone().add(startMinutes, 'minutes').toDate(),
      end: dayStart.clone().add(endMinutes, 'minutes').toDate(),
      resourceId: resourceId || undefined,
      isClosedHours: true,
      className: 'closed-hours-background-event',
      style: {
        backgroundColor: '#f2f2f2',
        borderRadius: '8px',
        opacity: 1,
        pointerEvents: 'none',
        backgroundClip: 'padding-box',
        color: '#002D55',
        boxSizing: 'border-box',
        padding: '6px 8px 7px',
      },
      tooltip: `Closed ${startLabel} - ${endLabel}`,
    };
  };

  if (!daySchedule || !daySchedule.enabled) {
    return [addBackgroundEvent(dayStartMinutes, dayEndMinutes, 'closed-all-day')].filter(Boolean);
  }

  const openMinutes = parseTimeToMinutes(daySchedule.start);
  const closeMinutes = parseTimeToMinutes(daySchedule.end);

  return [
    addBackgroundEvent(dayStartMinutes, openMinutes, 'before-open'),
    addBackgroundEvent(closeMinutes, dayEndMinutes, 'after-close'),
  ].filter(Boolean);
};

const colorWithAlpha = (hexColor, alpha) => {
  const sanitized = String(hexColor || '').trim();
  const normalized = sanitized.startsWith('#') ? sanitized.slice(1) : sanitized;
  const expanded = normalized.length === 3
    ? normalized.split('').map((part) => `${part}${part}`).join('')
    : normalized;

  if (!/^[0-9a-fA-F]{6}$/.test(expanded)) {
    return `rgba(49, 116, 173, ${alpha})`;
  }

  const red = Number.parseInt(expanded.slice(0, 2), 16);
  const green = Number.parseInt(expanded.slice(2, 4), 16);
  const blue = Number.parseInt(expanded.slice(4, 6), 16);

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

const buildAvailabilityBackgroundEvents = (date, slots = [], resourceId = null, resourceLabel = '') => {
  const dayStart = moment(date).startOf('day');

  return (Array.isArray(slots) ? slots : []).flatMap((slot) => {
    const startMinutes = parseTimeToMinutes(slot.start_time);
    const endMinutes = parseTimeToMinutes(slot.end_time);
    if (!Number.isFinite(startMinutes) || !Number.isFinite(endMinutes) || endMinutes <= startMinutes) {
      return [];
    }

    const startLabel = dayStart.clone().add(startMinutes, 'minutes').format('h:mm A');
    const endLabel = dayStart.clone().add(endMinutes, 'minutes').format('h:mm A');
    const isAvailable = slot.is_available !== false;
    const isBookable = slot.is_bookable !== false;
    const staffLabel = slot.staff_member_email || slot.staff_member_name || slot.staffName || slot.staffLabel || (resourceLabel || '');
    const roomLabel = slot.roomName || '';
    const allocationColor = normalizeAllocationColor(slot.roomColor)
      || normalizeAllocationColor(slot.color)
      || getAllocationDefaultColor(slot.room || roomLabel || slot.staff_member || staffLabel)
      || '#3174ad';
    const availabilityLabel = isAvailable ? 'Available' : 'Unavailable';
    const allocationDisplayLabel = `${staffLabel || 'Staff'} · ${roomLabel || 'Room'} · ${startLabel} - ${endLabel}`;
    const allocationStatusLabel = `${availabilityLabel}${isBookable ? '' : ' · Not bookable'}`;
    const isSickDayOverlaySlot = slot.isSickDayOverlaySlot === true;
    const fillColor = isSickDayOverlaySlot
      ? 'rgba(183, 28, 28, 0.20)'
      : colorWithAlpha(allocationColor, isAvailable ? (isBookable ? 0.11 : 0.07) : 0.04);
    const fillPatternColor = isSickDayOverlaySlot
      ? 'rgba(183, 28, 28, 0.12)'
      : colorWithAlpha(allocationColor, isAvailable ? 0.1 : 0.08);
    const fillFrameColor = isSickDayOverlaySlot
      ? allocationColor
      : colorWithAlpha(allocationColor, isAvailable ? (isBookable ? 0.7 : 0.55) : 0.38);

    return [{
      id: `allocation-${slot.id || resourceId || 'clinic'}-${dayStart.format('YYYY-MM-DD')}-${startMinutes}-${endMinutes}`,
      title: allocationDisplayLabel,
      start: dayStart.clone().add(startMinutes, 'minutes').toDate(),
      end: dayStart.clone().add(endMinutes, 'minutes').toDate(),
      resourceId: resourceId || undefined,
      isAllocationWindow: true,
      isAvailableAllocation: isAvailable,
      isBookableAllocation: isBookable,
      className: [
        'allocation-background-event',
        isBookable ? 'allocation-background-event-bookable' : 'allocation-background-event-blocked',
        isAvailable === false ? 'allocation-background-event-unavailable' : '',
      ].filter(Boolean).join(' '),
      style: {
        backgroundColor: fillColor,
        borderLeft: `5px solid ${allocationColor}`,
        borderTop: '1px solid rgba(0, 45, 85, 0.05)',
        borderRight: '1px solid rgba(0, 45, 85, 0.05)',
        borderBottom: '1px solid rgba(0, 45, 85, 0.05)',
        borderRadius: '8px',
        opacity: 1,
        pointerEvents: isSickDayOverlaySlot ? 'auto' : 'none',
        backgroundClip: 'padding-box',
        boxSizing: 'border-box',
        padding: '6px 8px 7px',
        color: '#002D55',
        whiteSpace: 'pre-line',
        zIndex: isSickDayOverlaySlot ? 5 : 4,
        cursor: isSickDayOverlaySlot ? 'pointer' : 'default',
        '--allocation-fill-color': colorWithAlpha(allocationColor, isAvailable ? (isBookable ? 0.08 : 0.06) : 0.03),
        '--allocation-room-color': allocationColor,
        '--allocation-pattern-color': fillPatternColor,
        '--allocation-frame-color': fillFrameColor,
      },
      allocationRoomColor: allocationColor,
      allocationDisplayLabel,
      allocationStaffLabel: staffLabel,
      allocationRoomLabel: roomLabel,
      allocationTimeLabel: `${startLabel} - ${endLabel}`,
      allocationStatusLabel,
      allocationFillColor: isSickDayOverlaySlot ? 'transparent' : colorWithAlpha(allocationColor, isAvailable ? (isBookable ? 0.14 : 0.1) : 0.06),
      allocationFrameColor: fillFrameColor,
      allocationPatternColor: fillPatternColor,
      tooltip: `${staffLabel ? `${staffLabel} · ` : ''}${roomLabel ? `${roomLabel} · ` : ''}${availabilityLabel}${isBookable ? '' : ' · Not bookable'} ${startLabel} - ${endLabel}`,
    }];
  });
};

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const formatMoney = (value, currency = 'EUR') => {
  const amount = Number.parseFloat(value || 0);
  const safeAmount = Number.isFinite(amount) ? amount : 0;
  return `${currency} ${safeAmount.toFixed(2)}`;
};

const formatSavedCardLabel = (paymentMethod) => {
  if (!paymentMethod) return 'Card';
  const brand = (paymentMethod.card_brand || paymentMethod.brand || 'Card').toString();
  const lastFour = paymentMethod.card_last_four || paymentMethod.last4 || '';
  return `${brand.charAt(0).toUpperCase()}${brand.slice(1)} ending in ${lastFour}`;
};

const StripeBookingPaymentForm = ({ invoiceNumber, currency, paymentQuote, onSuccess, onCancel, rememberPaymentMethod, makeDefaultPaymentMethod }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const consultationAmount = paymentQuote?.consultation_amount ?? paymentQuote?.charge_amount ?? 0;
  const feeAmount = paymentQuote?.fee_amount ?? 0;
  const chargeAmount = paymentQuote?.charge_amount ?? paymentQuote?.display_total_amount ?? 0;
  const feePercent = paymentQuote?.fee_percent ?? 0;
  const feeDisplayMode = paymentQuote?.fee_display_mode || 'platform_fee_deduction';

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      setError('Stripe is not ready yet');
      return;
    }

    setLoading(true);
    setError('');

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setError(submitError.message);
      setLoading(false);
      return;
    }

    const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.href,
      },
      redirect: 'if_required',
    });

    if (confirmError) {
      setError(confirmError.message);
    } else if (paymentIntent && paymentIntent.status === 'succeeded') {
      await onSuccess(paymentIntent);
    } else if (paymentIntent) {
      setError(`Payment status: ${paymentIntent.status}`);
    }

    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box style={{ marginBottom: 16 }}>
        <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 18 }}>
          Complete card payment
        </Typography>
        <Typography style={{ color: '#666', marginTop: 4 }}>
          Invoice {invoiceNumber} - {formatMoney(chargeAmount, currency)}
        </Typography>
      </Box>
      <Box style={{ padding: 14, backgroundColor: '#f5f9ff', border: '1px solid #dde6f2', borderRadius: 10, marginBottom: 16 }}>
        <Typography style={{ color: '#002D55', fontWeight: 700, marginBottom: 6 }}>
          Payment summary
        </Typography>
        <Typography style={{ color: '#002D55', fontSize: 14 }}>
          Consultation amount: {formatMoney(consultationAmount, currency)}
        </Typography>
        <Typography style={{ color: '#002D55', fontSize: 14 }}>
          Stripe fee ({Number(feePercent || 0).toFixed(2)}%): {formatMoney(feeAmount, currency)}
        </Typography>
        <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 15, marginTop: 6 }}>
          Total billed: {formatMoney(chargeAmount, currency)}
        </Typography>
        <Typography style={{ color: '#666', fontSize: 12, marginTop: 6 }}>
          Fee mode: {feeDisplayMode === 'patient_surcharge' ? 'charged on top of the consultation' : 'shown as a platform fee deduction'}
        </Typography>
        {rememberPaymentMethod ? (
          <Typography style={{ color: '#666', fontSize: 12, marginTop: 4 }}>
            This card will be saved for future payments{makeDefaultPaymentMethod ? ' and set as the default card.' : '.'}
          </Typography>
        ) : null}
      </Box>
      <Box style={{ padding: 16, backgroundColor: '#fff', border: '1px solid #dde6f2', borderRadius: 10, marginBottom: 16 }}>
        <PaymentElement options={{ layout: 'tabs' }} />
      </Box>
      {error ? (
        <Typography style={{ color: '#d32f2f', marginBottom: 12, fontSize: 13 }}>
          {error}
        </Typography>
      ) : null}
      <DialogActions style={{ padding: 0, justifyContent: 'space-between' }}>
        <Button onClick={onCancel} variant="outlined" disabled={loading}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" color="primary" disabled={loading || !stripe || !elements}>
          {loading ? 'Processing...' : 'Pay now'}
        </Button>
      </DialogActions>
    </form>
  );
};

const useStyles = makeStyles(() => ({
  root: {
    height: '100%',
    background: 'transparent',
    borderRadius: 16  ,
  },
  Button: {
    color: "#FFFFFF",
    backgroundColor: "#002D55"
  },
  avatar: {
    backgroundColor: '#6a8caf',
    height: 40,
    width: 40,
    marginRight: 0,
    marginLeft: 0,
    marginTop: 0,
  },
  card: {
    background: '#f7faff',
    borderRadius: 16,
    boxShadow: '0 6px 32px rgba(0,45,85,0.18)',
    overflow: 'hidden',
  },
  cardContent: {
    background: '#f7faff',
    borderRadius: 0,
    padding: '0 !important',
    marginBottom: 0,
    '&:last-child': {
      paddingBottom: '0 !important',
    },
  },
  newBookingButton: {
    transition: 'background 0.2s',
    backgroundColor: '#e6eaf0',
    color: '#002D55',
    fontWeight: 'bold',
    maxHeight: 'clamp(26px, 2.2vw, 36px)',
    zIndex: 2,
    border: 'none',
    boxShadow: 'none',
    fontSize: 'clamp(0.69rem, 0.75vw, 0.875rem)',
    padding: 'clamp(3px, 0.32vw, 6px) clamp(8px, 0.85vw, 16px)',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#e6eaf0',
      color: '#FF7F27',
      fontWeight: 'bold',
      maxHeight: 'clamp(26px, 2.2vw, 36px)',
      zIndex: 2,
    },
  },
  tabsIndicator: {
    backgroundColor: '#002D55',
    height: 2,
    borderRadius: 2,
  },
  tabRoot: {
    color: '#002D55',
    fontWeight: 600,
    fontSize: 'clamp(0.72rem, calc(2px + 0.722vw), 1.0rem)',
    '&$selected': {
      color: '#002D55',
    },
  },
  selected: {},
  cardHeaderRoot: {
    minHeight: 'clamp(42px, calc(24px + 1.45vw), 56px)',
    padding: 'clamp(8px, calc(1px + 0.55vw), 12px) clamp(10px, calc(2px + 0.625vw), 16px)',
    boxSizing: 'border-box',
    borderBottom: '2px solid #FF7F27',
    '& .MuiCardHeader-content': {
      overflow: 'hidden',
      flex: 1,
    },
    '& .MuiCardHeader-title': {
      fontSize: 'clamp(0.66rem, calc(0.291rem + 0.549vw), 0.95rem)',
      fontWeight: 700,
      color: '#002D55',
      lineHeight: 1.2,
    },
  },
  cardHeaderTitle: {
    color: '#002D55',
    fontWeight: 700,
    fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem) !important',
    lineHeight: 1.2,
    paddingLeft: 5,
  },
  calendarHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  headerTitle: {
    color: '#002D55',
    fontWeight: 700,
    fontSize: 'clamp(0.66rem, calc(0.291rem + 0.549vw), 0.95rem)',
    lineHeight: 1.2,
  },
  headerControls: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    rowGap: 8,
    columnGap: 8,
    justifyContent: 'flex-end',
  },
  bookingCard: {
    marginBottom: 12,
    background: '#fff',
    borderRadius: 10,
    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
    padding: 16,
  },
  orangeFocused: {
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink:not(.Mui-focused)': {
      color: '#002D55',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27',
    },
    '& .MuiInputLabel-outlined.Mui-focused': {
      color: '#002D55',
    },
    '& .MuiOutlinedInput-root': {
      minHeight: 'clamp(38px, 2.9vw, 44px)',
      borderRadius: 14,
    },
    '& .MuiInputBase-input': {
      fontSize: 'clamp(0.8rem, calc(2px + 0.85vw), 0.9rem)',
      paddingTop: 'clamp(8px, 0.55vw, 10px)',
      paddingBottom: 'clamp(8px, 0.55vw, 10px)',
    },
    '& .MuiOutlinedInput-input': {
      paddingLeft: 'clamp(8px, 0.73vw, 14px)',
      paddingRight: 'clamp(8px, 0.73vw, 14px)',
    },
    '& .MuiSelect-select': {
      paddingTop: 'clamp(8px, 0.55vw, 10px)',
      paddingBottom: 'clamp(8px, 0.55vw, 10px)',
      paddingLeft: 'clamp(8px, 0.73vw, 14px)',
      paddingRight: 'clamp(8px, 0.73vw, 14px) !important',
    },
    '& .MuiSelect-icon': { display: 'none' },
    '& .MuiSelect-iconOutlined': { display: 'none' },
    /* Autocomplete-specific overrides so Patient matches Practitioner */
    '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
      paddingTop: 'clamp(2px, 0.18vw, 3px)',
      paddingBottom: 'clamp(2px, 0.18vw, 3px)',
      paddingLeft: 'clamp(6px, 0.52vw, 10px)',
    },
    '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"] .MuiAutocomplete-input': {
      paddingTop: 'clamp(3px, 0.26vw, 5px)',
      paddingBottom: 'clamp(3px, 0.26vw, 5px)',
      paddingLeft: 'clamp(4px, 0.35vw, 6px)',
      fontSize: 'clamp(0.8rem, calc(2px + 0.85vw), 0.9rem)',
    },
    '& .MuiAutocomplete-endAdornment': {
      top: '50%',
      transform: 'translateY(-50%)',
    },
    '& .MuiInputLabel-root': {
      fontSize: 'clamp(0.78rem, calc(2px + 0.72vw), 0.875rem)',
    },
    '& .MuiInputLabel-outlined:not(.MuiInputLabel-shrink)': {
      top: '50%',
      transform: 'translate(14px, -50%) scale(1)',
    },
    '& .MuiMenuItem-root': {
      fontSize: 'clamp(0.8rem, calc(2px + 0.85vw), 0.9rem)',
    },
  },
  dialogMenuItem: {
    fontSize: 'clamp(0.8rem, calc(2px + 0.85vw), 0.9rem)',
  },
  dialogMenuList: {
    '& .MuiListItem-root, & .MuiMenuItem-root': {
      fontSize: 'clamp(0.8rem, calc(2px + 0.85vw), 0.9rem)',
      minHeight: 'clamp(30px, 2.5vw, 44px)',
      paddingTop: 'clamp(4px, 0.35vw, 6px)',
      paddingBottom: 'clamp(4px, 0.35vw, 6px)',
    },
  },
  PayButton: {
    backgroundColor: '#002D55',
    color: '#FFFFFF',
    borderRadius: 14,
    border: 'none',
    boxShadow: 'none',
    padding: 'clamp(3px, 0.32vw, 6px) clamp(8px, 0.85vw, 16px)',
    fontSize: 'clamp(0.89rem, 0.75vw, 0.975rem)',
    fontWeight: 600,
    textTransform: 'none',
    minWidth: 'clamp(70px, 5.2vw, 100px)',
    minHeight: 'clamp(42px, 2.5vw, 44px)',
    '&:hover': { backgroundColor: '#FF7F27', boxShadow: 'none' },
  },
  confirmButton: {
    backgroundColor: '#002D55',
    color: '#FFFFFF',
    borderRadius: 18,
    border: 'none',
    boxShadow: 'none',
    padding: 'clamp(3px, 0.32vw, 6px) clamp(8px, 0.85vw, 16px)',
    fontSize: 'clamp(0.69rem, 0.75vw, 0.875rem)',
    fontWeight: 600,
    textTransform: 'none',
    minWidth: 'clamp(70px, 5.2vw, 100px)',
    '&:hover': { backgroundColor: '#FF7F27', boxShadow: 'none' },
  },
  cancelButton: {
    backgroundColor: '#e6eaf0',
    color: '#002D55',
    fontWeight: 'bold',
    borderRadius: 18,
    border: '2px solid transparent',
    boxShadow: 'none',
    padding: 'clamp(3px, 0.32vw, 6px) clamp(8px, 0.85vw, 16px)',
    fontSize: 'clamp(0.69rem, 0.75vw, 0.875rem)',
    textTransform: 'none',
    minWidth: 'clamp(70px, 5.2vw, 100px)',
    '&:hover': { backgroundColor: '#e6eaf0', color: '#FF7F27', border: '1px solid #FF7F27', boxShadow: 'none' },
  },
  textfieldColor: { 
    '& .MuiInputBase-root': {
      backgroundColor: '#fff',
    },
    '& .MuiInputBase-input': {
      color: '#002D55',
    },
    '& .MuiInputBase-input::placeholder': {
      color: '#002D55',
      opacity: 1,
    },
    '& .MuiOutlinedInput-input::placeholder': {
      color: '#002D55',
      opacity: 1,
    },
  },
  compactSelect: {
    minWidth: 'clamp(130px, 14vw, 170px)',
    '& .MuiOutlinedInput-root': {
      minHeight: 'clamp(30px, 2.5vw, 36px)',
      marginRight: 0,
    },
    '& .MuiSelect-select': {
      fontSize: 'clamp(0.68rem, calc(2px + 0.722vw), 0.95rem)',
      paddingTop: 'clamp(2px, 0.25vw, 4px)',
      paddingBottom: 'clamp(2px, 0.25vw, 4px)',
      paddingLeft: 'clamp(6px, 0.7vw, 10px)',
      paddingRight: 26,
      lineHeight: 1.4,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#999',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27',
    },
  },
  staffHeaderSelect: {
    minWidth: 'clamp(140px, 30vw, 250px)',
    '& .MuiOutlinedInput-root': {
      height: 'clamp(28px, calc(9px + 1.47vw), 38px)',
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      backgroundColor: '#fff',
      paddingLeft: 'clamp(7.06px, calc(0px + 0.49vw), 9.41px)',
    },
    '& .MuiOutlinedInput-input': {
      paddingTop: '0 !important',
      paddingBottom: '0 !important',
    },
    '& .MuiSelect-iconOutlined': {
      right: 6,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#999',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27',
    },
  },
  orangeSelect: {
    background: '#FFFFFF',
    color: '#002D55',
    fontWeight: 600,
    fontSize: 'clamp(0.69rem, 1.04vw, 0.90rem)',
    border: '1px solid #ccc',
    borderRadius: 14,
    height: 'clamp(24px, 2.15vw, 27.5px)',
    boxSizing: 'border-box',
    display: 'inline-flex',
    alignItems: 'center',
    paddingLeft: '0px !important',
    cursor: 'pointer',
    outline: 'none',
    transition: 'border-color 0.2s',
    '& .MuiSelect-select': {
      padding: '0 clamp(16px, 1.25vw, 20px) 0 clamp(8px, 0.7vw, 12px) !important',
      minHeight: 'unset !important',
      lineHeight: 'normal',
      background: 'transparent',
      display: 'flex',
      alignItems: 'center',
    },
    '&:focus': {
      borderColor: '#FF7F27',
      outline: '#FF7F27 solid 1px',
    },
    '&:hover': {
      borderColor: '#FF7F27',
    },
  },
  staffHeaderSelectInput: {
    fontSize: 'clamp(0.775rem, calc(0.342rem + 0.646vw), 1.118rem)',
    paddingTop: '0 !important',
    paddingBottom: '0 !important',
    paddingLeft: '0 !important',
    paddingRight: '26px !important',
    lineHeight: 1.2,
    color: '#002D55',
    fontWeight: 'bold',
    letterSpacing: '0.6px',
  },
  staffHeaderMenuItem: {
    fontSize: 'clamp(0.7rem, calc(0.342rem + 0.646vw), 1.118rem)',
  },
  resourceToggle: {
    display: 'flex',
    marginLeft: 8,
    border: '1px solid #999',
    borderRadius: 4,
    overflow: 'hidden',
    height: 'clamp(30px, 2.4vw, 36px)',
  },
  resourceToggleButton: {
    borderRadius: 0,
    minWidth: 'clamp(54px, 6vw, 70px)',
    fontSize: 'clamp(0.68rem, 0.95vw, 0.8rem)',
    paddingLeft: 'clamp(8px, 0.9vw, 12px)',
    paddingRight: 'clamp(8px, 0.9vw, 12px)',
  },
  dialogModeToggle: {
    display: 'flex',
    gap: 'clamp(2px, 0.25vw, 4px)',
    alignItems: 'center',
  },
  dialogModeButton: {
    minWidth: 'clamp(78px, 7vw, 94px)',
    minHeight: 'clamp(27px, calc(4.8px + 1.625vw), 36px)',
    maxHeight: 'clamp(27px, calc(4.8px + 1.625vw), 36px)',
    fontSize: 'clamp(0.66rem, calc(0.291rem + 0.549vw), 0.95rem)',
    paddingLeft: 'clamp(8px, calc(-2px + 0.72vw), 12px)',
    paddingRight: 'clamp(8px, calc(-2px + 0.72vw), 12px)',
    textTransform: 'none',
    lineHeight: 1,
    letterSpacing: 0,
    boxShadow: 'none',
    border: 'none',
    borderRadius: 0,
  },
  dialogModeButtonActive: {
    backgroundColor: '#e6eaf0',
    color: '#FF7F27',
    '&:hover': {
      backgroundColor: '#e6eaf0',
    },
  },
  dialogModeButtonInactive: {
    backgroundColor: '#F0F8FF',
    color: '#002D55',
    '&:hover': {
      backgroundColor: '#e6eaf0',
      color: '#FF7F27',
    },
  },
  dialogModeButtonLeft: {
    borderRadius: '14px 0 0 14px',
  },
  dialogModeButtonRight: {
    borderRadius: '0 14px 14px 0',
  },
  calendarBody: {
    height: 'clamp(520px, calc(100px + 20vh + 38vw), 960px)',
    background: '#fff',
    borderRadius: 0,
    marginTop: 0,
    marginRight: 0,
    marginLeft: 0,
    marginBottom: 0,
    padding: 'clamp(8px, 1vw, 16px)',
    border: 'none',
    boxSizing: 'border-box',
  },
  dialogTitle: {
    color: '#002D55',
    fontWeight: 600,
    fontSize: 'clamp(0.80rem, calc(4px + 0.722vw), 1.1rem)',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 0,
  },
  dialogContent: {
    backgroundColor: '#e6eaf0',
    padding: '24px !important',
  },
  dialogPaper: {
    borderRadius: 18,
    overflow: 'hidden',
  },
  dialogHeader: {
    borderBottom: '1px solid #eeeeee',
    padding: '16px 24px',
    backgroundColor: '#ffffff',
  },
  dialogTitleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    gap: 12,
  },
  dialogTitleActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
    marginLeft: 'auto',
  },
  dialogTitleText: {
    flex: '0 0 auto',
    color: '#002D55',
    fontWeight: 600,
    fontSize: 'clamp(0.95rem, calc(4px + 0.72vw), 1.15rem)',
  },
  dialogSectionLabel: {
    color: '#64748B',
    fontSize: '0.72rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  closeButton: {
    color: '#64748B',
    padding: 6,
  },
  dialogTitleMeta: {
    color: '#64748B',
    fontSize: 'clamp(0.62rem, 0.7vw, 0.81rem)',
    fontWeight: 700,
    backgroundColor: '#f5f5f5',
    border: '1px solid #dddddd',
    borderRadius: 14,
    padding: '4px 12px',
    whiteSpace: 'nowrap',
  },
  resourceRoomLegend: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 'clamp(3px, 0.35vw, 6px)',
    marginTop: 0,
    minHeight: 'clamp(14px, 1.2vw, 18px)',
    padding: 'clamp(3px, 0.35vw, 6px) clamp(4px, 0.5vw, 8px)',
    borderRadius: 10,
    border: '1px solid rgba(0, 45, 85, 0.08)',
    background: 'rgba(240, 248, 255, 0.9)',
  },
  resourceRoomLegendItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'clamp(2px, 0.2vw, 4px)',
    fontSize: 'clamp(0.52rem, 0.62vw, 0.72rem)',
    fontWeight: 700,
    color: '#667085',
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    minWidth: 0,
    maxWidth: '100%',
  },
  resourceRoomLegendText: {
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: 1.1,
  },
  resourceRoomLegendDot: {
    width: 'clamp(6px, 0.55vw, 8px)',
    height: 'clamp(6px, 0.55vw, 8px)',
    borderRadius: '50%',
    display: 'inline-block',
    flexShrink: 0,
    boxShadow: '0 0 0 1px rgba(0, 45, 85, 0.14)',
  },
  calendarHeaderCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 1,
    width: '100%',
    minHeight: '100%',
    position: 'relative',
    paddingTop: 2,
    paddingBottom: 3,
  },
  calendarHeaderTitleRow: {
    width: '100%',
    minHeight: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  calendarHeaderLabel: {
    width: '100%',
    textAlign: 'center',
  },
  staffHeaderCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 1,
    width: '100%',
    minHeight: '100%',
    position: 'relative',
    paddingBottom: 3,
    paddingTop: 2,
    '&:hover': {
      cursor: 'pointer',
    },
  },
  headerLegendRow: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    alignSelf: 'stretch',
  },
  calendarHeaderRoomsBox: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 'clamp(3px, 0.35vw, 6px)',
    width: '100%',
    paddingTop: 3,
    paddingRight: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    borderRadius: 0,
    border: 'none',
    background: 'transparent',
    maxWidth: '100%',
    zIndex: 2,
  },
  '@media (max-width: 1366px)': {
    resourceRoomLegend: {
      gap: 'clamp(2px, 0.3vw, 5px)',
      minHeight: 'clamp(13px, 1.1vw, 17px)',
      padding: 'clamp(2px, 0.3vw, 5px) clamp(3px, 0.45vw, 7px)',
    },
    resourceRoomLegendItem: {
      gap: 'clamp(2px, 0.18vw, 3px)',
      fontSize: 'clamp(0.49rem, 0.58vw, 0.68rem)',
    },
    resourceRoomLegendDot: {
      width: 'clamp(5px, 0.45vw, 7px)',
      height: 'clamp(5px, 0.45vw, 7px)',
    },
    calendarHeaderRoomsBox: {
      gap: 'clamp(2px, 0.3vw, 5px)',
      paddingTop: 2,
    },
  },
  '@media (max-width: 1024px)': {
    resourceRoomLegend: {
      gap: 'clamp(2px, 0.25vw, 4px)',
      minHeight: 'clamp(12px, 1vw, 16px)',
      padding: 'clamp(2px, 0.25vw, 4px) clamp(3px, 0.35vw, 6px)',
      borderRadius: 8,
    },
    resourceRoomLegendItem: {
      gap: 'clamp(1px, 0.15vw, 3px)',
      fontSize: 'clamp(0.46rem, 0.54vw, 0.64rem)',
    },
    resourceRoomLegendDot: {
      width: 'clamp(5px, 0.4vw, 6px)',
      height: 'clamp(5px, 0.4vw, 6px)',
    },
    calendarHeaderRoomsBox: {
      gap: 'clamp(2px, 0.25vw, 4px)',
      paddingTop: 1,
    },
  },
  dialogPanel: {
    padding: 'clamp(12px, 1.25vw, 24px)',
    background: '#f7faff',
    width: '100%',
    boxSizing: 'border-box',
    margin: 0,
    borderRadius: 18,
    border: '1px solid #dce8f5',
    boxShadow: 'none',
  },
  dialogActions: {
    padding: 16,
    gap: 8,
    justifyContent: 'flex-end',
    borderTop: '1px solid #eeeeee',
    backgroundColor: '#ffffff',
  },
  deleteActionButton: {
    borderColor: '#f44336',
    color: '#f44336',
    borderRadius: 50,
    fontSize: 'clamp(0.69rem, 0.75vw, 0.875rem)',
    padding: 'clamp(3px, 0.32vw, 6px) clamp(8px, 0.85vw, 16px)',
    textTransform: 'none',
    boxShadow: 'none',
    '&:hover': {
      borderColor: '#d32f2f',
      backgroundColor: 'rgba(244, 67, 54, 0.04)',
      boxShadow: 'none',
    },
  },
  repeatActionButton: {
    borderColor: '#002D55',
    color: '#002D55',
    borderRadius: 50,
    fontSize: 'clamp(0.69rem, 0.75vw, 0.875rem)',
    padding: 'clamp(3px, 0.32vw, 6px) clamp(8px, 0.85vw, 16px)',
    textTransform: 'none',
    boxShadow: 'none',
    '&:hover': {
      borderColor: '#002D55',
      backgroundColor: 'rgba(0, 45, 85, 0.04)',
      boxShadow: 'none',
    },
  },
  deleteConfirmButton: {
    backgroundColor: '#f44336',
    color: '#fff',
    borderRadius: 50,
    boxShadow: 'none',
    padding: 'clamp(3px, 0.32vw, 6px) clamp(8px, 0.85vw, 16px)',
    fontSize: 'clamp(0.69rem, 0.75vw, 0.875rem)',
    fontWeight: 600,
    textTransform: 'none',
    minWidth: 'clamp(70px, 5.2vw, 100px)',
    '&:hover': {
      backgroundColor: '#d32f2f',
      boxShadow: 'none',
    },
  },
  deleteDialogText: {
    color: '#002D55',
    fontSize: 'clamp(0.72rem, 0.8vw, 0.88rem)',
  },
  notesField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 14,
      minHeight: 42,
    },
    '& .MuiOutlinedInput-multiline': {
      padding: 'clamp(2px, 0.2vw, 4px) clamp(6px, 0.45vw, 8px)',
    },
    '& .MuiOutlinedInput-inputMultiline': {
      padding: '4px 0',
      paddingLeft: 'clamp(6px, 0.45vw, 8px)',
      fontSize: 13,
      lineHeight: 1.25,
    },
    '& .MuiInputBase-inputMultiline': {
      overflow: 'auto',
    },
  },
}));

const EHRBookingCalendar = ({ className, selectedPatientId: selectedPatientIdProp, selectedPatientName, selectedTreatmentId: selectedTreatmentIdProp, profiles = EMPTY_PROFILES, ...rest }) => {
  const classes = useStyles();
  const [tabIndex, setTabIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [practitioners, setPractitioners] = useState([]);
  const [selectedPractitioner, setSelectedPractitioner] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedBookingVideoSession, setSelectedBookingVideoSession] = useState(null);
  const [bookingVideoCallOpen, setBookingVideoCallOpen] = useState(false);
  const [bookingVideoCallAppointment, setBookingVideoCallAppointment] = useState(null);
  const [openClassDialog, setOpenClassDialog] = useState(false);
  const [selectedClassBooking, setSelectedClassBooking] = useState(null);
  const [classAttendanceStaffId, setClassAttendanceStaffId] = useState('');
  const [classAttendanceDrafts, setClassAttendanceDrafts] = useState([]);
  const [classDialogSaving, setClassDialogSaving] = useState(false);
  const [staffVacations, setStaffVacations] = useState([]);
  const [openSickDayCancelConfirm, setOpenSickDayCancelConfirm] = useState(false);
  const [selectedSickDay, setSelectedSickDay] = useState(null);
  const [openSickDayAutoCancelConfirm, setOpenSickDayAutoCancelConfirm] = useState(false);
  const [pendingSickDaySubmission, setPendingSickDaySubmission] = useState(null);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' or 'edit'
  const [form, setForm] = useState({
    bookingMode: 'patient',
    patient: '',
    patientName: '',
    patientEmail: '',
    patientPhone: '',
    treatment: '',
    practitioner: '',
    date: '',
    time: '',
    endTime: '',
    notes: '',
    appointmentType: 'follow_up',
    serviceName: 'Follow-up',
    nonPatientType: 'block',
    title: '',
    assessmentDeliveryMode: '',
    booked_price: '',
    booked_currency: 'EUR',
    payment_method: '',
    split_payment_method: '',
    clinic_calendar: '',
    color: DEFAULT_BOOKING_COLOR,
  });
  const [calendarView, setCalendarView] = useState(Views.WEEK);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [showWeekendDays, setShowWeekendDays] = useState(false);
  const calendarViewRef = useRef(calendarView);
  const handleCalendarViewChange = useCallback((nextView) => {
    if (nextView === Views.DAY && calendarViewRef.current !== Views.DAY) {
      setCalendarDate(new Date());
    }
    calendarViewRef.current = nextView;
    setCalendarView((currentView) => currentView === nextView ? currentView : nextView);
  }, []);
  const token = useSelector(state => state.auth.token);
  const user = useSelector(state => state.auth.user);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const [stripePaymentDialogOpen, setStripePaymentDialogOpen] = useState(false);
  const [stripePaymentDialogStage, setStripePaymentDialogStage] = useState('idle');
  const [stripePaymentClientSecret, setStripePaymentClientSecret] = useState('');
  const [stripePaymentInvoice, setStripePaymentInvoice] = useState(null);
  const [stripePaymentQuote, setStripePaymentQuote] = useState(null);
  const [stripePaymentFlowMode, setStripePaymentFlowMode] = useState('default');
  const [stripePaymentConfirmation, setStripePaymentConfirmation] = useState(null);
  const [stripePaymentMethods, setStripePaymentMethods] = useState([]);
  const [stripePaymentSelectedMethodId, setStripePaymentSelectedMethodId] = useState('');
  const [stripePaymentRememberCard, setStripePaymentRememberCard] = useState(true);
  const [stripePaymentMakeDefaultCard, setStripePaymentMakeDefaultCard] = useState(true);
  const [stripePaymentWorking, setStripePaymentWorking] = useState(false);
  const [stripePaymentDialogError, setStripePaymentDialogError] = useState('');
  const [accreditDialogOpen, setAccreditDialogOpen] = useState(false);
  const [accreditPatientHasPaidBalance, setAccreditPatientHasPaidBalance] = useState(false);
  const [accreditTransferToPatientBalance, setAccreditTransferToPatientBalance] = useState(false);
  const [repeatDialogOpen, setRepeatDialogOpen] = useState(false);
  const [repeatSourceDraft, setRepeatSourceDraft] = useState(null);
  const [queuedRepeatSettings, setQueuedRepeatSettings] = useState(null);
  const [repeatSettings, setRepeatSettings] = useState({
    repeatPattern: 'weekly_same_day',
    repeatEvery: 1,
    repeatCount: 5,
    endMode: 'count',
    untilDate: '',
    weekdays: [],
  });
  const [repeatPreview, setRepeatPreview] = useState(null);
  const [repeatLoading, setRepeatLoading] = useState(false);
  const [repeatError, setRepeatError] = useState('');
  const [repeatDialogMode, setRepeatDialogMode] = useState('saved');
  const [repeatSettingsQueued, setRepeatSettingsQueued] = useState(false);
  const [bookingConflictDialogOpen, setBookingConflictDialogOpen] = useState(false);
  const [bookingConflictPreview, setBookingConflictPreview] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadSelectedBookingVideoSession = async () => {
      const selectedBookingId = selectedBooking?.id || selectedBooking?.appointment_id;
      if (!openDialog || dialogMode !== 'edit' || !selectedBookingId || !token || !isVideoConsultationBooking(selectedBooking)) {
        setSelectedBookingVideoSession(null);
        setBookingVideoCallOpen(false);
        setBookingVideoCallAppointment(null);
        return;
      }

      try {
        const response = await fetch(`${backendBaseUrl}/api/calendar/appointments/${selectedBookingId}/video-session/`, {
          headers: { Authorization: `Token ${token}`, Accept: 'application/json' },
        });
        const payload = await response.json().catch(() => ({}));

        if (!cancelled) {
          setSelectedBookingVideoSession(response.ok ? payload : null);
        }
      } catch (error) {
        if (!cancelled) {
          setSelectedBookingVideoSession(null);
        }
      }
    };

    loadSelectedBookingVideoSession();

    return () => {
      cancelled = true;
    };
  }, [dialogMode, openDialog, selectedBooking, token]);

  useEffect(() => {
    if (!snackbarOpen) return undefined;
    const timer = window.setTimeout(() => setSnackbarOpen(false), 4000);
    return () => window.clearTimeout(timer);
  }, [snackbarOpen, snackbarMessage, snackbarSeverity]);

  const fetchCareProviderStripeStatus = async () => {
    if (!token) return;

    setCareProviderStripeStatus((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const response = await fetch(`${backendBaseUrl}/api/calendar/stripe/connect/status/?account_type=careprovider`, {
        headers: { 'Authorization': `Token ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Unable to load Stripe readiness');
      }

      setCareProviderStripeStatus({
        loading: false,
        error: '',
        chargesEnabled: Boolean(data.charges_enabled),
        payoutsEnabled: Boolean(data.payouts_enabled),
        onboardingComplete: Boolean(data.onboarding_complete),
      });
    } catch (error) {
      setCareProviderStripeStatus((prev) => ({
        ...prev,
        loading: false,
        error: error.message || 'Unable to load Stripe readiness',
      }));
    }
  };

  const fetchClinicOpeningHours = async () => {
    if (!token) return;

    try {
      const response = await fetch(`${backendBaseUrl}/api/profile/clinic/settings/`, {
        headers: { 'Authorization': `Token ${token}` },
      });

      if (!response.ok) {
        return;
      }

      const data = await response.json().catch(() => ({}));
      setPatientBookingOpeningHoursEnabled(Boolean(data.patient_booking_opening_hours_enabled));
      const normalizedClinicOpeningHours = normalizeClinicOpeningHours(data.opening_hours);
      setClinicOpeningHours(normalizedClinicOpeningHours);
      setStaffOpeningHours(normalizeStaffOpeningHours(data.staff_opening_hours, normalizedClinicOpeningHours));
      setLateCancellationThresholdHours(Number(data.late_cancellation_threshold_hours || 24));
      setLateCancellationFeeAmount(Number(data.late_cancellation_fee_amount || 0));
    } catch (error) {
      console.error('Error fetching clinic opening hours:', error);
    }
  };

  // Long-press time adjustment
  const [timeAdjustAnchor, setTimeAdjustAnchor] = useState(null);
  const [timeAdjustBooking, setTimeAdjustBooking] = useState(null);
  const [timeAdjustStart, setTimeAdjustStart] = useState('');
  const [timeAdjustEnd, setTimeAdjustEnd] = useState('');
  const [timeAdjustSaving, setTimeAdjustSaving] = useState(false);
  const longPressTimerRef = useRef(null);
  const longPressMovedRef = useRef(false);
  const longPressTargetRef = useRef(null);
  const [payerReferralOptions, setPayerReferralOptions] = useState([]);
  const [clinicOtherPayers, setClinicOtherPayers] = useState([]);
  const [selectedPayer, setSelectedPayer] = useState('patient');
  const [patientTreatmentOptions, setPatientTreatmentOptions] = useState([]);
  const [patientTreatmentsLoading, setPatientTreatmentsLoading] = useState(false);
  const [patientSearchInput, setPatientSearchInput] = useState('');
  const [patientSearchResults, setPatientSearchResults] = useState([]);
  const [patientSearchLoading, setPatientSearchLoading] = useState(false);
  const [clinicLocations, setClinicLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedRoom, setSelectedRoom] = useState('');
  const [selectedCalendar, setSelectedCalendar] = useState('');
  const [toolbarRoomFilterIds, setToolbarRoomFilterIds] = useState([]);
  const [toolbarStaffFilterIds, setToolbarStaffFilterIds] = useState([]);
  const [availabilitySlots, setAvailabilitySlots] = useState([]);
  const [resourceViewMode, setResourceViewMode] = useState('staff'); // 'rooms' or 'staff'
  const [clinicServices, setClinicServices] = useState([]);
  const [patientBookingOpeningHoursEnabled, setPatientBookingOpeningHoursEnabled] = useState(false);
  const [clinicOpeningHours, setClinicOpeningHours] = useState(createClosedOpeningHours());
  const [staffOpeningHours, setStaffOpeningHours] = useState({});
  const [lateCancellationThresholdHours, setLateCancellationThresholdHours] = useState(24);
  const [lateCancellationFeeAmount, setLateCancellationFeeAmount] = useState(0);
  const [careProviderStripeStatus, setCareProviderStripeStatus] = useState({
    loading: false,
    chargesEnabled: false,
    payoutsEnabled: false,
    onboardingComplete: false,
    error: '',
  });
  const selectedPayerRef = useRef(selectedPayer);
  const bookingDialogPractitionerManualRef = useRef(false);

  const bookedPatientOptions = useMemo(() => {
    const patientsById = new Map();
    bookings.forEach((booking) => {
      if (!booking.patient || patientsById.has(String(booking.patient))) return;
      const nameParts = String(booking.patient_name || '').trim().split(/\s+/);
      patientsById.set(String(booking.patient), {
        user_id: String(booking.patient), first_name: nameParts.shift() || '', last_name: nameParts.join(' '),
        email: booking.patient_email || '', phone: booking.patient_phone || '',
      });
    });
    return Array.from(patientsById.values());
  }, [bookings]);

  const bookingPatientOptions = useMemo(() => {
    const patientsById = new Map();
    [...profiles, ...bookedPatientOptions, ...patientSearchResults].forEach((patient) => {
      if (patient?.user_id) patientsById.set(String(patient.user_id), patient);
    });
    return Array.from(patientsById.values());
  }, [bookedPatientOptions, patientSearchResults, profiles]);

  const getServiceDefaultColor = useCallback((serviceId) => {
    const service = clinicServices.find((candidate) => String(candidate.id) === String(serviceId));
    return service?.color || DEFAULT_BOOKING_COLOR;
  }, [clinicServices]);

  const getPatientDisplayName = useCallback((patientId) => {
    if (!patientId) {
      return '';
    }

    const patient = bookingPatientOptions.find((candidate) => String(candidate.user_id) === String(patientId));

    if (!patient) {
      return '';
    }

    return `${patient.first_name || ''} ${patient.last_name || ''}`.trim() || patient.email || '';
  }, [bookingPatientOptions]);

  const getPatientDisplaySummary = useCallback((patientId) => {
    if (!patientId) {
      return '';
    }

    const patient = bookingPatientOptions.find((candidate) => String(candidate.user_id) === String(patientId));

    if (!patient) {
      return String(patientId);
    }

    const fullName = `${patient.first_name || ''} ${patient.last_name || ''}`.trim() || patient.email || '';
    const cprNumber = String(patient.cpr || patient.pii_number || '').trim();
    return cprNumber ? `${fullName}\nCPR: ${cprNumber}` : fullName;
  }, [bookingPatientOptions]);

  const getClassAttendanceDefaultNote = useCallback((booking, patientId, attended) => {
    const classLabel = booking?.title || booking?.service_name || 'Class';
    const patientLabel = getPatientDisplaySummary(patientId) || 'Patient';
    const startLabel = booking?.start_datetime ? moment(booking.start_datetime).format('DD MMM YYYY HH:mm') : '';
    const attendanceLabel = attended
      ? 'Patient attended '
      : 'Patient did not attend this class.';
    return `${attendanceLabel}${startLabel ? `Class: ${classLabel} on ${startLabel}, and is following the agreed exercise plan.` : `\nClass: ${classLabel}`}${patientLabel ? `` : ''}`;
  }, [getPatientDisplaySummary]);

  const buildClassAttendanceJournalNoteHtml = useCallback((booking, patientId, attended, manualNote = '') => {
    const attendanceSummary = getClassAttendanceDefaultNote(booking, patientId, attended);
    const manualNoteText = String(manualNote || '').trim();
    const symptomParagraph = manualNoteText || 'Class attendance update.';

    return [
      '<p><strong>Symptom development since last consultation:</strong></p>',
      `<p>${escapeHtml(symptomParagraph)}</p>`,
      '<hr>',
      '<p><strong>Treatment and adjustments:</strong></p>',
      '<p>No exercise feedback recorded for this class attendance.</p>',
      '<hr>',
      '<p><strong>Plan until next time:</strong></p>',
      `<p>${escapeHtml(attendanceSummary)}</p>`,
      '<hr>',
      '<p><strong>Patient accept:</strong></p>',
      '<p>Agreed.</p>',
    ].join('');
  }, [getClassAttendanceDefaultNote]);

  const buildClassAttendanceDrafts = useCallback((booking) => {
    const attendeeIds = Array.isArray(booking?.classPatientIds)
      ? booking.classPatientIds
      : Array.isArray(booking?.service?.class_patients)
        ? booking.service.class_patients
        : [];

    return attendeeIds.map((patientId) => ({
      patientId: String(patientId),
      attended: false,
      note: '',
      treatmentId: '',
      treatmentLabel: '',
    }));
  }, [getClassAttendanceDefaultNote]);

  const isClassBooking = useCallback((bookingOrEvent) => {
    if (!bookingOrEvent) {
      return false;
    }

    const candidate = bookingOrEvent.resource || bookingOrEvent;
    return Boolean(
      candidate?.booking_kind === 'class'
      || candidate?.isClassService
      || candidate?.service?.is_class
      || String(candidate?.appointment_type || '').toLowerCase() === 'class'
      || String(candidate?.service?.service_type || '').toLowerCase() === 'class'
    );
  }, []);

  const getPractitionerDisplayName = useCallback((staffId) => {
    if (!staffId) {
      return '';
    }

    const staffMember = Array.isArray(practitioners)
      ? practitioners.find((candidate) => String(candidate.id) === String(staffId))
      : null;

    if (!staffMember) {
      return String(staffId);
    }

    return staffMember.name || staffMember.display_name || staffMember.full_name || staffMember.email || String(staffId);
  }, [practitioners]);

  const getTreatmentDisplayName = useCallback((treatment) => {
    if (!treatment) {
      return '';
    }

    const diagnosis = treatment.diagnosis || {};
    return treatment.display_name || treatment.name || treatment.diagnosis_title || diagnosis.title || diagnosis.diagnosis || 'Untitled course';
  }, []);

  const getSuggestedTreatmentIdForPatient = useCallback((patientId, treatments, preferredTreatmentId = '') => {
    if (!patientId || !Array.isArray(treatments) || treatments.length === 0) {
      return '';
    }

    const normalizedPreferredId = String(preferredTreatmentId || '');
    if (normalizedPreferredId && treatments.some((treatment) => String(treatment.id) === normalizedPreferredId)) {
      return normalizedPreferredId;
    }

    if (treatments.length === 1) {
      return String(treatments[0].id);
    }

    const recentBooking = bookings
      .filter((booking) => (
        String(booking.patient || '') === String(patientId)
        && booking.treatment
        && booking.status !== 'cancelled'
      ))
      .sort((left, right) => new Date(right.start_datetime) - new Date(left.start_datetime))[0];

    const recentTreatmentId = String(recentBooking?.treatment || '');
    if (recentTreatmentId && treatments.some((treatment) => String(treatment.id) === recentTreatmentId)) {
      return recentTreatmentId;
    }

    return String(treatments[0].id);
  }, [bookings]);

  const fetchClassAttendanceTreatmentInfo = useCallback(async (patientId, preferredTreatmentId = '') => {
    if (!token || !patientId) {
      return { treatmentId: '', treatmentLabel: '' };
    }

    try {
      const response = await fetch(`${backendBaseUrl}/treatment/user/treatments/?patient=${encodeURIComponent(patientId)}`, {
        headers: { Authorization: `Token ${token}` },
      });

      if (!response.ok) {
        return { treatmentId: '', treatmentLabel: '' };
      }

      const data = await response.json();
      const treatments = Array.isArray(data) ? data : (data.treatments || data.results || []);
      if (!Array.isArray(treatments) || treatments.length === 0) {
        return { treatmentId: '', treatmentLabel: '' };
      }

      const treatmentId = getSuggestedTreatmentIdForPatient(patientId, treatments, preferredTreatmentId);
      const treatment = treatments.find((candidate) => String(candidate.id) === String(treatmentId)) || treatments[0];
      return {
        treatmentId: String(treatmentId || treatment?.id || ''),
        treatmentLabel: getTreatmentDisplayName(treatment),
      };
    } catch (error) {
      console.error('Error resolving class attendee treatment info:', error);
      return { treatmentId: '', treatmentLabel: '' };
    }
  }, [getSuggestedTreatmentIdForPatient, getTreatmentDisplayName, token]);

  const fetchPatientTreatmentOptions = useCallback(async (patientId, preferredTreatmentId = '') => {
    if (!token || !patientId) {
      setPatientTreatmentOptions([]);
      setPatientTreatmentsLoading(false);
      return;
    }

    setPatientTreatmentsLoading(true);
    try {
      const response = await fetch(`${backendBaseUrl}/treatment/user/treatments/?patient=${encodeURIComponent(patientId)}`, {
        headers: { 'Authorization': `Token ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to load treatments');
      }

      const data = await response.json();
      const treatments = Array.isArray(data) ? data : (data.treatments || data.results || []);
      setPatientTreatmentOptions(treatments);

      const nextTreatmentId = getSuggestedTreatmentIdForPatient(patientId, treatments, preferredTreatmentId);
      setForm((currentForm) => {
        if (String(currentForm.patient || '') !== String(patientId) || currentForm.bookingMode !== 'patient') {
          return currentForm;
        }

        const currentTreatmentIsAvailable = treatments.some(
          (treatment) => String(treatment.id) === String(currentForm.treatment || '')
        );
        if (currentTreatmentIsAvailable || String(currentForm.treatment || '') === String(nextTreatmentId || '')) {
          return currentForm;
        }

        return {
          ...currentForm,
          treatment: nextTreatmentId,
        };
      });
    } catch (error) {
      console.error('Error fetching patient treatments:', error);
      setPatientTreatmentOptions([]);
    } finally {
      setPatientTreatmentsLoading(false);
    }
  }, [getSuggestedTreatmentIdForPatient, token]);

  const selectedFormService = clinicServices.find((candidate) => String(candidate.id) === String(form.appointmentType));
  const selectedFormAppointmentType = selectedFormService
    ? (selectedFormService.service_type || 'other')
    : (form.appointmentType || 'follow_up');
  const resolveBookingTreatmentSeed = useCallback((bookingTreatmentId = '') => {
    const normalizedBookingTreatmentId = String(bookingTreatmentId || '');
    if (normalizedBookingTreatmentId) {
      return normalizedBookingTreatmentId;
    }

    return String(selectedTreatmentIdProp || '');
  }, [selectedTreatmentIdProp]);
  const showAssessmentDeliveryModeField = form.bookingMode === 'patient' && !selectedBooking && selectedFormAppointmentType === 'initial_consultation';
  const overlayLocationId = selectedLocation || form.clinic_location || '';

  const getCalendarsForLocation = useCallback((locationId) => {
    if (!locationId) {
      return [];
    }

    const location = clinicLocations.find((candidate) => String(candidate.id) === String(locationId));
    const calendars = Array.isArray(location?.calendars) ? location.calendars : [];

    return calendars
      .filter((calendar) => calendar && calendar.is_active !== false)
      .slice()
      .sort((left, right) => {
        const orderDelta = Number(left.order || 0) - Number(right.order || 0);
        if (orderDelta !== 0) return orderDelta;
        return String(left.name || '').localeCompare(String(right.name || ''));
      });
  }, [clinicLocations]);

  const getDefaultCalendarIdForLocation = useCallback((locationId) => {
    const calendars = getCalendarsForLocation(locationId);
    return calendars.length > 0 ? String(calendars[0].id) : '';
  }, [getCalendarsForLocation]);

  const selectedLocationRooms = useMemo(() => {
    if (!selectedLocation) {
      return [];
    }
    const location = clinicLocations.find((candidate) => String(candidate.id) === String(selectedLocation));
    const rooms = Array.isArray(location?.rooms) ? location.rooms : [];
    return rooms
      .slice()
      .sort((left, right) => {
        const orderDelta = Number(left.order || 0) - Number(right.order || 0);
        if (orderDelta !== 0) return orderDelta;
        return String(left.name || '').localeCompare(String(right.name || ''));
      });
  }, [clinicLocations, selectedLocation]);


  useEffect(() => {
    if (!Array.isArray(clinicLocations) || clinicLocations.length === 0) {
      return;
    }

    const hasValidSelection = selectedLocation
      && clinicLocations.some((location) => String(location.id) === String(selectedLocation));

    if (hasValidSelection) {
      return;
    }

    const primaryLocation = clinicLocations.find((location) => location?.is_primary);
    const nextLocation = primaryLocation || clinicLocations[0];

    if (nextLocation?.id) {
      setSelectedLocation(String(nextLocation.id));
    }
  }, [clinicLocations, selectedLocation]);
    const selectedLocationData = useMemo(() => {
      if (!selectedLocation) {
        return null;
      }
      return clinicLocations.find((candidate) => String(candidate.id) === String(selectedLocation)) || null;
    }, [clinicLocations, selectedLocation]);

    const toolbarLocationCalendars = useMemo(
      () => getCalendarsForLocation(selectedLocation),
      [selectedLocation, getCalendarsForLocation]
    );

    const activeRoomFilterIds = useMemo(() => {
      const toolbarFilterIds = toolbarRoomFilterIds.map((roomId) => String(roomId)).filter(Boolean);
      if (toolbarFilterIds.length > 0) {
        return toolbarFilterIds;
      }
      return selectedRoom ? [String(selectedRoom)] : [];
    }, [selectedRoom, toolbarRoomFilterIds]);

    const activeStaffFilterIds = useMemo(() => {
      return toolbarStaffFilterIds.map((staffId) => String(staffId)).filter(Boolean);
    }, [toolbarStaffFilterIds]);

    const singleStaffDefaultViewKeyRef = useRef(null);
    const suppressStaffWeekAutoSwitchRef = useRef(false);

    useEffect(() => {
      const showingAllRooms = resourceViewMode === 'rooms'
        && !selectedRoom
        && activeRoomFilterIds.length === 0
        && !selectedPractitioner
        && activeStaffFilterIds.length === 0;
      const showingAllStaff = resourceViewMode === 'staff' && !selectedPractitioner && activeStaffFilterIds.length === 0;

      // The all-rooms / all-staff overview defaults to the day (resource) view.
      if (showingAllRooms || showingAllStaff) {
        singleStaffDefaultViewKeyRef.current = null;
        if (calendarView !== Views.DAY) {
          handleCalendarViewChange(Views.DAY);
        }
        return;
      }

      // When a single staff member becomes selected we default to the week view, but only once
      // per selection change so the user can still toggle to the Day schedule afterwards.
      if (resourceViewMode === 'staff') {
        const singleStaffKey = selectedPractitioner
          ? `p:${selectedPractitioner}`
          : activeStaffFilterIds.length === 1
            ? `f:${activeStaffFilterIds[0]}`
            : null;

        if (singleStaffKey) {
          if (!showWeekendDays) {
            const weekStart = moment(calendarDate || new Date()).startOf('week');
            if (!moment(calendarDate).isSame(weekStart, 'day')) {
              setCalendarDate(weekStart.toDate());
            }
          }

          if (suppressStaffWeekAutoSwitchRef.current && calendarView === Views.DAY) {
            singleStaffDefaultViewKeyRef.current = singleStaffKey;
            return;
          }

          if (singleStaffDefaultViewKeyRef.current !== singleStaffKey) {
            singleStaffDefaultViewKeyRef.current = singleStaffKey;
            const staffWeekView = showWeekendDays ? Views.WEEK : Views.WORK_WEEK;
            if (calendarView !== staffWeekView) {
              handleCalendarViewChange(staffWeekView);
            }
          }
          return;
        }
      }

      singleStaffDefaultViewKeyRef.current = null;
    }, [activeRoomFilterIds.length, activeStaffFilterIds, calendarView, handleCalendarViewChange, resourceViewMode, selectedPractitioner, selectedRoom, showWeekendDays]);

    const fetchAvailabilitySlots = useCallback(async () => {
      if (!token || !selectedLocation) {
        setAvailabilitySlots([]);
        return;
      }

      try {
        const params = new URLSearchParams();
        params.set('clinic_location_id', String(selectedLocation));

        const response = await fetch(`${backendBaseUrl}/api/calendar/availability/?${params.toString()}`, {
          headers: { 'Authorization': `Token ${token}` },
        });

        if (!response.ok) {
          setAvailabilitySlots([]);
          return;
        }

        const data = await response.json().catch(() => ({}));
        setAvailabilitySlots(Array.isArray(data.availability_slots) ? data.availability_slots : []);
      } catch (error) {
        console.error('Error fetching availability slots:', error);
        setAvailabilitySlots([]);
      }
    }, [selectedLocation, token]);

    useEffect(() => {
      fetchAvailabilitySlots();
    }, [fetchAvailabilitySlots]);

    const availablePractitioners = useMemo(() => {
      if (!selectedLocationData) {
        return practitioners;
      }

      const locationStaffIds = new Set(
        (selectedLocationData.staff_members_detail || []).map((staff) => String(staff.id)).filter(Boolean)
      );
      let filtered = locationStaffIds.size > 0
        ? practitioners.filter((staff) => locationStaffIds.has(String(staff.id)))
        : [];

      const roomAllocatedStaffIds = activeRoomFilterIds.length > 0
        ? new Set(
          availabilitySlots
            .filter((slot) => (
              String(slot.clinic_location || '') === String(selectedLocation)
              && activeRoomFilterIds.includes(String(slot.room || ''))
            ))
            .map((slot) => String(slot.staff_member || '')).filter(Boolean)
        )
        : new Set();

      if (roomAllocatedStaffIds.size > 0) {
        filtered = filtered.filter((staff) => roomAllocatedStaffIds.has(String(staff.id)));
      }

      if (selectedBooking?.staff_member) {
        const selectedBookingStaff = practitioners.find((staff) => String(staff.id) === String(selectedBooking.staff_member));
        if (selectedBookingStaff && !filtered.some((staff) => String(staff.id) === String(selectedBookingStaff.id))) {
          filtered = [selectedBookingStaff, ...filtered];
        }
      }

      return filtered;
    }, [activeRoomFilterIds, availabilitySlots, practitioners, selectedBooking, selectedLocation, selectedLocationData]);

    useEffect(() => {
      if (!selectedPractitioner) {
        return;
      }

      if (!availablePractitioners.some((staff) => String(staff.id) === String(selectedPractitioner))) {
        setSelectedPractitioner('');
      }
    }, [availablePractitioners, selectedPractitioner]);

    const bookingAvailablePractitioners = useMemo(() => {
      if (!form.clinic_location) {
        return practitioners;
      }

      const location = clinicLocations.find((candidate) => String(candidate.id) === String(form.clinic_location)) || null;
      if (!location) {
        return practitioners;
      }

      const locationStaffIds = new Set(
        (location.staff_members_detail || []).map((staff) => String(staff.id)).filter(Boolean)
      );
      let filtered = locationStaffIds.size > 0
        ? practitioners.filter((staff) => locationStaffIds.has(String(staff.id)))
        : [];

      const roomAllocatedStaffIds = form.bookingMode === 'non_patient' || !form.room
        ? new Set()
        : new Set(
          availabilitySlots
            .filter((slot) => (
              String(slot.clinic_location || '') === String(form.clinic_location)
              && String(slot.room || '') === String(form.room)
            ))
            .map((slot) => String(slot.staff_member || '')).filter(Boolean)
        );

      if (roomAllocatedStaffIds.size > 0) {
        filtered = filtered.filter((staff) => roomAllocatedStaffIds.has(String(staff.id)));
      }

      if (selectedBooking?.staff_member) {
        const selectedBookingStaff = practitioners.find((staff) => String(staff.id) === String(selectedBooking.staff_member));
        if (selectedBookingStaff && !filtered.some((staff) => String(staff.id) === String(selectedBookingStaff.id))) {
          filtered = [selectedBookingStaff, ...filtered];
        }
      }

      return filtered;
    }, [availabilitySlots, clinicLocations, form.clinic_location, form.room, practitioners, selectedBooking]);

    useEffect(() => {
      if (!form.practitioner) {
        return;
      }

      if (openDialog && dialogMode === 'create') {
        return;
      }

      if (!bookingAvailablePractitioners.some((staff) => String(staff.id) === String(form.practitioner))) {
        setForm((current) => ({ ...current, practitioner: '' }));
      }
    }, [bookingAvailablePractitioners, dialogMode, form.practitioner, openDialog, selectedPractitioner]);

    useEffect(() => {
      if (!openDialog || dialogMode !== 'create') {
        return;
      }

      const seededPractitionerId = String(selectedPractitioner || '').trim();
      const currentPractitionerId = String(form.practitioner || '').trim();

      const resolveMatchingRoom = (practitionerId, currentForm) => {
        if (!practitionerId || !currentForm.date || !currentForm.time) {
          return '';
        }

        const startMoment = moment(`${currentForm.date}T${currentForm.time}`, 'YYYY-MM-DDTHH:mm');
        const endMoment = currentForm.endTime
          ? moment(`${currentForm.date}T${currentForm.endTime}`, 'YYYY-MM-DDTHH:mm')
          : startMoment.clone().add(30, 'minutes');

        const matchedAllocation = (Array.isArray(availabilitySlots) ? availabilitySlots : []).find((slot) => {
          if (!slot) return false;

          const slotStaffId = String(slot.staff_member || slot.staffId || slot.staff_member_id || '').trim();
          if (slotStaffId !== practitionerId) {
            return false;
          }

          if (String(slot.clinic_location || '') !== String(currentForm.clinic_location || selectedLocation || '')) {
            return false;
          }

          const slotDateKey = slot.specific_date ? moment(slot.specific_date).format('YYYY-MM-DD') : '';
          const slotWeekday = Number(slot.day_of_week);
          const selectedDateKey = startMoment.format('YYYY-MM-DD');
          const selectedWeekday = (startMoment.day() + 6) % 7;
          const matchesDate = slotDateKey
            ? slotDateKey === selectedDateKey
            : Number.isFinite(slotWeekday)
              ? slotWeekday === selectedWeekday
              : true;

          const slotStartMinutes = parseTimeToMinutes(slot.start_time);
          const slotEndMinutes = parseTimeToMinutes(slot.end_time);
          const selectedStartMinutes = startMoment.hours() * 60 + startMoment.minutes();
          const selectedEndMinutes = endMoment.hours() * 60 + endMoment.minutes();
          const matchesTime = Number.isFinite(slotStartMinutes) && Number.isFinite(slotEndMinutes)
            ? selectedStartMinutes >= slotStartMinutes && selectedEndMinutes <= slotEndMinutes
            : true;

          return matchesDate && matchesTime && String(slot.room || slot.room_id || slot.roomId || '').trim();
        });

        return String(matchedAllocation?.room || matchedAllocation?.room_id || matchedAllocation?.roomId || '').trim();
      };

      const preferredPractitionerId = bookingDialogPractitionerManualRef.current
        ? currentPractitionerId
        : (seededPractitionerId || currentPractitionerId);

      if (preferredPractitionerId) {
        setForm((current) => {
          const nextRoom = resolveMatchingRoom(preferredPractitionerId, current);
          const nextPractitioner = preferredPractitionerId;

          if (String(current.practitioner || '') === nextPractitioner && String(current.room || '') === String(nextRoom || '')) {
            return current;
          }

          return {
            ...current,
            practitioner: nextPractitioner,
            ...(nextRoom ? { room: nextRoom } : {}),
          };
        });
        return;
      }

      if (bookingAvailablePractitioners.length === 0) {
        return;
      }

      const firstAvailablePractitioner = bookingAvailablePractitioners[0];
      if (!firstAvailablePractitioner) {
        return;
      }

      setForm((current) => {
        if (current.practitioner) {
          return current;
        }

        return {
          ...current,
          practitioner: String(firstAvailablePractitioner.id || ''),
        };
      });
    }, [availabilitySlots, bookingAvailablePractitioners, dialogMode, form.practitioner, openDialog, selectedLocation, selectedPractitioner]);


  useEffect(() => {
    if (!selectedLocation) {
      if (selectedCalendar) {
        setSelectedCalendar('');
      }
      return;
    }

    const calendars = getCalendarsForLocation(selectedLocation);
    const defaultCalendarId = calendars.length > 0 ? String(calendars[0].id) : '';
    const hasSelectedCalendar = selectedCalendar
      && calendars.some((calendar) => String(calendar.id) === String(selectedCalendar));

    if (!hasSelectedCalendar && defaultCalendarId !== selectedCalendar) {
      setSelectedCalendar(defaultCalendarId);
    }
  }, [getCalendarsForLocation, selectedCalendar, selectedLocation]);

  const activeOverlayCalendar = null;

  useEffect(() => {
    selectedPayerRef.current = selectedPayer;
  }, [selectedPayer]);

  // --- Bidirectional patient selection sync via localStorage ---
  const calendarUserId = user && (user.id || user.user_id) ? (user.id || user.user_id) : null;

  // Keep the visible calendar date stable across remounts/re-renders.
  // This prevents jumps back to "today" if the calendar subtree remounts.
  const loadedCalendarDateUserRef = useRef(null);
  useEffect(() => {
    if (!calendarUserId) return;
    if (loadedCalendarDateUserRef.current === calendarUserId) return;
    loadedCalendarDateUserRef.current = calendarUserId;
    try {
      const saved = localStorage.getItem(`vp_calendarDate_${calendarUserId}`);
      if (!saved) return;
      const parsed = new Date(saved);
      if (!Number.isNaN(parsed.getTime())) {
        setCalendarDate(parsed);
      }
    } catch (e) { /* ignore */ }
  }, [calendarUserId]);

  useEffect(() => {
    if (!calendarUserId || !calendarDate) return;
    try {
      localStorage.setItem(`vp_calendarDate_${calendarUserId}`, calendarDate.toISOString());
    } catch (e) { /* ignore */ }
  }, [calendarUserId, calendarDate]);

  // Resolve selected patient on demand (prop has priority, then localStorage)
  // to avoid re-rendering the calendar when appointment clicks sync patient selection.
  const getSelectedPatientId = useCallback(() => {
    if (selectedPatientIdProp) return String(selectedPatientIdProp);
    if (!calendarUserId) return '';
    try {
      return localStorage.getItem(`vp_selectedPatientId_${calendarUserId}`) || '';
    } catch (e) {
      return '';
    }
  }, [selectedPatientIdProp, calendarUserId]);

  const latestSelectedPatientIdRef = useRef('');

  useEffect(() => {
    latestSelectedPatientIdRef.current = getSelectedPatientId();
  }, [getSelectedPatientId]);

  useEffect(() => {
    const selectedPatientId = getSelectedPatientId();
    if (!selectedPatientId || form.bookingMode !== 'patient') {
      return;
    }

    if (dialogMode === 'edit' && selectedBooking) {
      return;
    }

    setForm((currentForm) => (
      String(currentForm.patient || '') === String(selectedPatientId)
        ? currentForm
        : { ...currentForm, patient: String(selectedPatientId) }
    ));
  }, [form.bookingMode, getSelectedPatientId, dialogMode, selectedBooking, selectedPatientIdProp]);

  useEffect(() => {
    if (form.bookingMode !== 'patient') {
      return;
    }

    const selectedPatient = bookingPatientOptions.find((patient) => String(patient.user_id) === String(form.patient));

    if (!selectedPatient) {
      setForm((currentForm) => (
        currentForm.patientEmail || currentForm.patientPhone
          ? { ...currentForm, patientEmail: '', patientPhone: '' }
          : currentForm
      ));
      return;
    }

    const nextPatientEmail = selectedPatient.email || selectedPatient.user?.email || '';
    const nextPatientPhone = selectedPatient.phone || selectedPatient.user?.phone || '';

    setForm((currentForm) => (
      currentForm.patientEmail === nextPatientEmail && currentForm.patientPhone === nextPatientPhone
        ? currentForm
        : { ...currentForm, patientEmail: nextPatientEmail, patientPhone: nextPatientPhone }
    ));
  }, [bookingPatientOptions, form.bookingMode, form.patient]);

  useEffect(() => {
    if (!openDialog || form.bookingMode !== 'patient') {
      setPatientTreatmentOptions([]);
      setPatientTreatmentsLoading(false);
      return;
    }

    if (!form.patient) {
      setPatientTreatmentOptions([]);
      setPatientTreatmentsLoading(false);
      setForm((currentForm) => (
        currentForm.treatment
          ? { ...currentForm, treatment: '' }
          : currentForm
      ));
      return;
    }

    void fetchPatientTreatmentOptions(
      form.patient,
      resolveBookingTreatmentSeed(selectedBooking?.treatment || '')
    );
  }, [fetchPatientTreatmentOptions, form.bookingMode, form.patient, openDialog, resolveBookingTreatmentSeed, selectedBooking]);

  useEffect(() => {
    if (!openDialog || selectedBooking || form.bookingMode !== 'patient' || selectedFormAppointmentType !== 'initial_consultation') {
      return;
    }

    const normalizedSourceTreatmentId = String(selectedTreatmentIdProp || '');
    if (!normalizedSourceTreatmentId) {
      return;
    }

    if (!patientTreatmentOptions.some((treatment) => String(treatment.id) === normalizedSourceTreatmentId)) {
      return;
    }

    setForm((currentForm) => {
      if (String(currentForm.patient || '') !== String(form.patient)) {
        return currentForm;
      }

      if (String(currentForm.treatment || '') === normalizedSourceTreatmentId) {
        return currentForm;
      }

      return {
        ...currentForm,
        treatment: normalizedSourceTreatmentId,
      };
    });
  }, [form.bookingMode, form.patient, openDialog, patientTreatmentOptions, selectedBooking, selectedFormAppointmentType, selectedTreatmentIdProp]);

  const lastManualSelectRef = useRef(() => {
    try {
      const saved = localStorage.getItem('vp_calendarLastManualSelect');
      return saved ? Number(saved) : 0;
    } catch (e) { return 0; }
  });
  // Resolve the initializer on first render
  if (typeof lastManualSelectRef.current === 'function') {
    lastManualSelectRef.current = lastManualSelectRef.current();
  }
  const autoSelectTimerRef = useRef(null);
  const initializedSelectionRef = useRef(false);
  const bookingClickTimerRef = useRef(null);

  const getUpcomingPatientId = useCallback((sourceBookings = bookings) => {
    const now = new Date();
    const upcoming = (Array.isArray(sourceBookings) ? sourceBookings : [])
      .filter(booking => booking && booking.patient && new Date(booking.end_datetime) >= now)
      .sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime));
    return upcoming.length ? String(upcoming[0].patient) : '';
  }, [bookings]);

  const selectPatientFromBooking = useCallback((patientId) => {
    if (!patientId || !calendarUserId) {
      console.warn('[CALENDAR] Cannot select patient — patientId:', patientId, 'calendarUserId:', calendarUserId);
      return;
    }
    console.log('[CALENDAR] Selecting patient', patientId, 'for user', calendarUserId);
    // Write to localStorage synchronously (cross-page sync)
    const key = `vp_selectedPatientId_${calendarUserId}`;
    try {
      localStorage.setItem(key, String(patientId));
      // Dispatch event for same-tab listeners (native storage event only fires cross-tab)
      window.dispatchEvent(new StorageEvent('storage', {
        key,
        newValue: String(patientId),
        storageArea: localStorage,
      }));
      window.dispatchEvent(new CustomEvent('vp:selectedPatientId', {
        detail: {
          key,
          patientId: String(patientId),
        },
      }));
    } catch (e) { /* ignore */ }
  }, [calendarUserId]);

  const recordManualSelect = useCallback(() => {
    lastManualSelectRef.current = Date.now();
    try { localStorage.setItem('vp_calendarLastManualSelect', String(Date.now())); } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!calendarUserId || !bookings.length || initializedSelectionRef.current) return;

    const cachedPatientId = getSelectedPatientId();
    if (cachedPatientId) {
      initializedSelectionRef.current = true;
      return;
    }

    const nextUpcomingPatientId = getUpcomingPatientId(bookings);
    if (nextUpcomingPatientId) {
      selectPatientFromBooking(nextUpcomingPatientId);
    }
    initializedSelectionRef.current = true;
  }, [calendarUserId, bookings, getSelectedPatientId, getUpcomingPatientId, selectPatientFromBooking]);

  const clearPendingBookingDialogTimer = useCallback(() => {
    if (bookingClickTimerRef.current) {
      clearTimeout(bookingClickTimerRef.current);
      bookingClickTimerRef.current = null;
    }
  }, []);

  const syncPatientSelectionFromBooking = useCallback((booking) => {
    if (!booking || !booking.patient) {
      return;
    }

    recordManualSelect();
    selectPatientFromBooking(booking.patient);

    if (autoSelectTimerRef.current) clearTimeout(autoSelectTimerRef.current);
    autoSelectTimerRef.current = setTimeout(() => {
      const nextUpcomingPatientId = getUpcomingPatientId(bookings);
      if (nextUpcomingPatientId) selectPatientFromBooking(nextUpcomingPatientId);
    }, 60 * 60 * 1000);
  }, [bookings, getUpcomingPatientId, recordManualSelect, selectPatientFromBooking]);

  useEffect(() => {
    initializedSelectionRef.current = false;
  }, [calendarUserId]);

  // Auto-select next upcoming patient when idle for 1 hour
  useEffect(() => {
    if (!calendarUserId || !bookings.length) return;

    const AUTO_SELECT_DELAY = 60 * 60 * 1000; // 1 hour

    const pickNextPatient = () => {
      const nextUpcomingPatientId = getUpcomingPatientId(bookings);
      if (nextUpcomingPatientId) selectPatientFromBooking(nextUpcomingPatientId);
    };

    const scheduleAutoSelect = () => {
      if (autoSelectTimerRef.current) clearTimeout(autoSelectTimerRef.current);
      autoSelectTimerRef.current = setTimeout(() => {
        pickNextPatient();
        // Re-schedule so it keeps updating if still idle
        scheduleAutoSelect();
      }, AUTO_SELECT_DELAY);
    };

    // On mount / bookings change, auto-select if no manual selection has happened yet
    // (covers "physio logs in first thing in the morning" scenario)
    const msSinceLastSelect = Date.now() - lastManualSelectRef.current;
    const hasExistingSelection = Boolean(getSelectedPatientId());
    if (!hasExistingSelection && msSinceLastSelect >= AUTO_SELECT_DELAY) {
      pickNextPatient();
    }

    scheduleAutoSelect();

    return () => {
      if (autoSelectTimerRef.current) clearTimeout(autoSelectTimerRef.current);
    };
  }, [calendarUserId, bookings, getSelectedPatientId, getUpcomingPatientId, selectPatientFromBooking]);

  useEffect(() => {
    fetchBookings();
    fetchPractitioners();
    fetchClinicLocations();
    fetchClinicServices();
    fetchCareProviderStripeStatus();
    fetchClinicOpeningHours();
    // eslint-disable-next-line
  }, []);

  const fetchBookings = useCallback(async () => {
    setIsLoading(true);
    try {
      const appointmentParams = new URLSearchParams(getDefaultBookingWindow());
      const [appointmentsResponse, invoicesResponse] = await Promise.all([
        fetch(`${backendBaseUrl}/api/calendar/appointments/?${appointmentParams.toString()}`, {
          headers: { 'Authorization': `Token ${token}` },
        }),
        fetch(`${backendBaseUrl}/api/calendar/invoices/`, {
          headers: { 'Authorization': `Token ${token}` },
        }),
      ]);

      if (appointmentsResponse.ok) {
        const data = await appointmentsResponse.json();
        setBookings(data.appointments || []);
      }

      if (invoicesResponse.ok) {
        const data = await invoicesResponse.json();
        setInvoices(Array.isArray(data) ? data : data.results || []);
      }
    } catch (e) {
      console.error('Error fetching appointments:', e);
    }
    setIsLoading(false);
  }, [token]);

  useEffect(() => {
    const query = patientSearchInput.trim();
    if (!openDialog || form.bookingMode !== 'patient' || query.length < 2 || !token) {
      setPatientSearchResults([]);
      setPatientSearchLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setPatientSearchLoading(true);
      try {
        const response = await fetch(`${backendBaseUrl}/treatment/patients/search/?q=${encodeURIComponent(query)}`, {
          headers: { Authorization: `Token ${token}` }, signal: controller.signal,
        });
        const data = await response.json().catch(() => ({}));
        if (response.ok && !controller.signal.aborted) setPatientSearchResults(Array.isArray(data.patients) ? data.patients : []);
      } catch (error) {
        if (error.name !== 'AbortError') console.error('Error searching patients:', error);
      } finally {
        if (!controller.signal.aborted) setPatientSearchLoading(false);
      }
    }, 300);

    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [form.bookingMode, openDialog, patientSearchInput, token]);

  const fetchStaffVacations = useCallback(async () => {
    if (!token) return;

    try {
      const response = await fetch(`${backendBaseUrl}/api/calendar/vacations/`, {
        headers: { 'Authorization': `Token ${token}` },
      });

      if (!response.ok) {
        setStaffVacations([]);
        return;
      }

      const data = await response.json().catch(() => ({}));
      setStaffVacations(Array.isArray(data.vacations) ? data.vacations : []);
    } catch (error) {
      console.error('Error fetching staff absences:', error);
      setStaffVacations([]);
    }
  }, [token]);

  useEffect(() => {
    fetchStaffVacations();
  }, [fetchStaffVacations]);

  const upsertBookingInState = useCallback((bookingData) => {
    if (!bookingData?.id) {
      return;
    }

    setBookings((currentBookings) => {
      const nextBookings = Array.isArray(currentBookings) ? [...currentBookings] : [];
      const existingIndex = nextBookings.findIndex((booking) => String(booking.id) === String(bookingData.id));

      if (existingIndex >= 0) {
        nextBookings[existingIndex] = bookingData;
        return nextBookings;
      }

      return [bookingData, ...nextBookings];
    });
  }, []);

  const fetchPractitioners = async () => {
    try {
      const response = await fetch(`${backendBaseUrl}/profile/my_careprovider_staff/`, {
        headers: { 'Authorization': `Token ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        // Map backend staff data to practitioner format for dropdown
        const staffList = (data.staff || []).map(staff => ({
          id: staff.user_id,
          name: `${staff.first_name} ${staff.last_name}`.trim() || staff.email,
          staff_engagement_type: staff.staff_engagement_type || 'employee',
          stripe_account_id: staff.stripe_account_id || '',
          stripe_onboarding_complete: Boolean(staff.stripe_onboarding_complete),
        }));
        setPractitioners(staffList);
      }
    } catch (e) {
      console.error('Error fetching practitioners:', e);
    }
  };

  const fetchClinicServices = async () => {
    try {
      const response = await fetch(`${backendBaseUrl}/api/services/services/`, {
        headers: { 'Authorization': `Token ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setClinicServices(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      console.error('Error fetching clinic services:', e);
    }
  };

  const fetchClinicLocations = async () => {
    try {
      const response = await fetch(`${backendBaseUrl}/api/profile/clinic/locations/`, {
        headers: { 'Authorization': `Token ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setClinicLocations(data || []);
      }
    } catch (e) {
      console.error('Error fetching clinic locations:', e);
    }
  };

  const fetchPayerReferralOptions = useCallback(async (patientId) => {
    if (!patientId || !token) return;
    try {
      const response = await fetch(`${backendBaseUrl}/profile/patient/${patientId}/insurance/`, {
        headers: { 'Authorization': `Token ${token}` },
      });
      if (response.ok) {
        const insuranceRecords = await response.json();
        const optionsList = [];
        for (const insurance of insuranceRecords) {
          const refResponse = await fetch(
            `${backendBaseUrl}/profile/patient/${patientId}/insurance/${insurance.id}/referral-letters/`,
            { headers: { 'Authorization': `Token ${token}` } }
          );
          if (refResponse.ok) {
            const referralLetters = await refResponse.json();
            for (const referral of referralLetters) {
              optionsList.push({
                id: `ref:${referral.id}`,
                insuranceId: String(insurance.id),
                referralLetterId: String(referral.id),
                label: `${insurance.insurance_provider} · ${referral.referral_id}`,
                insuranceProvider: insurance.insurance_provider || '',
                billingEmail: insurance.billing_email || '',
                approvedTreatments: insurance.approved_treatments,
                totalCostAmount: insurance.total_cost_amount,
                referralDetails: referral,
              });
            }
          }
        }
        setPayerReferralOptions(optionsList);
        // Auto-select first referral letter if available
        if (optionsList.length > 0 && selectedPayerRef.current === 'patient') {
          setSelectedPayer(optionsList[0].id);
        }
      }
    } catch (e) {
      console.error('Error fetching payer options:', e);
    }
  }, [token]);

  const fetchClinicOtherPayers = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${backendBaseUrl}/api/profile/clinic/other-payers/`, {
        headers: { Authorization: `Token ${token}`, Accept: 'application/json' },
      });
      if (!response.ok) {
        setClinicOtherPayers([]);
        return;
      }
      const payers = await response.json();
      setClinicOtherPayers(
        Array.isArray(payers)
          ? payers.map((payer) => ({
              id: String(payer.id || ''),
              label: payer.name || 'Other payer',
              billingEmail: payer.billing_email || '',
              eanNumber: payer.ean_number || '',
              cvrNumber: payer.cvr_number || '',
            })).filter((option) => option.id)
          : []
      );
    } catch (error) {
      console.error('Error fetching other payers:', error);
      setClinicOtherPayers([]);
    }
  }, [token]);

  useEffect(() => {
    fetchClinicOtherPayers();
  }, [fetchClinicOtherPayers]);

  useEffect(() => {
    if (!openClassDialog || !selectedClassBooking) {
      setClassAttendanceDrafts([]);
      setClassAttendanceStaffId('');
      setClassDialogSaving(false);
      return;
    }

    const attendeeIds = Array.isArray(selectedClassBooking?.classPatientIds)
      ? selectedClassBooking.classPatientIds
      : Array.isArray(selectedClassBooking?.service?.class_patients)
        ? selectedClassBooking.service.class_patients
        : [];

    const initialDrafts = buildClassAttendanceDrafts(selectedClassBooking);
    setClassAttendanceDrafts(initialDrafts);
    const serviceStaffIds = Array.isArray(selectedClassBooking?.service?.staff_members)
      ? selectedClassBooking.service.staff_members.map((staffId) => String(staffId))
      : Array.isArray(selectedClassBooking?.service?.staff_member_ids)
        ? selectedClassBooking.service.staff_member_ids.map((staffId) => String(staffId))
        : [];
    const primaryServiceStaffId = String(selectedClassBooking?.service?.class_primary_staff || '').trim();
    const bookingStaffId = String(selectedClassBooking?.staff_member || '').trim();
    const nextClassAttendanceStaffId = bookingStaffId
      || (primaryServiceStaffId && (!serviceStaffIds.length || serviceStaffIds.includes(primaryServiceStaffId)) ? primaryServiceStaffId : '')
      || serviceStaffIds[0]
      || '';
    setClassAttendanceStaffId(nextClassAttendanceStaffId);

    let cancelled = false;
    const loadTreatmentInfo = async () => {
      const treatmentInfoList = await Promise.all(attendeeIds.map(async (patientId) => ({
        patientId: String(patientId),
        ...(await fetchClassAttendanceTreatmentInfo(patientId)),
      })));

      if (cancelled) {
        return;
      }

      setClassAttendanceDrafts((current) => current.map((entry) => {
        const treatmentInfo = treatmentInfoList.find((candidate) => String(candidate.patientId) === String(entry.patientId));
        if (!treatmentInfo) {
          return entry;
        }

        return {
          ...entry,
          treatmentId: treatmentInfo.treatmentId || entry.treatmentId || '',
          treatmentLabel: treatmentInfo.treatmentLabel || entry.treatmentLabel || '',
        };
      }));
    };

    void loadTreatmentInfo();

    return () => {
      cancelled = true;
    };
  }, [openClassDialog, selectedClassBooking, buildClassAttendanceDrafts, fetchClassAttendanceTreatmentInfo]);

  const classAttendanceStaffOptions = useMemo(() => {
    const serviceStaffIds = Array.isArray(selectedClassBooking?.service?.staff_members)
      ? selectedClassBooking.service.staff_members.map((staffId) => String(staffId)).filter(Boolean)
      : Array.isArray(selectedClassBooking?.service?.staff_member_ids)
        ? selectedClassBooking.service.staff_member_ids.map((staffId) => String(staffId)).filter(Boolean)
        : [];

    const candidatePractitioners = serviceStaffIds.length > 0
      ? practitioners.filter((practitioner) => serviceStaffIds.includes(String(practitioner.id)))
      : practitioners;

    const currentStaffId = String(classAttendanceStaffId || selectedClassBooking?.staff_member || '').trim();
    if (currentStaffId && !candidatePractitioners.some((practitioner) => String(practitioner.id) === currentStaffId)) {
      const currentPractitioner = practitioners.find((practitioner) => String(practitioner.id) === currentStaffId);
      if (currentPractitioner) {
        return [currentPractitioner, ...candidatePractitioners.filter((practitioner) => String(practitioner.id) !== currentStaffId)];
      }
    }

    return candidatePractitioners;
  }, [classAttendanceStaffId, practitioners, selectedClassBooking]);

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const formatAppointmentType = (appointmentType) => {
    if (!appointmentType) return 'Appointment';
    const normalized = String(appointmentType).toLowerCase();
    if (normalized.includes('video')) return 'Video Consultation';
    if (normalized.includes('follow')) return 'Follow-up';
    if (normalized.includes('assessment')) return 'Assessment';
    if (normalized.includes('treatment')) return 'Treatment Session';
    if (normalized.includes('in_person')) return 'In-Person';
    return 'Appointment';
  };

  const NON_PATIENT_BOOKING_OPTIONS = [
    { value: 'break', label: 'Break' },
    { value: 'meeting', label: 'Meeting' },
    { value: 'other', label: 'Other' },
    { value: 'vacation', label: 'Vacation' },
    { value: 'sick_day', label: 'Sick day' },
    { value: 'paid_leave', label: 'Paid leave' },
    { value: 'agreed_absence', label: 'Agreed absence' },
    { value: 'block', label: 'Block calendar' },
  ];

  const getNonPatientBookingLabel = (value) => {
    const match = NON_PATIENT_BOOKING_OPTIONS.find((option) => option.value === value);
    return match ? match.label : 'Blocked time';
  };

  // Services filtered by selected practitioner (hide services that don't include this staff)
  const filteredServices = useMemo(() => {
    if (!selectedPractitioner) return clinicServices;
    return clinicServices.filter(svc =>
      !svc.staff_members || svc.staff_members.length === 0 ||
      svc.staff_members.some(id => String(id) === String(selectedPractitioner))
    );
  }, [clinicServices, selectedPractitioner]);

  const selectedBookingStaff = useMemo(() => {
    const staffId = selectedBooking?.primary_staff_member || selectedBooking?.staff_member || '';
    if (!staffId) return null;
    return practitioners.find((staff) => String(staff.id) === String(staffId)) || null;
  }, [practitioners, selectedBooking]);

  const stripeAutopayDisabledReason = useMemo(() => {
    if (!selectedBooking?.id) return '';

    if (String(selectedBooking.billing_type || 'clinic') === 'staff_subcontractor') {
      if (careProviderStripeStatus.loading) {
        return 'Checking Stripe readiness...';
      }
      if (!selectedBookingStaff) {
        return 'This appointment is billed to a subcontractor, but that staff member is not loaded yet.';
      }
      if (String(selectedBookingStaff.staff_engagement_type || 'employee').toLowerCase() !== 'subcontractor') {
        return '';
      }
      if (!selectedBookingStaff.stripe_account_id || !selectedBookingStaff.stripe_onboarding_complete) {
        return `${selectedBookingStaff.name || 'This subcontractor'} has not finished Stripe setup yet.`;
      }
      return '';
    }

    return '';
  }, [careProviderStripeStatus.loading, selectedBooking, selectedBookingStaff]);

  const isStripeAutopayAvailable = !stripeAutopayDisabledReason;

  useEffect(() => {
    const bookingIsPaid = selectedBooking?.payment_status === 'paid';
    if (form.payment_method === 'CardAutopay' && !isStripeAutopayAvailable && !bookingIsPaid) {
      setForm((current) => ({ ...current, payment_method: '' }));
    }
  }, [form.payment_method, isStripeAutopayAvailable, selectedBooking]);

  // Map bookings to Big Calendar events - filter by practitioner and location
  const events = useMemo(() => {
    let filteredBookings = bookings;

    // Selecting a single practitioner narrows the calendar to just their bookings,
    // in every view mode (including the staff resource view).
    if (selectedPractitioner) {
      filteredBookings = filteredBookings.filter((booking) => String(booking.staff_member) === String(selectedPractitioner));
    }

    // Filter by selected location if set
    if (selectedLocation) {
      filteredBookings = filteredBookings.filter(booking => booking.clinic_location === selectedLocation);
    }

    if (activeRoomFilterIds.length > 0) {
      const roomIdSet = new Set(activeRoomFilterIds.map((roomId) => String(roomId)));
      filteredBookings = filteredBookings.filter((booking) => roomIdSet.has(String(booking.room?.id || booking.room || '')));
    }

    if (!selectedPractitioner && activeStaffFilterIds.length > 0) {
      const staffIdSet = new Set(activeStaffFilterIds.map((staffId) => String(staffId)));
      filteredBookings = filteredBookings.filter((booking) => staffIdSet.has(String(booking.staff_member || '')));
    }

    filteredBookings = filteredBookings.filter((booking) => !String(booking?.notes || '').includes(CLASS_ATTENDANCE_NOTE_MARKER));

    const eventRangeUnit = calendarView === Views.MONTH
      ? 'month'
      : (calendarView === Views.WEEK || calendarView === Views.WORK_WEEK)
        ? 'week'
        : 'day';
    const visibleStart = moment(calendarDate).startOf(eventRangeUnit);
    const visibleEnd = moment(calendarDate).endOf(eventRangeUnit);
    const visibleDates = [];
    const cursor = visibleStart.clone();

    while (cursor.isSameOrBefore(visibleEnd, 'day')) {
      visibleDates.push(cursor.clone());
      cursor.add(1, 'day');
    }

    const getClassServiceWeekdays = (service) => Array.from(new Set((Array.isArray(service?.class_weekdays) && service.class_weekdays.length > 0
      ? service.class_weekdays
      : [service?.class_weekday])
      .map((weekday) => Number(weekday))
      .filter((weekday) => Number.isFinite(weekday) && weekday >= 0 && weekday <= 6)))
      .sort((left, right) => left - right);

    const classBookingEntries = visibleDates.flatMap((dateMoment) => {
      const weekday = (dateMoment.day() + 6) % 7;
      const dateKey = dateMoment.format('YYYY-MM-DD');

      return clinicServices
        .filter((service) => service.is_class && getClassServiceWeekdays(service).includes(weekday))
        .map((service) => {
          const room = (selectedLocationData?.rooms || []).find((candidate) => String(candidate.id) === String(service.class_default_room)) || null;
          const roomId = String(service.class_default_room || room?.id || '');
          const roomLocationId = room?.clinic_location ? String(room.clinic_location) : String(selectedLocation || '');
          const staffId = Array.isArray(service.staff_members) && service.staff_members.length > 0
            ? String(service.staff_members[0])
            : '';
          const startMoment = moment(`${dateKey} ${service.class_start_time || '00:00:00'}`);
          const durationMinutes = Number(service.duration_minutes || 0) || 0;
          const endMoment = startMoment.clone().add(durationMinutes, 'minutes');

          return {
            id: `class-booking-${service.id}-${dateKey}`,
            clinic_location: roomLocationId,
            room: room ? { id: roomId, name: room.name || '' } : roomId,
            staff_member: staffId,
            title: service.name,
            start_datetime: startMoment.toISOString(),
            end_datetime: endMoment.toISOString(),
            appointment_type: 'class',
            service_name: service.name,
            patient_name: '',
            patient: null,
            status: 'scheduled',
            color: room?.color || service.color || DEFAULT_BOOKING_COLOR,
            service_color: room?.color || service.color || DEFAULT_BOOKING_COLOR,
            service: service,
            isClassService: true,
            booking_kind: 'class',
            classDateKey: dateKey,
            classPatientIds: Array.isArray(service.class_patients) ? service.class_patients : [],
            classRoomName: room?.name || '',
            classPatientCount: Array.isArray(service.class_patients) ? service.class_patients.length : 0,
            classPatientSummary: Array.isArray(service.class_patients)
              ? service.class_patients.map((patientId) => getPatientDisplaySummary(patientId)).filter(Boolean).join(' · ')
              : '',
            assignedStaff: Array.isArray(service.staff_members) && service.staff_members.length > 0
              ? service.staff_members.map((staffMemberId) => getPractitionerDisplayName(staffMemberId)).filter(Boolean).join(' · ')
              : 'All staff',
          };
        });
    });

    filteredBookings = [...filteredBookings, ...classBookingEntries];
    
    const mappedEvents = filteredBookings.map((booking) => {
      const start = booking.start_datetime ? new Date(booking.start_datetime) : new Date();
      const end = booking.end_datetime ? new Date(booking.end_datetime) : new Date(start.getTime() + 60 * 60 * 1000);
      const appointmentLabel = booking.service_name || booking.service?.name || formatAppointmentType(booking.appointment_type);
      const patientLabel = booking.patient_name || booking.patient?.name || '';
      const title = patientLabel ? `${patientLabel} - ${appointmentLabel}` : (booking.title || appointmentLabel);
      
      // resourceId depends on the active view mode
      let resourceId;
      if (resourceViewMode === 'staff') {
        resourceId = booking.staff_member || 'unassigned';
      } else {
        resourceId = booking.room?.id || booking.room || 'unassigned';
      }

      return {
        id: booking.id,
        title,
        start,
        end,
        resource: booking,
        resourceId,
        status: booking.status,
        color: booking.color || booking.service_color || null,
        booking_kind: booking.booking_kind || (booking.service?.is_class ? 'class' : 'appointment'),
      };
    });

    return mappedEvents;
  }, [activeRoomFilterIds, activeStaffFilterIds, bookings, calendarDate, calendarView, clinicServices, getPatientDisplaySummary, getPractitionerDisplayName, resourceViewMode, selectedLocation, selectedLocationData, selectedPractitioner]);

  // Build resources for the Day view (rooms or staff based on toggle)
  const calendarResources = useMemo(() => {
    if (resourceViewMode === 'staff') {
      // Show practitioners as columns
      if (!availablePractitioners || availablePractitioners.length === 0) return null;
      // A single selected practitioner narrows the calendar to just that staff member
      // while calendar presets keep the same staff combinations visible.
      const list = selectedPractitioner
        ? availablePractitioners.filter((staff) => String(staff.id) === String(selectedPractitioner))
        : activeStaffFilterIds.length > 0
          ? availablePractitioners.filter((staff) => activeStaffFilterIds.includes(String(staff.id)))
          : availablePractitioners;
      return list.map(p => ({ id: p.id, title: p.name }));
    }
    // rooms mode
    if (!selectedLocation) return null;
    const loc = clinicLocations.find(l => l.id === selectedLocation);
    if (!loc || !loc.rooms || loc.rooms.length === 0) return null;
    const roomList = activeRoomFilterIds.length > 0
      ? loc.rooms.filter((room) => activeRoomFilterIds.includes(String(room.id)))
      : selectedRoom
        ? loc.rooms.filter((room) => String(room.id) === String(selectedRoom))
        : loc.rooms;
    const resources = roomList.map(r => ({ id: r.id, title: r.name }));
    resources.push({ id: 'unassigned', title: 'Unassigned' });
    return resources;
  }, [activeRoomFilterIds, activeStaffFilterIds, availablePractitioners, clinicLocations, resourceViewMode, selectedLocation, selectedPractitioner, selectedRoom]);

  const hasConfiguredOpeningHours = useMemo(
    () => openingHoursHasActiveDay(clinicOpeningHours) || Object.values(staffOpeningHours || {}).some((openingHours) => openingHoursHasActiveDay(openingHours)),
    [clinicOpeningHours, staffOpeningHours],
  );

  const resolveOpeningHoursForResource = useCallback((resourceId = null) => {
    const normalizedResourceId = String(resourceId || '').trim();
    const baseHours = clinicOpeningHours;

    if (normalizedResourceId && staffOpeningHours[normalizedResourceId]) {
      return staffOpeningHours[normalizedResourceId];
    }

    if (selectedPractitioner && staffOpeningHours[selectedPractitioner]) {
      return staffOpeningHours[selectedPractitioner];
    }

    return baseHours;
  }, [clinicOpeningHours, selectedPractitioner, staffOpeningHours]);

  const visibleRangeUnit = calendarView === Views.MONTH
    ? 'month'
    : (calendarView === Views.WEEK || calendarView === Views.WORK_WEEK)
      ? 'week'
      : 'day';

  const calendarBackgroundEvents = useMemo(() => {
    if (!hasConfiguredOpeningHours) {
      return [];
    }

    const visibleStart = moment(calendarDate).startOf(visibleRangeUnit);
    const visibleEnd = moment(calendarDate).endOf(visibleRangeUnit);
    const dates = [];
    const cursor = visibleStart.clone();

    while (cursor.isSameOrBefore(visibleEnd, 'day')) {
      dates.push(cursor.clone());
      cursor.add(1, 'day');
    }

    const resourcesForOverlay = calendarView === Views.DAY && Array.isArray(calendarResources) && calendarResources.length > 0
      ? calendarResources
      : [{ id: null }];

    return dates.flatMap((dateMoment) => resourcesForOverlay.flatMap((resource) => (
      buildClosedHoursBackgroundEvents(dateMoment.toDate(), resolveOpeningHoursForResource(resource.id), resource.id)
    )));
  }, [calendarDate, calendarResources, hasConfiguredOpeningHours, resolveOpeningHoursForResource, visibleRangeUnit]);

  const availabilityBackgroundEvents = useMemo(() => {
    if (!selectedLocation || !Array.isArray(availabilitySlots) || availabilitySlots.length === 0) {
      return [];
    }

    const roomColorById = new Map(
      (selectedLocationData?.rooms || []).map((room) => [String(room.id), room.color || '#3174ad'])
    );
    const roomNameById = new Map(
      (selectedLocationData?.rooms || []).map((room) => [String(room.id), room.name || 'Room'])
    );
    const staffNameById = new Map(
      ((selectedLocationData?.staff_members_detail || practitioners) || []).map((staff) => [
        String(staff.id),
        staff.full_name || staff.name || staff.label || staff.display_name || staff.username || 'Staff',
      ])
    );
    const staffEmailById = new Map(
      ((selectedLocationData?.staff_members_detail || practitioners) || []).map((staff) => [
        String(staff.id),
        staff.email || staff.email_address || staff.user_email || '',
      ])
    );

    const visibleStart = moment(calendarDate).startOf(visibleRangeUnit);
    const visibleEnd = moment(calendarDate).endOf(visibleRangeUnit);
    const dates = [];
    const cursor = visibleStart.clone();

    while (cursor.isSameOrBefore(visibleEnd, 'day')) {
      dates.push(cursor.clone());
      cursor.add(1, 'day');
    }

    const selectedLocationId = String(selectedLocation || '');
    const selectedRoomIdSet = new Set(activeRoomFilterIds.map((roomId) => String(roomId)));
    // A single selected practitioner takes precedence so the overlay only shows their allocations.
    const selectedPractitionerIdSet = selectedPractitioner
      ? new Set([String(selectedPractitioner)])
      : new Set(activeStaffFilterIds.map((staffId) => String(staffId)));

    const filteredSlots = availabilitySlots.filter((slot) => {
      if (!slot) {
        return false;
      }

      if (selectedLocationId && String(slot.clinic_location || '') !== selectedLocationId) {
        return false;
      }

      if (selectedRoomIdSet.size > 0 && !selectedRoomIdSet.has(String(slot.room || ''))) {
        return false;
      }

      if (selectedPractitionerIdSet.size > 0 && !selectedPractitionerIdSet.has(String(slot.staff_member || ''))) {
        return false;
      }

      return true;
    });

    const resourcesForOverlay = calendarView === Views.DAY && Array.isArray(calendarResources) && calendarResources.length > 0
      ? calendarResources
      : [{ id: null }];

    const sickDayOverrideSlotIdSet = new Set(
      (Array.isArray(staffVacations) ? staffVacations : [])
        .filter((absence) => String(absence.absence_type || '') === 'sick_day')
        .flatMap((absence) => (Array.isArray(absence.override_slot_ids) ? absence.override_slot_ids : []))
        .map((slotId) => String(slotId))
    );

    return dates.flatMap((dateMoment) => {
      const dayKey = dateMoment.format('YYYY-MM-DD');
      // Allocation slots store day_of_week using Python's weekday() convention (Monday=0),
      // matching clinicSettings. Convert moment's day() (Sunday=0) to the same basis.
      const weekday = (dateMoment.day() + 6) % 7;
      const daySlots = filteredSlots.filter((slot) => {
        if (slot.specific_date) {
          return moment(slot.specific_date).format('YYYY-MM-DD') === dayKey;
        }

        const slotWeekday = Number(slot.day_of_week);
        return Number.isFinite(slotWeekday) && slotWeekday === weekday;
      });

      return resourcesForOverlay.flatMap((resource) => {
        const resourceId = resource?.id ?? null;
        const resourceSlots = calendarView === Views.DAY && resourceId !== null
          ? daySlots.filter((slot) => (
            resourceViewMode === 'staff'
              ? String(slot.staff_member || '') === String(resourceId)
              : String(slot.room || '') === String(resourceId)
          ))
          : daySlots;

        const enrichedSlots = resourceSlots.map((slot) => ({
          ...slot,
          color: slot.color || '',
          roomColor: roomColorById.get(String(slot.room || '')) || '#3174ad',
          roomName: roomNameById.get(String(slot.room || '')) || '',
          staffName: slot.staff_member_email || slot.staff_member_name || slot.staffName || staffNameById.get(String(slot.staff_member || '')) || '',
          staffEmail: slot.staff_member_email || staffEmailById.get(String(slot.staff_member || '')) || '',
          isSickDayOverlaySlot: sickDayOverrideSlotIdSet.has(String(slot.id)),
        }));

        return buildAvailabilityBackgroundEvents(dateMoment.toDate(), enrichedSlots, resourceId, resource?.title || '');
      });
    });
  }, [
    availabilitySlots,
    calendarDate,
    calendarResources,
    calendarView,
    resourceViewMode,
    selectedLocation,
    selectedLocationData,
    activeRoomFilterIds,
    activeStaffFilterIds,
    staffVacations,
    selectedPractitioner,
  ]);

  const combinedBackgroundEvents = useMemo(() => ([
    ...calendarBackgroundEvents,
    ...availabilityBackgroundEvents,
  ]), [availabilityBackgroundEvents, calendarBackgroundEvents]);

  // Style calendar events based on status
  // Use a ref so the memoized callback always reads the latest selectedBooking
  const selectedBookingRef = useRef(selectedBooking);
  selectedBookingRef.current = selectedBooking;

  const selectedBookingRelatedInvoices = useMemo(() => {
    if (!selectedBooking?.id) return null;

    return invoices.filter((invoice) => (
      Array.isArray(invoice.line_items) && invoice.line_items.some((lineItem) => {
        const lineAppointmentId = lineItem?.appointment && typeof lineItem.appointment === 'object'
          ? lineItem.appointment.id
          : lineItem?.appointment;
        return String(lineAppointmentId) === String(selectedBooking.id);
      })
    ));
  }, [invoices, selectedBooking]);

  const selectedBookingInvoice = useMemo(() => {
    if (!Array.isArray(selectedBookingRelatedInvoices) || selectedBookingRelatedInvoices.length === 0) return null;

    const reversedStatuses = new Set(['accredited', 'refunded', 'cancelled']);
    return selectedBookingRelatedInvoices.find((invoice) => invoice.status === 'paid')
      || selectedBookingRelatedInvoices.find((invoice) => !reversedStatuses.has(invoice.status))
      || selectedBookingRelatedInvoices[0];
  }, [selectedBookingRelatedInvoices]);

  const selectedBookingHasPaidInvoice = Array.isArray(selectedBookingRelatedInvoices)
    && selectedBookingRelatedInvoices.some((invoice) => invoice.status === 'paid');
  const selectedBookingIsReversed = Array.isArray(selectedBookingRelatedInvoices)
    && selectedBookingRelatedInvoices.some((invoice) => ['accredited', 'refunded', 'cancelled'].includes(invoice.status));
  const selectedBookingIsPatientAppointment = Boolean(selectedBooking?.patient || selectedBooking?.patient_name || selectedBooking?.patient_email);
  const selectedBookingCancellationReason = String(selectedBooking?.cancellation_reason || '').trim().toLowerCase();
  const selectedBookingCancelledById = selectedBooking?.cancelled_by?.id || selectedBooking?.cancelled_by_id || selectedBooking?.cancelled_by || '';
  const selectedBookingPatientUserId = selectedBooking?.patient?.user_id || selectedBooking?.patient?.id || selectedBooking?.patient_id || '';
  const isAutoCancelledDueToSickDay = selectedBookingCancellationReason.includes('sick day');
  const isPatientCancelledBooking = !isAutoCancelledDueToSickDay
    && Boolean(selectedBookingCancelledById && selectedBookingPatientUserId && String(selectedBookingCancelledById) === String(selectedBookingPatientUserId));
  const selectedBookingCancelledBannerText = isAutoCancelledDueToSickDay
    ? '⚠️ This appointment was auto-cancelled due to sick day'
    : isPatientCancelledBooking
      ? '⚠️ This appointment has been cancelled by the patient'
      : '⚠️ This appointment has been cancelled';
  const isSelectedBookingPaid = selectedBookingHasPaidInvoice || selectedBooking?.payment_status === 'paid';
  const isSelectedBookingUnpaid = !isSelectedBookingPaid && (selectedBookingIsReversed || selectedBooking?.payment_status === 'unpaid');
  const selectedBookingPaymentMethodLabel = getPaymentMethodDisplayLabel(
    selectedBookingInvoice?.payment_method || selectedBooking?.payment_method || form.payment_method
  );
  const selectedBookingPaymentSelectValue = normalizePaymentMethodSelectValue(
    selectedBookingInvoice?.payment_method || selectedBooking?.payment_method || form.payment_method
  );
  const selectedInsuranceBillingOption = useMemo(() => {
    if (form.bookingMode !== 'patient' || selectedPayer === 'patient') {
      return null;
    }

    return (Array.isArray(payerReferralOptions) ? payerReferralOptions : []).find((option) => option.id === selectedPayer) || null;
  }, [form.bookingMode, payerReferralOptions, selectedPayer]);

  const selectedOtherPayerBillingOption = useMemo(() => {
    if (form.bookingMode !== 'patient' || !String(selectedPayer || '').startsWith('other:')) {
      return null;
    }

    return (Array.isArray(clinicOtherPayers) ? clinicOtherPayers : []).find((option) => `other:${option.id}` === selectedPayer) || null;
  }, [clinicOtherPayers, form.bookingMode, selectedPayer]);

  const selectedInsuranceCoveragePerAppointment = useMemo(
    () => getInsuranceCoveragePerAppointment(selectedInsuranceBillingOption),
    [selectedInsuranceBillingOption]
  );

  const billingReceiverAddress = useMemo(() => {
    if (form.bookingMode === 'patient' && selectedPayer !== 'patient') {
      if (String(selectedPayer || '').startsWith('other:')) {
        return (
          selectedOtherPayerBillingOption?.billingEmail
          || selectedBookingInvoice?.other_payer_billing_email
          || selectedBooking?.other_payer_billing_email
          || ''
        );
      }

      return (
        selectedInsuranceBillingOption?.billingEmail
        || selectedBookingInvoice?.insurer_billing_email
        || selectedBooking?.insurer_billing_email
        || ''
      );
    }

    return form.patientEmail || selectedBooking?.patient_email || selectedBooking?.patient?.email || '';
  }, [form.bookingMode, form.patientEmail, selectedBooking, selectedBookingInvoice, selectedInsuranceBillingOption, selectedOtherPayerBillingOption, selectedPayer]);

  const selectedBookingAmountValue = useMemo(
    () => parseOptionalNumber(form.booked_price || selectedBooking?.booked_price || 0),
    [form.booked_price, selectedBooking?.booked_price]
  );

  const isInsuranceSplitBilling = Boolean(
    hasInsuranceSplitBilling(selectedBookingAmountValue, selectedInsuranceCoveragePerAppointment)
  );

  const selectedSplitBillingPatientAmount = useMemo(() => {
    return calculateSplitBillingPatientAmount({
      bookingMode: form.bookingMode,
      selectedPayer,
      bookedPrice: selectedBookingAmountValue,
      coveragePerAppointment: selectedInsuranceCoveragePerAppointment,
      invoicePatientAmount: selectedBookingInvoice?.patient_portion_amount,
    });
  }, [form.bookingMode, selectedBookingAmountValue, selectedBookingInvoice?.patient_portion_amount, selectedInsuranceCoveragePerAppointment, selectedPayer]);

  const selectedSplitBillingCurrency = selectedBookingInvoice?.currency || selectedBooking?.booked_currency || form.booked_currency || 'EUR';

  const bookingPaymentMethodOptions = useMemo(
    () => getBookingPaymentMethodOptions({
      bookingMode: form.bookingMode,
      selectedPayer,
      payerReferralOptions,
      bookedPrice: form.booked_price,
    }),
    [form.bookingMode, form.booked_price, payerReferralOptions, selectedPayer]
  );

  const selectedBookingStartMoment = selectedBooking?.start_datetime ? moment(selectedBooking?.start_datetime) : null;
  const repeatSourceWeekday = selectedBookingStartMoment && selectedBookingStartMoment.isValid()
    ? selectedBookingStartMoment.isoWeekday() % 7
    : 0;
  const bookingDialogMenuProps = {
    getContentAnchorEl: null,
    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
    transformOrigin: { vertical: 'top', horizontal: 'left' },
    PaperProps: { style: { borderRadius: 14, marginTop: 8 } },
    classes: { list: classes.dialogMenuList },
  };

  useEffect(() => {
    if (form.bookingMode !== 'patient' || isSelectedBookingPaid) {
      return;
    }

    const currentValue = String(form.payment_method || '');
    const optionValues = bookingPaymentMethodOptions.map((option) => option.value);
    const isValidSelection = optionValues.includes(currentValue);

    if (selectedPayer === 'patient') {
      if (!currentValue || isValidSelection) {
        return;
      }

      setForm((current) => ({
        ...current,
        payment_method: '',
      }));
      return;
    }

    if (isValidSelection) {
      return;
    }

    setForm((current) => ({
      ...current,
      payment_method: bookingPaymentMethodOptions[0]?.value || '',
    }));
  }, [bookingPaymentMethodOptions, form.bookingMode, form.payment_method, isSelectedBookingPaid, selectedPayer]);

  useEffect(() => {
    if (form.bookingMode !== 'patient' || isSelectedBookingPaid || !isInsuranceSplitBilling) {
      if (form.split_payment_method) {
        setForm((current) => ({
          ...current,
          split_payment_method: '',
        }));
      }
      return;
    }

    const currentValue = String(form.split_payment_method || '');
    const optionValues = PATIENT_PAYMENT_METHOD_OPTIONS.map((option) => option.value);
    if (currentValue && optionValues.includes(currentValue)) {
      return;
    }

    setForm((current) => ({
      ...current,
      split_payment_method: PATIENT_PAYMENT_METHOD_OPTIONS[0]?.value || '',
    }));
  }, [form.bookingMode, form.split_payment_method, isInsuranceSplitBilling, isSelectedBookingPaid]);

  const roomLegendByResourceId = useMemo(() => {
    if (resourceViewMode !== 'staff' || !Array.isArray(availabilitySlots) || availabilitySlots.length === 0) {
      return {};
    }

    const targetDay = moment(calendarDate).format('YYYY-MM-DD');
    const targetWeekday = (moment(calendarDate).day() + 6) % 7;
    const roomNameById = new Map(
      (selectedLocationData?.rooms || []).map((room) => [String(room.id), room.name || 'Room'])
    );
    const roomColorById = new Map(
      (selectedLocationData?.rooms || []).map((room) => [String(room.id), room.color || '#3174ad'])
    );

    const grouped = {};

    (Array.isArray(availabilitySlots) ? availabilitySlots : []).forEach((slot) => {
      if (!slot) return;
      const slotDay = String(slot.specific_date || slot.day || slot.date || slot.start_datetime || '').slice(0, 10);
      if (slotDay && slotDay !== targetDay) return;

      const slotWeekday = Number(slot.day_of_week);
      if (Number.isFinite(slotWeekday) && slotWeekday !== targetWeekday) return;

      const roomId = String(slot.room || slot.room_id || slot.roomId || '').trim();
      if (!roomId) return;

      const startMinutes = Number.isFinite(parseTimeToMinutes(slot.start_time))
        ? parseTimeToMinutes(slot.start_time)
        : Number.isFinite(parseTimeToMinutes(slot.start))
          ? parseTimeToMinutes(slot.start)
          : Number.isFinite(moment(slot.start_datetime || slot.start).isValid() ? moment(slot.start_datetime || slot.start).hour() * 60 + moment(slot.start_datetime || slot.start).minute() : NaN)
            ? moment(slot.start_datetime || slot.start).hour() * 60 + moment(slot.start_datetime || slot.start).minute()
            : Number.POSITIVE_INFINITY;

      const roomName = String(
        roomNameById.get(roomId)
        || slot.room_name
        || slot.roomLabel
        || slot.roomName
        || 'Room'
      ).trim();

      const roomColor = normalizeAllocationColor(roomColorById.get(roomId))
        || normalizeAllocationColor(slot.roomColor)
        || normalizeAllocationColor(slot.color)
        || getAllocationDefaultColor(roomId || roomName || slot.staff_member || slot.staff_member_name || slot.staff_member_email);

      const staffId = String(slot.staff_member || slot.staffId || slot.staff_member_id || '').trim();
      const resourceKey = staffId;
      if (!resourceKey) return;

      if (!grouped[resourceKey]) {
        grouped[resourceKey] = [];
      }

      const alreadyListed = grouped[resourceKey].some((entry) => String(entry.id) === roomId);
      if (!alreadyListed) {
        grouped[resourceKey].push({ id: roomId, label: roomName, color: roomColor, startMinutes });
      } else {
        grouped[resourceKey] = grouped[resourceKey].map((entry) => (
          String(entry.id) === roomId && Number.isFinite(startMinutes) && startMinutes < (entry.startMinutes ?? Number.POSITIVE_INFINITY)
            ? { ...entry, startMinutes }
            : entry
        ));
      }
    });

    return Object.keys(grouped).reduce((acc, resourceKey) => {
      acc[resourceKey] = grouped[resourceKey]
        .slice()
        .sort((left, right) => {
          const leftStart = Number.isFinite(left.startMinutes) ? left.startMinutes : Number.POSITIVE_INFINITY;
          const rightStart = Number.isFinite(right.startMinutes) ? right.startMinutes : Number.POSITIVE_INFINITY;
          if (leftStart !== rightStart) {
            return leftStart - rightStart;
          }
          return String(left.label || '').localeCompare(String(right.label || ''));
        });
      return acc;
    }, {});
  }, [availabilitySlots, calendarDate, resourceViewMode, selectedLocationData]);

  const focusedStaffFilterId = useMemo(() => {
    if (selectedPractitioner) {
      return String(selectedPractitioner);
    }

    return activeStaffFilterIds.length === 1 ? String(activeStaffFilterIds[0]) : '';
  }, [activeStaffFilterIds, selectedPractitioner]);

  const roomLegendByDate = useMemo(() => {
    if (!focusedStaffFilterId || !Array.isArray(availabilitySlots) || availabilitySlots.length === 0) {
      return {};
    }

    const visibleStart = moment(calendarDate).startOf(visibleRangeUnit);
    const visibleEnd = moment(calendarDate).endOf(visibleRangeUnit);
    const visibleDates = [];
    const cursor = visibleStart.clone();

    while (cursor.isSameOrBefore(visibleEnd, 'day')) {
      visibleDates.push(cursor.clone());
      cursor.add(1, 'day');
    }

    const roomNameById = new Map(
      (selectedLocationData?.rooms || []).map((room) => [String(room.id), room.name || 'Room'])
    );
    const roomColorById = new Map(
      (selectedLocationData?.rooms || []).map((room) => [String(room.id), room.color || '#3174ad'])
    );

    return visibleDates.reduce((acc, dateMoment) => {
      const dateKey = dateMoment.format('YYYY-MM-DD');
      const weekday = (dateMoment.day() + 6) % 7;
      const grouped = [];

      (Array.isArray(availabilitySlots) ? availabilitySlots : []).forEach((slot) => {
        if (!slot || String(slot.staff_member || slot.staffId || slot.staff_member_id || '') !== focusedStaffFilterId) return;

        const roomId = String(slot.room || slot.room_id || slot.roomId || '').trim();
        if (!roomId) return;

        const specificDateKey = slot.specific_date ? moment(slot.specific_date).format('YYYY-MM-DD') : '';
        const slotWeekday = Number(slot.day_of_week);
        const slotMoment = slot.start_datetime ? moment(slot.start_datetime) : null;

        const matchesSpecificDate = specificDateKey && specificDateKey === dateKey;
        const matchesWeekday = Number.isFinite(slotWeekday) && slotWeekday === weekday;
        const matchesDateTime = !specificDateKey && !Number.isFinite(slotWeekday) && slotMoment && slotMoment.isValid()
          ? slotMoment.format('YYYY-MM-DD') === dateKey
          : false;

        if (!matchesSpecificDate && !matchesWeekday && !matchesDateTime) {
          return;
        }

        const roomName = String(
          roomNameById.get(roomId)
          || slot.room_name
          || slot.roomLabel
          || slot.roomName
          || 'Room'
        ).trim();

        const roomColor = normalizeAllocationColor(roomColorById.get(roomId))
          || normalizeAllocationColor(slot.roomColor)
          || normalizeAllocationColor(slot.color)
          || getAllocationDefaultColor(roomId || roomName || slot.staff_member || slot.staff_member_name || slot.staff_member_email);

        const startMinutes = Number.isFinite(parseTimeToMinutes(slot.start_time))
          ? parseTimeToMinutes(slot.start_time)
          : slotMoment && slotMoment.isValid()
            ? slotMoment.hours() * 60 + slotMoment.minutes()
            : Number.POSITIVE_INFINITY;

        const existing = grouped.find((entry) => String(entry.id) === roomId);
        if (existing) {
          existing.startMinutes = Math.min(existing.startMinutes, startMinutes);
          return;
        }

        grouped.push({ id: roomId, label: roomName, color: roomColor, startMinutes });
      });

      acc[dateKey] = grouped
        .slice()
        .sort((left, right) => {
          const leftStart = Number.isFinite(left.startMinutes) ? left.startMinutes : Number.POSITIVE_INFINITY;
          const rightStart = Number.isFinite(right.startMinutes) ? right.startMinutes : Number.POSITIVE_INFINITY;
          if (leftStart !== rightStart) {
            return leftStart - rightStart;
          }
          return String(left.label || '').localeCompare(String(right.label || ''));
        });
      return acc;
    }, {});
  }, [availabilitySlots, calendarDate, focusedStaffFilterId, selectedLocationData, visibleRangeUnit]);

  const RoomLegendBox = useCallback(({ legendItems, legendKey }) => {
    if (!Array.isArray(legendItems) || legendItems.length === 0) {
      return null;
    }

    return (
      <Box className={classes.headerLegendRow}>
        <Box className={classes.calendarHeaderRoomsBox}>
          {legendItems.map((room) => (
            <Box key={`${legendKey}-${room.id}`} className={classes.resourceRoomLegendItem}>
              <span className={classes.resourceRoomLegendDot} style={{ backgroundColor: room.color }} />
              <span className={classes.resourceRoomLegendText}>{room.label}</span>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }, [classes.calendarHeaderRoomsBox, classes.headerLegendRow, classes.resourceRoomLegendDot, classes.resourceRoomLegendItem]);

  const ResourceHeader = useCallback(({ label, resource }) => {
    const resourceKey = String(resource?.id || label || '').trim();
    const legendItems = roomLegendByResourceId[resourceKey] || [];

    const handleClick = () => {
      if (!resourceKey) return;
      setSelectedPractitioner(resourceKey);
      setToolbarStaffFilterIds([]);
      setResourceViewMode('staff');
    };

    return (
      <Box className={classes.staffHeaderCell} onClick={handleClick} role="button" tabIndex={0}>
        <Box className={classes.calendarHeaderTitleRow}>
          <Typography style={{ fontWeight: 700, color: '#002D55', lineHeight: 1.15, fontSize: 'clamp(0.68rem, 0.8vw, 0.9rem)' }}>
            {label}
          </Typography>
        </Box>
        <RoomLegendBox legendItems={legendItems} legendKey={resourceKey} />
      </Box>
    );
  }, [RoomLegendBox, classes.calendarHeaderTitleRow, classes.staffHeaderCell, roomLegendByResourceId, setResourceViewMode, setSelectedPractitioner, setToolbarStaffFilterIds]);

  const CalendarHeader = useCallback(({ date, label }) => {
    const dateKey = moment(date).format('YYYY-MM-DD');
    const legendItems = roomLegendByDate[dateKey] || [];

    return (
      <Box className={classes.calendarHeaderCell}>
        <Box className={classes.calendarHeaderTitleRow}>
          <Typography className={classes.calendarHeaderLabel} style={{ fontWeight: 700, color: '#002D55', lineHeight: 1.15, fontSize: 'clamp(0.68rem, 0.8vw, 0.9rem)' }}>
            {label}
          </Typography>
        </Box>
        <RoomLegendBox legendItems={legendItems} legendKey={dateKey} />
      </Box>
    );
  }, [RoomLegendBox, classes.calendarHeaderCell, classes.calendarHeaderLabel, classes.calendarHeaderTitleRow, roomLegendByDate]);

  const eventStyleGetter = useCallback((event) => {
    if (event?.isSickDayAbsence) {
      return {
        className: 'sick-day-calendar-event',
        style: {
          backgroundColor: 'rgba(183, 28, 28, 0.28)',
          backgroundImage: 'linear-gradient(135deg, rgba(255,255,255,0.14), rgba(0,0,0,0.05))',
          borderRadius: '6px',
          border: '1px solid rgba(127, 29, 29, 0.55)',
          boxShadow: '0 2px 6px rgba(127, 29, 29, 0.18)',
          opacity: 1,
          pointerEvents: 'none',
          color: '#fff',
          fontWeight: 700,
          zIndex: 6,
        },
      };
    }

    if (event?.isClosedHours) {
      return {
        className: 'closed-hours-background-event',
        style: {
          backgroundColor: '#f2f2f2',
          borderRadius: '8px',
          opacity: 1,
          pointerEvents: 'none',
          backgroundClip: 'padding-box',
          color: '#002D55',
          boxSizing: 'border-box',
          padding: '6px 8px 7px',
        },
      };
    }

    if (event?.isAllocationWindow) {
      const allocationFillColor = event.allocationFillColor || colorWithAlpha(event.allocationRoomColor || DEFAULT_BOOKING_COLOR, 0.11);
      const allocationRoomColor = event.allocationRoomColor || DEFAULT_BOOKING_COLOR;
      const allocationPatternColor = event.allocationPatternColor || colorWithAlpha(allocationRoomColor, 0.1);
      const allocationFrameColor = event.allocationFrameColor || colorWithAlpha(allocationRoomColor, 0.7);
      return {
        className: [
          'allocation-background-event',
          event.isBookableAllocation ? 'allocation-background-event-bookable' : 'allocation-background-event-blocked',
          event.isAvailableAllocation === false ? 'allocation-background-event-unavailable' : '',
        ].filter(Boolean).join(' '),
        style: {
          backgroundColor: allocationFillColor,
          borderLeft: `5px solid ${allocationRoomColor}`,
          borderTop: '1px solid rgba(0, 45, 85, 0.05)',
          borderRight: '1px solid rgba(0, 45, 85, 0.05)',
          borderBottom: '1px solid rgba(0, 45, 85, 0.05)',
          borderRadius: '8px',
          opacity: 1,
          pointerEvents: 'none',
          backgroundClip: 'padding-box',
          boxSizing: 'border-box',
          padding: '6px 8px 7px',
          color: '#002D55',
          whiteSpace: 'pre-line',
          zIndex: 4,
          '--allocation-fill-color': allocationFillColor,
          '--allocation-room-color': allocationRoomColor,
          '--allocation-pattern-color': allocationPatternColor,
          '--allocation-frame-color': allocationFrameColor,
        },
      };
    }

    if (event?.isClassService) {
      const classColor = event.color || DEFAULT_BOOKING_COLOR;
      return {
        className: 'class-service-event',
        style: {
          backgroundColor: classColor,
          backgroundImage: `linear-gradient(135deg, ${colorWithAlpha(classColor, 0.98)}, ${colorWithAlpha(classColor, 0.88)})`,
          border: `1px solid ${colorWithAlpha(classColor, 0.72)}`,
          borderRadius: '8px',
          opacity: 1,
          color: '#fff',
          fontWeight: 700,
          boxShadow: `0 8px 20px ${colorWithAlpha(classColor, 0.22)}`,
          zIndex: 20,
          position: 'relative',
          pointerEvents: 'auto',
        },
      };
    }

    let backgroundColor = event.color || DEFAULT_BOOKING_COLOR;
    let opacity = 1;
    const isSelected = selectedBookingRef.current && event.id === selectedBookingRef.current.id;

    if (event.status === 'cancelled') {
      backgroundColor = '#f44336'; // Red color
      opacity = 0.75; // 25% transparency = 75% opacity
    }

    return {
      className: 'booking-event-inset',
      style: {
        backgroundColor,
        opacity,
        borderRadius: '4px',
        border: isSelected
          ? '1px solid #FF7F27'
          : event.status === 'cancelled' ? '1px solid #b71c1c' : 'none',
        outline: isSelected ? '1px solid #FF7F27' : 'none',
        outlineOffset: isSelected ? '1px' : undefined,
        boxShadow: isSelected ? '0 0 0 2px rgba(255,127,39,0.35)' : 'none',
        color: event.status === 'cancelled' ? '#7a0000' : '#fff',
        fontWeight: event.status === 'cancelled' ? '700' : 'normal',
        textDecoration: event.status === 'cancelled' ? 'line-through' : 'none',
        textShadow: event.status === 'cancelled' ? '0 1px 0 rgba(255,255,255,0.4)' : 'none',
      },
    };
  }, []);

  const calendarBackgroundEventPropGetter = useCallback((event) => {
    if (event?.isClosedHours) {
      return {
        className: 'closed-hours-background-event',
        style: {
          backgroundColor: '#f2f2f2',
          borderRadius: '8px',
          opacity: 1,
          pointerEvents: 'none',
          backgroundClip: 'padding-box',
          color: '#002D55',
          boxSizing: 'border-box',
          padding: '6px 8px 7px',
        },
      };
    }

    if (event?.isAllocationWindow) {
      const allocationFillColor = event.allocationFillColor || colorWithAlpha(event.allocationRoomColor || DEFAULT_BOOKING_COLOR, 0.18);
      const allocationRoomColor = event.allocationRoomColor || DEFAULT_BOOKING_COLOR;
      const allocationPatternColor = event.allocationPatternColor || colorWithAlpha(allocationRoomColor, 0.16);
      const allocationFrameColor = event.allocationFrameColor || colorWithAlpha(allocationRoomColor, 0.82);
      const isSickDayOverlaySlot = event.isSickDayOverlaySlot === true;
      return {
        className: [
          'allocation-background-event',
          event.isBookableAllocation ? 'allocation-background-event-bookable' : 'allocation-background-event-blocked',
          event.isAvailableAllocation === false ? 'allocation-background-event-unavailable' : '',
          isSickDayOverlaySlot ? 'sick-day-overlay-background-event' : '',
        ].filter(Boolean).join(' '),
        style: {
          backgroundColor: isSickDayOverlaySlot ? 'rgba(183, 28, 28, 0.20)' : allocationFillColor,
          borderLeft: `5px solid ${isSickDayOverlaySlot ? '#B71C1C' : allocationRoomColor}`,
          borderTop: '1px solid rgba(0, 45, 85, 0.05)',
          borderRight: '1px solid rgba(0, 45, 85, 0.05)',
          borderBottom: '1px solid rgba(0, 45, 85, 0.05)',
          borderRadius: '8px',
          opacity: 1,
          pointerEvents: isSickDayOverlaySlot ? 'auto' : 'none',
          backgroundClip: 'padding-box',
          boxSizing: 'border-box',
          padding: '6px 8px 7px',
          color: '#002D55',
          whiteSpace: 'pre-line',
          zIndex: isSickDayOverlaySlot ? 5 : 4,
          cursor: isSickDayOverlaySlot ? 'pointer' : 'default',
          '--allocation-fill-color': allocationFillColor,
          '--allocation-room-color': allocationRoomColor,
          '--allocation-pattern-color': allocationPatternColor,
          '--allocation-frame-color': allocationFrameColor,
        },
      };
    }

    return {};
  }, []);

  const calendarEventTooltipAccessor = useCallback((event) => {
    if (!event) return '';

    if (event.isClassService && event.tooltip) {
      return event.tooltip;
    }

    if (event.isSickDayAbsence) {
      const startLabel = event.start ? moment(event.start).format('h:mm A') : '';
      const endLabel = event.end ? moment(event.end).format('h:mm A') : '';
      const titleLabel = event.title || 'Sick day';
      const cancelHint = 'Click to cancel';

      if (startLabel && endLabel) {
        return `${startLabel} - ${endLabel}\n${titleLabel}\n${cancelHint}`;
      }

      return `${titleLabel}\n${cancelHint}`;
    }

    const startLabel = event.start ? moment(event.start).format('h:mm A') : '';
    const endLabel = event.end ? moment(event.end).format('h:mm A') : '';
    const titleLabel = event.title || '';

    if (startLabel && endLabel && titleLabel) {
      return `${startLabel} - ${endLabel}\n${titleLabel}`;
    }

    if (titleLabel) {
      return titleLabel;
    }

    return startLabel && endLabel ? `${startLabel} - ${endLabel}` : '';
  }, []);

  const calendarFormats = useMemo(() => ({
    eventTimeRangeFormat: () => null,
  }), []);

  const openBookingDialog = useCallback((booking) => {
    if (!booking) {
      console.error('[CALENDAR] No booking data in event.resource');
      return;
    }

    if (isClassBooking(booking)) {
      setSelectedClassBooking(booking.resource || booking);
      setOpenClassDialog(true);
      return;
    }

    setDialogMode('edit');
    setSelectedBooking(booking);

    const startDate = new Date(booking.start_datetime);
    const endDate = new Date(booking.end_datetime);
    const dateStr = startDate.toISOString().split('T')[0];
    const timeStr = `${String(startDate.getHours()).padStart(2, '0')}:${String(startDate.getMinutes()).padStart(2, '0')}`;
    const endTimeStr = `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`;

    console.log('[CALENDAR] Setting form with booking data:', { dateStr, timeStr, endTimeStr });

    const bookingMode = booking.patient ? 'patient' : 'non_patient';
    const absenceType = booking.absence_type || '';
    const titleText = String(booking.title || '').toLowerCase();
    const nonPatientType = absenceType === 'sick_day' || titleText.includes('sick day')
      ? 'sick_day'
      : titleText.includes('vacation')
      ? 'vacation'
      : titleText.includes('other')
        ? 'other'
        : titleText.includes('meeting')
          ? 'meeting'
          : titleText.includes('break')
            ? 'break'
            : 'block';

    setForm({
      bookingMode,
      patient: booking.patient || '',
      patientName: booking.patient_name || booking.patient?.name || '',
      patientEmail: booking.patient_email || booking.patient?.email || '',
      patientPhone: booking.patient_phone || booking.patient?.phone || '',
      treatment: resolveBookingTreatmentSeed(booking.treatment),
      practitioner: booking.staff_member || '',
      date: dateStr,
      time: timeStr,
      endTime: endTimeStr,
      notes: booking.patient_notes || '',
      appointmentType: booking.service || booking.appointment_type || 'follow_up',
      serviceName: booking.service_name || booking.service?.name || formatAppointmentType(booking.appointment_type),
      nonPatientType,
      title: booking.title || '',
      assessmentDeliveryMode: '',
      booked_price: booking.booked_price ?? '',
      booked_currency: booking.booked_currency || 'EUR',
      payment_method: normalizePaymentMethodSelectValue(booking.payment_method || ''),
      split_payment_method: '',
      clinic_location: booking.clinic_location || selectedLocation || '',
      clinic_calendar: booking.clinic_calendar || selectedCalendar || getDefaultCalendarIdForLocation(booking.clinic_location || selectedLocation) || '',
      room: booking.room || selectedRoom || '',
      color: booking.color || booking.service_color || DEFAULT_BOOKING_COLOR,
    });

    if (booking.payer_type === 'insurance' && booking.payer_referral_letter) {
      const referralId = String(booking.payer_referral_letter.id || booking.payer_referral_letter);
      setSelectedPayer(`ref:${referralId}`);
    } else if (booking.payer_type === 'other' && booking.payer_other) {
      const otherPayerId = String(booking.payer_other.id || booking.payer_other);
      setSelectedPayer(`other:${otherPayerId}`);
    } else {
      setSelectedPayer('patient');
    }

    if (booking.patient) {
      fetchPayerReferralOptions(booking.patient);
    }

    console.log('[CALENDAR] Opening dialog in EDIT mode');
    setOpenDialog(true);
  }, [fetchPayerReferralOptions, getDefaultCalendarIdForLocation, isClassBooking, resolveBookingTreatmentSeed, selectedLocation, selectedLocationRooms, selectedRoom, selectedCalendar]);

  const openSickDayCancelDialog = useCallback((absence) => {
    if (!absence) {
      return;
    }

    setSelectedSickDay(absence);
    setOpenSickDayCancelConfirm(true);
  }, []);

  const handleSelectEvent = useCallback((event) => {
    // console.log('[CALENDAR] Event clicked:', event);
    // console.log('[CALENDAR] Event resource:', event.resource);

    if (isClassBooking(event)) {
      setSelectedClassBooking(event.resource || event);
      setOpenClassDialog(true);
      return;
    }

    if (event?.isSickDayAbsence) {
      clearPendingBookingDialogTimer();
      openSickDayCancelDialog(event.resource || event);
      return;
    }

    const booking = event.resource;
    if (!booking) {
      return;
    }

    clearPendingBookingDialogTimer();
    syncPatientSelectionFromBooking(booking);
  }, [clearPendingBookingDialogTimer, isClassBooking, openSickDayCancelDialog, syncPatientSelectionFromBooking]);

  const handleDoubleClickEvent = useCallback((event) => {
    if (isClassBooking(event)) {
      setSelectedClassBooking(event.resource || event);
      setOpenClassDialog(true);
      return;
    }

    const booking = event.resource;
    if (!booking) {
      return;
    }

    // console.log('[CALENDAR] Event double-clicked:', event);
    clearPendingBookingDialogTimer();
    syncPatientSelectionFromBooking(booking);
    openBookingDialog(booking);
  }, [clearPendingBookingDialogTimer, isClassBooking, openBookingDialog, syncPatientSelectionFromBooking]);

  const handleSelectSlot = useCallback((slotInfo) => {
    const clickedStartMoment = moment(slotInfo.start);
    const clickedDateKey = clickedStartMoment.format('YYYY-MM-DD');
    const clickedStartMinutes = clickedStartMoment.hours() * 60 + clickedStartMoment.minutes();
    const clickedResourceId = String(slotInfo?.resourceId || '').trim();
    const currentSickDayOverrideSlotIdSet = new Set(
      (Array.isArray(staffVacations) ? staffVacations : [])
        .filter((absence) => String(absence.absence_type || '') === 'sick_day')
        .flatMap((absence) => (Array.isArray(absence.override_slot_ids) ? absence.override_slot_ids : []))
        .map((slotId) => String(slotId))
    );

    const sickDayMatchingAllocation = (Array.isArray(availabilitySlots) ? availabilitySlots : []).find((slot) => {
      if (!slot || !currentSickDayOverrideSlotIdSet.has(String(slot.id))) {
        return false;
      }

      const slotDateKey = slot.specific_date ? moment(slot.specific_date).format('YYYY-MM-DD') : '';
      const slotWeekday = Number(slot.day_of_week);
      const startMinutes = parseTimeToMinutes(slot.start_time);
      const endMinutes = parseTimeToMinutes(slot.end_time);
      const matchesDate = slotDateKey
        ? slotDateKey === clickedDateKey
        : Number.isFinite(slotWeekday)
          ? slotWeekday === ((clickedStartMoment.day() + 6) % 7)
          : true;
      const matchesTime = Number.isFinite(startMinutes) && Number.isFinite(endMinutes)
        ? clickedStartMinutes >= startMinutes && clickedStartMinutes < endMinutes
        : true;
      const matchesResource = !clickedResourceId
        ? true
        : (resourceViewMode === 'staff'
          ? String(slot.staff_member || '') === clickedResourceId
          : String(slot.room || '') === clickedResourceId);

      return matchesDate && matchesTime && matchesResource;
    });

    if (sickDayMatchingAllocation) {
      const matchingSickDay = (Array.isArray(staffVacations) ? staffVacations : []).find((absence) => (
        String(absence.absence_type || '') === 'sick_day'
        && Array.isArray(absence.override_slot_ids)
        && absence.override_slot_ids.some((slotId) => String(slotId) === String(sickDayMatchingAllocation.id))
      ));

      if (matchingSickDay) {
        clearPendingBookingDialogTimer();
        openSickDayCancelDialog(matchingSickDay);
        return;
      }
    }

    console.log('[CALENDAR] Slot clicked - opening CREATE mode');
    clearPendingBookingDialogTimer();
    bookingDialogPractitionerManualRef.current = false;

    const resolveAllocationForSlot = () => {
      const clickedResourceId = String(slotInfo?.resourceId || '').trim();
      const selectedStaffId = String(selectedPractitioner || '').trim();
      const clickedDateKey = clickedStartMoment.format('YYYY-MM-DD');

      const matchingAllocation = (Array.isArray(availabilitySlots) ? availabilitySlots : []).find((slot) => {
        if (!slot) return false;
        const slotStaffId = String(slot.staff_member || slot.staffId || slot.staff_member_id || '').trim();
        const slotRoomId = String(slot.room || slot.room_id || slot.roomId || '').trim();
        const slotDateKey = slot.specific_date ? moment(slot.specific_date).format('YYYY-MM-DD') : '';
        const slotWeekday = Number(slot.day_of_week);
        const clickedWeekday = (clickedStartMoment.day() + 6) % 7;
        const startMinutes = parseTimeToMinutes(slot.start_time);
        const endMinutes = parseTimeToMinutes(slot.end_time);
        const matchesDate = slotDateKey
          ? slotDateKey === clickedDateKey
          : Number.isFinite(slotWeekday)
            ? slotWeekday === clickedWeekday
            : true;
        const matchesTime = Number.isFinite(startMinutes) && Number.isFinite(endMinutes)
          ? clickedStartMinutes >= startMinutes && clickedStartMinutes < endMinutes
          : true;
        const matchesStaff = resourceViewMode === 'staff'
          ? slotStaffId === clickedResourceId
          : true;
        return matchesDate && matchesTime && matchesStaff && slotRoomId;
      });

      if (matchingAllocation) {
        return {
          room: String(matchingAllocation.room || matchingAllocation.room_id || matchingAllocation.roomId || '').trim(),
          staff: String(matchingAllocation.staff_member || matchingAllocation.staffId || matchingAllocation.staff_member_id || '').trim(),
          startTime: matchingAllocation.start_time || '',
          endTime: matchingAllocation.end_time || '',
        };
      }

      return {
        room: slotInfo.resourceId && slotInfo.resourceId !== 'unassigned' ? String(slotInfo.resourceId) : String(selectedRoom || ''),
        staff: selectedStaffId || (resourceViewMode === 'staff' ? clickedResourceId : '') || '',
        startTime: '',
        endTime: '',
      };
    };
    
    // If in month view, switch to week view and focus on the clicked date
    if (calendarView === Views.MONTH) {
      handleCalendarViewChange(Views.WEEK);
      setCalendarDate(slotInfo.start);
      return;
    }
    
    // User clicked on an empty slot - open create dialog
    setDialogMode('create');
    setSelectedBooking(null);
    const startMoment = moment(slotInfo.start);
    // Use drag end when available; fall back to +30 min for a plain click
    const slotDuration = slotInfo.end && slotInfo.end > slotInfo.start
      ? moment(slotInfo.end).diff(startMoment, 'minutes')
      : 0;
    const endMoment = slotDuration > 0
      ? moment(slotInfo.end)
      : moment(slotInfo.start).add(30, 'minutes');
    const patientId = latestSelectedPatientIdRef.current || '';
    const allocation = resolveAllocationForSlot();
    setForm({
      bookingMode: 'patient',
      patient: patientId,
      patientName: getPatientDisplayName(patientId),
      treatment: resolveBookingTreatmentSeed(''),
      practitioner: allocation.staff || '',
      date: startMoment.format('YYYY-MM-DD'),
      time: allocation.startTime ? moment(startMoment.format('YYYY-MM-DD') + ' ' + allocation.startTime).format('HH:mm') : startMoment.format('HH:mm'),
      endTime: allocation.endTime ? moment(startMoment.format('YYYY-MM-DD') + ' ' + allocation.endTime).format('HH:mm') : endMoment.format('HH:mm'),
      notes: '',
      appointmentType: 'follow_up',
      serviceName: formatAppointmentType('follow_up'),
      nonPatientType: 'block',
      title: '',
      assessmentDeliveryMode: '',
      booked_price: '',
      booked_currency: 'EUR',
      payment_method: '',
      split_payment_method: '',
      clinic_location: selectedLocation || '',
      clinic_calendar: selectedCalendar || getDefaultCalendarIdForLocation(selectedLocation),
      room: allocation.room,
      color: DEFAULT_BOOKING_COLOR,
    });
    setSelectedPayer('patient');
    
    // Fetch payer options if patient is pre-selected
    if (patientId) {
      fetchPayerReferralOptions(patientId);
    } else {
      setPayerReferralOptions([]);
    }
    
    setOpenDialog(true);
  }, [availabilitySlots, calendarView, clearPendingBookingDialogTimer, fetchPayerReferralOptions, getDefaultCalendarIdForLocation, getPatientDisplayName, resolveBookingTreatmentSeed, resourceViewMode, selectedLocation, selectedPractitioner, selectedRoom, selectedCalendar]);

  const handleEventDrop = useCallback(async ({ event, start, end, resourceId }) => {
    console.log('[CALENDAR] Event dropped/resized - raw dates:', { start, end });
    
    const booking = event.resource;
    if (!booking) return;

    const resolveDroppedAllocation = () => {
      const targetResourceId = String(resourceId || '').trim();
      const targetDateKey = moment(start).format('YYYY-MM-DD');
      const targetStartMinutes = moment(start).hours() * 60 + moment(start).minutes();
      const targetEndMinutes = moment(end).hours() * 60 + moment(end).minutes();

      if (calendarView === Views.DAY && resourceViewMode === 'rooms') {
        return {
          room: targetResourceId && targetResourceId !== 'unassigned' ? targetResourceId : '',
          start: moment(start),
          end: moment(end),
        };
      }

      const matchingAllocation = (Array.isArray(availabilitySlots) ? availabilitySlots : []).find((slot) => {
        if (!slot) return false;

        const slotStaffId = String(slot.staff_member || slot.staffId || slot.staff_member_id || '').trim();
        const slotRoomId = String(slot.room || slot.room_id || slot.roomId || '').trim();
        const slotDateKey = slot.specific_date ? moment(slot.specific_date).format('YYYY-MM-DD') : '';
        const slotWeekday = Number(slot.day_of_week);
        const clickedWeekday = (moment(start).day() + 6) % 7;
        const startMinutes = parseTimeToMinutes(slot.start_time);
        const endMinutes = parseTimeToMinutes(slot.end_time);

        const matchesDate = slotDateKey
          ? slotDateKey === targetDateKey
          : Number.isFinite(slotWeekday)
            ? slotWeekday === clickedWeekday
            : true;

        const matchesTime = Number.isFinite(startMinutes) && Number.isFinite(endMinutes)
          ? targetStartMinutes >= startMinutes && targetStartMinutes < endMinutes
          : true;

        const matchesResource = calendarView === Views.DAY && resourceViewMode === 'staff'
          ? slotStaffId === targetResourceId
          : true;

        return matchesDate && matchesTime && matchesResource && slotRoomId;
      });

      if (matchingAllocation) {
        return {
          room: String(matchingAllocation.room || matchingAllocation.room_id || matchingAllocation.roomId || '').trim(),
        };
      }

      return {
        room: String(booking.room || booking.room_id || ''),
      };
    };

    const droppedAllocation = resolveDroppedAllocation();
    const effectiveStart = moment(start);
    const effectiveEnd = moment(end);

    // Convert local times to UTC format (backend expects UTC)
    // moment(date).utc() converts the local Date to UTC representation
    const startDatetime = moment(effectiveStart).utc().format('YYYY-MM-DDTHH:mm:ss') + 'Z';
    const endDatetime = moment(effectiveEnd).utc().format('YYYY-MM-DDTHH:mm:ss') + 'Z';
    const durationMinutes = Math.round((effectiveEnd.valueOf() - effectiveStart.valueOf()) / (60 * 1000));

    console.log('[DRAG] Original local times:', { start: start.toString(), end: end.toString() });
    console.log('[DRAG] Converted to UTC:', { startDatetime, endDatetime, durationMinutes });

    const appointmentData = {
      patient: booking.patient,
      staff_member: booking.staff_member,
      room: droppedAllocation.room,
      start_datetime: startDatetime,
      end_datetime: endDatetime,
      duration_minutes: durationMinutes,
      status: booking.status || 'scheduled',
      patient_notes: booking.patient_notes || '',
      appointment_type: booking.appointment_type || 'follow_up',
      care_provider: user?.id,
      clinic_calendar: booking.clinic_calendar || null,
    };

    try {
      const response = await fetch(`${backendBaseUrl}/api/calendar/appointments/${booking.id}/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
        },
        body: JSON.stringify(appointmentData),
      });

      if (response.ok) {
        console.log('[DRAG] Event updated successfully');
        fetchBookings();
      } else {
        const errorData = await response.json();
        console.error('[DRAG] Error updating event:', errorData);
        setSnackbarMessage('Error updating appointment: ' + JSON.stringify(errorData));
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        fetchBookings(); // Revert to original
      }
    } catch (e) {
      console.error('[DRAG] Exception updating event:', e);
      setSnackbarMessage('Error updating appointment: ' + e.message);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      fetchBookings(); // Revert to original
    }
  }, [availabilitySlots, calendarView, fetchBookings, resourceViewMode, token, user?.id]);

  const handleDialogClose = () => {
    clearPendingBookingDialogTimer();
    bookingDialogPractitionerManualRef.current = false;
    setOpenDialog(false);
    setAccreditDialogOpen(false);
    setAccreditPatientHasPaidBalance(false);
    setAccreditTransferToPatientBalance(false);
    setRepeatDialogOpen(false);
    setRepeatDialogMode('saved');
    setRepeatSourceDraft(null);
    setQueuedRepeatSettings(null);
    setRepeatSettingsQueued(false);
    setBookingConflictDialogOpen(false);
    setBookingConflictPreview(null);
    setRepeatPreview(null);
    setRepeatError('');
    setRepeatLoading(false);
    setDialogMode('create');
    setSelectedBooking(null);
    setSelectedBookingVideoSession(null);
    setBookingVideoCallOpen(false);
    setBookingVideoCallAppointment(null);
    setForm({
      bookingMode: 'patient',
      patient: getSelectedPatientId() || '',
      patientName: selectedPatientName || '',
      practitioner: '',
      date: '',
      time: '',
      endTime: '',
      notes: '',
      appointmentType: 'follow_up',
      serviceName: formatAppointmentType('follow_up'),
      nonPatientType: 'block',
      title: '',
      assessmentDeliveryMode: '',
      booked_price: '',
      booked_currency: 'EUR',
      payment_method: '',
      split_payment_method: '',
      color: DEFAULT_BOOKING_COLOR,
    });
    setRepeatSettings({
      repeatPattern: 'weekly_same_day',
      repeatEvery: 1,
      repeatCount: 5,
      endMode: 'count',
      untilDate: '',
      weekdays: [],
    });
  };

  const handleOpenSelectedBookingVideoCall = () => {
    if (warnIfVideoCallAlreadyActive()) {
      return;
    }

    const selectedBookingId = selectedBooking?.id || selectedBooking?.appointment_id;
    if (!selectedBookingId) {
      return;
    }

    setBookingVideoCallAppointment({
      ...selectedBooking,
      id: selectedBookingId,
      appointment_id: selectedBookingId,
      video_session: selectedBookingVideoSession || selectedBooking?.video_session || null,
    });
    setBookingVideoCallOpen(true);
  };

  const handleCloseSelectedBookingVideoCall = () => {
    setBookingVideoCallOpen(false);
    setBookingVideoCallAppointment(null);
  };

  const openRepeatBookingDialog = useCallback((sourceBooking = selectedBooking, preserveCurrentRepeatSettings = false, dialogMode = 'saved') => {
    if (!sourceBooking?.id || !sourceBooking?.start_datetime) {
      setRepeatError('Open a saved booking first.');
      setRepeatDialogOpen(true);
      setRepeatDialogMode('draft');
      return;
    }

    const sourceStartMoment = moment(sourceBooking.start_datetime);
    const sourceWeekday = sourceStartMoment && sourceStartMoment.isValid()
      ? sourceStartMoment.isoWeekday() % 7
      : 0;

    setSelectedBooking(sourceBooking);
    setRepeatDialogMode(dialogMode);
    if (!preserveCurrentRepeatSettings) {
      setRepeatSettings({
        repeatPattern: 'weekly_same_day',
        repeatEvery: 1,
        repeatCount: 5,
        endMode: 'count',
        untilDate: '',
        weekdays: [sourceWeekday],
      });
      setRepeatSettingsQueued(false);
    }
    setRepeatPreview(null);
    setRepeatError('');
    setRepeatLoading(false);
    setRepeatDialogOpen(true);
  }, [selectedBooking]);

  const openDraftRepeatBookingDialog = useCallback(() => {
    setRepeatSourceDraft({
      bookingMode: form.bookingMode,
      title: form.title,
      patientName: form.patientName,
      serviceName: form.serviceName,
      nonPatientType: form.nonPatientType,
    });
    setRepeatDialogMode('draft');
    setRepeatPreview(null);
    setRepeatError('');
    setRepeatLoading(false);
    setRepeatDialogOpen(true);
  }, [form.bookingMode, form.nonPatientType, form.patientName, form.serviceName, form.title]);

  const buildRepeatRequestPayload = useCallback((mode, collisionAction = 'fail', sourceBooking = selectedBooking, repeatSettingsOverride = repeatSettings) => {
    const sourceStartMoment = sourceBooking?.start_datetime ? moment(sourceBooking.start_datetime) : selectedBookingStartMoment;
    const sourceWeekday = sourceStartMoment && sourceStartMoment.isValid()
      ? sourceStartMoment.isoWeekday() % 7
      : 0;
    const repeatCount = repeatSettingsOverride.endMode === 'count' ? Number(repeatSettingsOverride.repeatCount || 0) : null;
    const weekdays = repeatSettingsOverride.repeatPattern === 'weekly_same_day'
      ? [sourceWeekday]
      : repeatSettingsOverride.repeatPattern === 'multiple_weekdays'
        ? (repeatSettingsOverride.weekdays || [])
        : [];

    return {
      mode,
      repeat_pattern: repeatSettingsOverride.repeatPattern,
      repeat_every: Number(repeatSettingsOverride.repeatEvery || 1),
      repeat_count: repeatCount,
      until_date: repeatSettingsOverride.endMode === 'until_date' ? (repeatSettingsOverride.untilDate || null) : null,
      weekdays,
      collision_action: collisionAction,
    };
  }, [repeatSettings, selectedBooking, selectedBookingStartMoment]);

  const buildDraftRepeatPreview = useCallback(() => {
    if (!form.date || !form.time || !form.endTime) {
      return null;
    }

    const sourceStartMoment = moment(`${form.date}T${form.time}`, 'YYYY-MM-DDTHH:mm', true);
    const sourceEndMoment = moment(`${form.date}T${form.endTime}`, 'YYYY-MM-DDTHH:mm', true);
    if (!sourceStartMoment.isValid() || !sourceEndMoment.isValid() || !sourceStartMoment.isBefore(sourceEndMoment)) {
      return null;
    }

    const repeatEvery = Math.max(1, Number(repeatSettings.repeatEvery || 1));
    const repeatCount = repeatSettings.endMode === 'count' ? Math.max(1, Number(repeatSettings.repeatCount || 0)) : null;
    const untilDateMoment = repeatSettings.endMode === 'until_date' && repeatSettings.untilDate
      ? moment(repeatSettings.untilDate, 'YYYY-MM-DD', true).endOf('day')
      : null;
    const sourceWeekday = sourceStartMoment.isoWeekday() % 7;
    const targetWeekdays = repeatSettings.repeatPattern === 'daily'
      ? []
      : repeatSettings.repeatPattern === 'weekly_same_day'
        ? [sourceWeekday]
        : (repeatSettings.weekdays && repeatSettings.weekdays.length > 0 ? repeatSettings.weekdays : [sourceWeekday]);
    const sourceWeekStart = sourceStartMoment.clone().startOf('day').subtract(sourceWeekday, 'days');
    const sourceDurationMinutes = sourceEndMoment.diff(sourceStartMoment, 'minutes');
    const sourceStaffId = String(form.practitioner || '');
    const sourceRoomId = String(form.room || '');
    const sourceLocationId = String(form.clinic_location || '');

    const buildOccurrenceMoment = (baseDateMoment) => baseDateMoment.clone()
      .hour(sourceStartMoment.hour())
      .minute(sourceStartMoment.minute())
      .second(sourceStartMoment.second())
      .millisecond(sourceStartMoment.millisecond());

    const bookingMatchesRepeatScope = (booking) => {
      const bookingStaffId = String(booking.staff_member || booking.staff_member_id || '');
      const bookingRoomId = String(booking.room || booking.room_id || '');

      if (!sourceRoomId) {
        return false;
      }

      return bookingRoomId === sourceRoomId && bookingStaffId !== sourceStaffId;
    };

    const draftOccurrences = [];
    const maxCycles = repeatCount || 60;

    for (let cycleIndex = 1; cycleIndex <= maxCycles; cycleIndex += 1) {
      if (repeatSettings.endMode === 'count' && repeatCount && cycleIndex > repeatCount) {
        break;
      }

      if (repeatSettings.repeatPattern === 'daily') {
        const targetDateMoment = sourceStartMoment.clone().add(repeatEvery * cycleIndex, 'days');
        if (untilDateMoment && targetDateMoment.isAfter(untilDateMoment, 'day')) {
          break;
        }

        const occurrenceStartMoment = buildOccurrenceMoment(targetDateMoment.clone().startOf('day'));
        const occurrenceEndMoment = occurrenceStartMoment.clone().add(sourceDurationMinutes, 'minutes');
        draftOccurrences.push({
          index: cycleIndex,
          date: targetDateMoment.format('YYYY-MM-DD'),
          weekday: targetDateMoment.isoWeekday() % 7,
          start_datetime: occurrenceStartMoment.toISOString(),
          end_datetime: occurrenceEndMoment.toISOString(),
          start_datetime_obj: occurrenceStartMoment,
          end_datetime_obj: occurrenceEndMoment,
        });
        continue;
      }

      const weekStartMoment = sourceWeekStart.clone().add(repeatEvery * cycleIndex, 'weeks');
      targetWeekdays.forEach((weekday) => {
        const targetDateMoment = weekStartMoment.clone().add(weekday, 'days');
        if (untilDateMoment && targetDateMoment.isAfter(untilDateMoment, 'day')) {
          return;
        }

        const occurrenceStartMoment = buildOccurrenceMoment(targetDateMoment.clone().startOf('day'));
        const occurrenceEndMoment = occurrenceStartMoment.clone().add(sourceDurationMinutes, 'minutes');
        draftOccurrences.push({
          index: cycleIndex,
          date: targetDateMoment.format('YYYY-MM-DD'),
          weekday: targetDateMoment.isoWeekday() % 7,
          start_datetime: occurrenceStartMoment.toISOString(),
          end_datetime: occurrenceEndMoment.toISOString(),
          start_datetime_obj: occurrenceStartMoment,
          end_datetime_obj: occurrenceEndMoment,
        });
      });
    }

    const draftOccurrencesWithConflicts = draftOccurrences.map((occurrence) => {
      const conflictMatches = bookings.filter((booking) => {
        const bookingStatus = String(booking.status || '').toLowerCase();
        if (!['scheduled', 'confirmed'].includes(bookingStatus)) {
          return false;
        }

        if (!bookingMatchesRepeatScope(booking)) {
          return false;
        }

        const bookingStartMoment = moment(booking.start_datetime);
        const bookingEndMoment = moment(booking.end_datetime);
        if (!bookingStartMoment.isValid() || !bookingEndMoment.isValid()) {
          return false;
        }

        const occurrenceStartMoment = moment(occurrence.start_datetime);
        const occurrenceEndMoment = moment(occurrence.end_datetime);
        return bookingStartMoment.isBefore(occurrenceEndMoment) && bookingEndMoment.isAfter(occurrenceStartMoment);
      }).map((booking) => ({
        id: booking.id,
        title: booking.title || '',
        patient_name: booking.patient_name || '',
        staff_member_name: booking.staff_member_name || '',
        room_name: booking.room_name || '',
        start_datetime: booking.start_datetime,
        end_datetime: booking.end_datetime,
      }));

      return {
        ...occurrence,
        conflict_count: conflictMatches.length,
        conflicts: conflictMatches,
      };
    });

    const conflictOccurrences = draftOccurrencesWithConflicts.filter((occurrence) => occurrence.conflict_count > 0);

    return {
      success: true,
      mode: 'preview',
      repeat_pattern: repeatSettings.repeatPattern,
      repeat_every: repeatSettings.repeatEvery,
      repeat_count: repeatCount,
      until_date: repeatSettings.endMode === 'until_date' ? (repeatSettings.untilDate || null) : null,
      weekdays: targetWeekdays,
      source_appointment: null,
      occurrences: draftOccurrencesWithConflicts.map(({ start_datetime_obj, end_datetime_obj, ...occurrence }) => occurrence),
      conflicts: conflictOccurrences.map(({ start_datetime_obj, end_datetime_obj, ...occurrence }) => occurrence),
      summary: {
        requested: draftOccurrencesWithConflicts.length,
        conflicting: conflictOccurrences.length,
        available: draftOccurrencesWithConflicts.length - conflictOccurrences.length,
      },
    };
  }, [bookings, form.clinic_location, form.date, form.endTime, form.practitioner, form.room, form.time, repeatSettings]);

  const handleRepeatPreview = useCallback(async () => {
    if (!selectedBooking?.id) {
      setRepeatError('Open a saved booking first.');
      return;
    }

    setRepeatLoading(true);
    setRepeatError('');

    try {
      const response = await fetch(`${backendBaseUrl}/api/calendar/appointments/${selectedBooking.id}/repeat/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
        },
        body: JSON.stringify(buildRepeatRequestPayload('preview')),
      });

      const responseData = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(responseData.error || responseData.errors || 'Unable to preview repeated bookings');
      }

      setRepeatPreview(responseData);
    } catch (error) {
      setRepeatError(error.message || 'Unable to preview repeated bookings');
    } finally {
      setRepeatLoading(false);
    }
  }, [buildRepeatRequestPayload, selectedBooking, token]);

  const handleRepeatCreate = useCallback(async (collisionAction, sourceBooking = selectedBooking, repeatSettingsOverride = repeatSettings) => {
    if (!sourceBooking?.id) {
      setRepeatError('Open a saved booking first.');
      return;
    }

    setRepeatLoading(true);
    setRepeatError('');

    try {
      const response = await fetch(`${backendBaseUrl}/api/calendar/appointments/${sourceBooking.id}/repeat/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
        },
        body: JSON.stringify(buildRepeatRequestPayload('create', collisionAction, sourceBooking, repeatSettingsOverride)),
      });

      const responseData = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (responseData.occurrences) {
          setRepeatPreview(responseData);
        }
        throw new Error(responseData.error || responseData.errors || 'Unable to create repeated bookings');
      }

      const createdCount = Array.isArray(responseData.created) ? responseData.created.length : 0;
      const skippedCount = Array.isArray(responseData.skipped) ? responseData.skipped.length : 0;

      await fetchBookings();
      setRepeatSettingsQueued(false);
      setQueuedRepeatSettings(null);
      setRepeatDialogOpen(false);
      setRepeatPreview(null);
      setRepeatError('');
      handleDialogClose();
      setSnackbarMessage(
        skippedCount > 0
          ? `Created ${createdCount} repeated appointments and skipped ${skippedCount} conflicting dates.`
          : `Created ${createdCount} repeated appointments.`
      );
      setSnackbarSeverity(skippedCount > 0 ? 'warning' : 'success');
      setSnackbarOpen(true);
    } catch (error) {
      setRepeatError(error.message || 'Unable to create repeated bookings');
    } finally {
      setRepeatLoading(false);
    }
    return true;
  }, [buildRepeatRequestPayload, fetchBookings, handleDialogClose, repeatSettings, selectedBooking, token]);

  useEffect(() => {
    if (
      repeatDialogOpen
      && repeatDialogMode === 'review'
      && repeatSettingsQueued
      && queuedRepeatSettings
      && selectedBooking?.id
      && !repeatPreview
      && !repeatLoading
    ) {
      setRepeatSettingsQueued(false);
      void handleRepeatPreview();
    }
  }, [handleRepeatPreview, queuedRepeatSettings, repeatDialogMode, repeatDialogOpen, repeatLoading, repeatPreview, repeatSettingsQueued, selectedBooking?.id]);

  const handleDeleteClick = () => {
    setOpenDeleteConfirm(true);
  };

  const selectedBookingIsWithinLateCancellationWindow = Boolean(
    selectedBookingStartMoment
    && selectedBookingStartMoment.isValid()
    && selectedBookingStartMoment.isAfter(moment())
    && selectedBookingStartMoment.diff(moment(), 'hours', true) <= Number(lateCancellationThresholdHours || 24)
  );

  const handleDeleteConfirm = async (chargeLateCancellationFee = false, deleteMode = 'cancel') => {
    if (!selectedBooking?.id) {
      setSnackbarMessage('Error: No booking selected');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    setIsLoading(true);
    try {
      const isPatientAppointment = Boolean(selectedBooking.patient || selectedBooking.patient_name || selectedBooking.patient_email);
      const shouldCancelAppointment = isPatientAppointment && deleteMode !== 'delete';
      const response = shouldCancelAppointment
        ? await fetch(`${backendBaseUrl}/api/calendar/appointments/${selectedBooking.id}/cancel/`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Token ${token}`,
            },
            body: JSON.stringify({
              reason: 'Cancelled by clinic staff',
              charge_late_cancellation_fee: Boolean(chargeLateCancellationFee),
            }),
          })
        : await fetch(`${backendBaseUrl}/api/calendar/appointments/${selectedBooking.id}/delete/`, {
            method: 'DELETE',
            headers: { 'Authorization': `Token ${token}` },
          });

      if (response.ok) {
        setOpenDeleteConfirm(false);
        fetchBookings();
        handleDialogClose();
        setSnackbarMessage(shouldCancelAppointment ? 'Appointment cancelled successfully' : 'Appointment deleted successfully');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } else {
        const errorData = await response.json();
        console.error('Error deleting/cancelling appointment:', errorData);
        setSnackbarMessage('Error deleting/cancelling appointment: ' + JSON.stringify(errorData));
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (e) {
      console.error('Error deleting/cancelling appointment:', e);
      setSnackbarMessage('Error deleting/cancelling appointment: ' + e.message);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
    setIsLoading(false);
  };

  const handleSickDayCancelConfirm = async () => {
    if (!selectedSickDay?.id) {
      setSnackbarMessage('Error: No sick day selected');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${backendBaseUrl}/api/calendar/vacations/${selectedSickDay.id}/delete/`, {
        method: 'DELETE',
        headers: { 'Authorization': `Token ${token}` },
      });

      if (response.ok) {
        setOpenSickDayCancelConfirm(false);
        setSelectedSickDay(null);
        await Promise.all([
          fetchAvailabilitySlots(),
          fetchStaffVacations(),
        ]);
        setSnackbarMessage('Sick day cancelled successfully');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } else {
        const errorData = await response.json();
        console.error('Error cancelling sick day:', errorData);
        setSnackbarMessage('Error cancelling sick day: ' + JSON.stringify(errorData));
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error cancelling sick day:', error);
      setSnackbarMessage('Error cancelling sick day: ' + error.message);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
    setIsLoading(false);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    const getNonPatientTitle = (type) => {
      if (type === 'block') {
        return 'Blocked time';
      }
      if (type === 'sick_day') {
        return 'Sick day';
      }
      return getNonPatientBookingLabel(type);
    };

    if (name === 'bookingMode') {
      if (value === 'non_patient') {
        const defaultNonPatientType = form.nonPatientType || 'block';
        const defaultTitle = getNonPatientTitle(defaultNonPatientType);
        setForm((prev) => ({
          ...prev,
          bookingMode: 'non_patient',
          patient: '',
          patientName: '',
          treatment: '',
          appointmentType: 'other',
          serviceName: '',
          title: prev.title && prev.title !== 'Blocked time' ? prev.title : defaultTitle,
          nonPatientType: defaultNonPatientType,
        }));
        setSelectedPayer('patient');
        setPayerReferralOptions([]);
        return;
      }

      setForm((prev) => ({
        ...prev,
        bookingMode: 'patient',
        title: '',
        appointmentType: prev.appointmentType === 'other' ? 'follow_up' : prev.appointmentType,
        serviceName: prev.serviceName || formatAppointmentType(prev.appointmentType === 'other' ? 'follow_up' : prev.appointmentType),
      }));
      return;
    }

    if (name === 'nonPatientType') {
      const nextTitle = getNonPatientTitle(value);
      const isAbsenceType = ['sick_day', 'paid_leave', 'agreed_absence'].includes(value);
      setForm((prev) => ({
        ...prev,
        nonPatientType: value,
        title: prev.title && prev.title !== getNonPatientTitle(prev.nonPatientType || 'block') ? prev.title : nextTitle,
        time: isAbsenceType ? '00:00' : prev.time,
        endTime: isAbsenceType ? '23:59' : prev.endTime,
      }));
      return;
    }

    if (name === 'practitioner') {
      const nextPractitioner = String(value || '');
      bookingDialogPractitionerManualRef.current = true;

      setForm((prev) => {
        const nextForm = {
          ...prev,
          practitioner: nextPractitioner,
        };

        if (dialogMode === 'create' && nextPractitioner && prev.date && prev.time) {
          const selectedStart = moment(`${prev.date}T${prev.time}`, 'YYYY-MM-DDTHH:mm');
          const selectedEnd = prev.endTime
            ? moment(`${prev.date}T${prev.endTime}`, 'YYYY-MM-DDTHH:mm')
            : selectedStart.clone().add(30, 'minutes');

          const matchingAllocation = (Array.isArray(availabilitySlots) ? availabilitySlots : []).find((slot) => {
            if (!slot) return false;

            const slotStaffId = String(slot.staff_member || slot.staffId || slot.staff_member_id || '').trim();
            if (slotStaffId !== nextPractitioner) {
              return false;
            }

            if (String(slot.clinic_location || '') !== String(prev.clinic_location || selectedLocation || '')) {
              return false;
            }

            const slotDateKey = slot.specific_date ? moment(slot.specific_date).format('YYYY-MM-DD') : '';
            const slotWeekday = Number(slot.day_of_week);
            const selectedDateKey = selectedStart.format('YYYY-MM-DD');
            const selectedWeekday = (selectedStart.day() + 6) % 7;
            const matchesDate = slotDateKey
              ? slotDateKey === selectedDateKey
              : Number.isFinite(slotWeekday)
                ? slotWeekday === selectedWeekday
                : true;

            const startMinutes = parseTimeToMinutes(slot.start_time);
            const endMinutes = parseTimeToMinutes(slot.end_time);
            const selectedStartMinutes = selectedStart.hours() * 60 + selectedStart.minutes();
            const selectedEndMinutes = selectedEnd.hours() * 60 + selectedEnd.minutes();
            const matchesTime = Number.isFinite(startMinutes) && Number.isFinite(endMinutes)
              ? selectedStartMinutes >= startMinutes && selectedEndMinutes <= endMinutes
              : true;

            return matchesDate && matchesTime && String(slot.room || slot.room_id || slot.roomId || '').trim();
          });

          if (matchingAllocation) {
            nextForm.room = String(matchingAllocation.room || matchingAllocation.room_id || matchingAllocation.roomId || '').trim();
          }
        }

        return nextForm;
      });
      return;
    }

    if (name === 'appointmentType') {
      const svc = clinicServices.find(s => String(s.id) === String(value));
      if (svc) {
        // Auto-fill price, currency, and end time from the selected service
        setForm(prev => {
          const startMoment = prev.time
            ? moment(`${prev.date || '2000-01-01'}T${prev.time}`, 'YYYY-MM-DDTHH:mm')
            : null;
          const newEndTime = startMoment
            ? startMoment.clone().add(svc.duration_minutes, 'minutes').format('HH:mm')
            : prev.endTime;
          const currentServiceColor = getServiceDefaultColor(prev.appointmentType);
          const shouldUseServiceDefaultColor = !prev.color || prev.color === currentServiceColor || prev.color === DEFAULT_BOOKING_COLOR;

          return {
            ...prev,
            appointmentType: value,
            serviceName: svc.name || svc.service_name || formatAppointmentType(value),
            assessmentDeliveryMode: svc.service_type === 'initial_consultation' ? prev.assessmentDeliveryMode : '',
            booked_price: svc.price != null ? String(svc.price) : prev.booked_price,
            booked_currency: svc.currency || prev.booked_currency,
            endTime: newEndTime,
            color: shouldUseServiceDefaultColor ? (svc.color || DEFAULT_BOOKING_COLOR) : prev.color,
          };
        });
        return;
      }
    }
    if (name === 'clinic_location') {
      const nextCalendarId = value ? getDefaultCalendarIdForLocation(value) : '';
      setForm((prev) => ({
        ...prev,
        clinic_location: value,
        room: '',
        clinic_calendar: nextCalendarId,
      }));
      return;
    }
    setForm({
      ...form,
      [name]: value,
      ...(name === 'appointmentType' ? { serviceName: clinicServices.find(s => String(s.id) === String(value))?.name || formatAppointmentType(value) } : {}),
      ...(name === 'appointmentType' && value !== 'initial_consultation' ? { assessmentDeliveryMode: '' } : {}),
      ...(name === 'title' ? { title: value } : {}),
    });
  };

  const adjustEndTime = (minutes) => {
    if (!form.endTime) return;
    const [hours, mins] = form.endTime.split(':').map(Number);
    
    // Simple time arithmetic without Date object calendar complexity
    let totalMinutes = hours * 60 + mins + minutes;
    
    // Handle day wrapping
    if (totalMinutes < 0) totalMinutes = 0;
    if (totalMinutes >= 24 * 60) totalMinutes = 24 * 60 - 5; // Cap at 23:55
    
    const newHours = Math.floor(totalMinutes / 60);
    const newMins = totalMinutes % 60;
    const newEndTime = `${String(newHours).padStart(2, '0')}:${String(newMins).padStart(2, '0')}`;
    
    console.log('[TIME] Adjusted from', form.endTime, 'by', minutes, 'minutes to', newEndTime);
    setForm({ ...form, endTime: newEndTime });
  };

  const reportStaffVacation = async ({ staffMember, startDate, endDate, note, sourceAppointmentId, absenceType = 'vacation', autoCancelPatients = false }) => {
    if (!staffMember || !startDate) {
      return;
    }
    try {
      const response = await fetch(`${backendBaseUrl}/api/calendar/vacations/create/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
        },
        body: JSON.stringify({
          staff_member: staffMember,
          absence_type: absenceType,
          start_date: startDate,
          end_date: endDate || startDate,
          note: note || '',
          source_appointment: sourceAppointmentId || null,
          auto_cancel_patients: Boolean(autoCancelPatients),
        }),
      });
      if (response.ok) {
        setSnackbarMessage(
          absenceType === 'sick_day'
            ? (autoCancelPatients
              ? 'Sick day saved. Affected patients were auto-cancelled and notified.'
              : 'Sick day saved and applied to the calendar.')
            : 'Vacation reported. Staff allocations freed and clinic owner notified.'
        );
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      }
    } catch (vacationError) {
      console.error('[VACATION] Failed to report staff vacation:', vacationError);
    }
  };

  const handleConfirmSickDaySubmission = async (autoCancelPatients = false) => {
    if (!pendingSickDaySubmission) {
      setOpenSickDayAutoCancelConfirm(false);
      return;
    }

    setIsLoading(true);
    try {
      await reportStaffVacation({
        ...pendingSickDaySubmission,
        autoCancelPatients,
      });
      await Promise.all([
        fetchBookings(),
        fetchAvailabilitySlots(),
        fetchStaffVacations(),
      ]);
      handleDialogClose();
      setOpenSickDayAutoCancelConfirm(false);
      setPendingSickDaySubmission(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveBooking = async (collisionAction = 'fail', options = {}) => {
    const normalizedCollisionAction = typeof collisionAction === 'string' ? collisionAction : 'fail';
    const finalizeQueuedRepeat = Boolean(options.finalizeQueuedRepeat);
    const repeatCollisionAction = typeof options.repeatCollisionAction === 'string' ? options.repeatCollisionAction : 'book_anyway';
    const formatErrorMessage = (value) => {
      if (typeof value === 'string') return value;
      if (value == null) return 'Unknown error';
      if (Array.isArray(value)) {
        return value.map((item) => formatErrorMessage(item)).join(', ');
      }
      if (typeof value === 'object') {
        if (typeof value.error === 'string') return value.error;
        if (typeof value.detail === 'string') return value.detail;
        if (typeof value.message === 'string') return value.message;
        try {
          return JSON.stringify(value);
        } catch (serializationError) {
          return 'Unable to serialize error response';
        }
      }
      return String(value);
    };

    const isPatientBooking = form.bookingMode !== 'non_patient';
    if ((!isPatientBooking && !form.title) || !form.practitioner || !form.date || !form.time || !form.endTime || (isPatientBooking && (!form.patient || !form.treatment))) {
      setSnackbarMessage('Please fill in all required fields');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    setIsLoading(true);
    const method = selectedBooking ? 'PUT' : 'POST';
    const url = selectedBooking
      ? `${backendBaseUrl}/api/calendar/appointments/${selectedBooking.id}/`
      : `${backendBaseUrl}/api/calendar/appointments/create/`;
    
    console.log('[SAVE] Mode:', selectedBooking ? 'EDIT' : 'CREATE');
    console.log('[SAVE] Method:', method);
    console.log('[SAVE] URL:', url);
    // console.log('[SAVE] Selected Booking:', selectedBooking);
    
    // Convert local date/time to UTC datetime strings (backend expects UTC with Z suffix)
    // Parse the local form values and convert to UTC
    const localDate = moment(`${form.date}T${form.time}`, 'YYYY-MM-DDTHH:mm');
    const localEndDate = moment(`${form.date}T${form.endTime}`, 'YYYY-MM-DDTHH:mm');
    
    const startDatetime = localDate.utc().format('YYYY-MM-DDTHH:mm:ss') + 'Z';
    const endDatetime = localEndDate.utc().format('YYYY-MM-DDTHH:mm:ss') + 'Z';
    
    // Calculate duration in minutes from local times
    const durationMinutes = localEndDate.diff(localDate, 'minutes');
    
    // Resolve payer selection to backend fields
    const resolvePayerSelection = (selectionValue) => {
      if (!selectionValue || selectionValue === 'patient') {
        return {
          payer_type: 'patient',
          payer_insurance: null,
          payer_other: null,
          payer_referral_letter: null,
          insurance_paid: false,
        };
      }
      if (String(selectionValue || '').startsWith('other:')) {
        const selectedOther = (Array.isArray(clinicOtherPayers) ? clinicOtherPayers : []).find((option) => `other:${option.id}` === selectionValue) || null;
        return {
          payer_type: 'other',
          payer_insurance: null,
          payer_other: selectedOther?.id || String(selectionValue).replace(/^other:/, ''),
          payer_referral_letter: null,
          insurance_paid: false,
        };
      }
      const selected = payerReferralOptions.find((option) => option.id === selectionValue);
      if (!selected) {
        return {
          payer_type: 'patient',
          payer_insurance: null,
          payer_other: null,
          payer_referral_letter: null,
          insurance_paid: false,
        };
      }
      return {
        payer_type: 'insurance',
        payer_insurance: selected.insuranceId,
        payer_other: null,
        payer_referral_letter: selected.referralLetterId,
        insurance_paid: true,
      };
    };
    
    const payerFields = resolvePayerSelection(selectedPayer);

    // If appointmentType is a service UUID, derive appointment_type from the service record
    const selectedService = isPatientBooking ? clinicServices.find(s => String(s.id) === String(form.appointmentType)) : null;
    const resolvedAppointmentType = isPatientBooking
      ? (selectedService ? (selectedService.service_type || 'other') : (form.appointmentType || 'follow_up'))
      : 'other';
    const selectedServiceColor = selectedService?.color || DEFAULT_BOOKING_COLOR;
    const resolvedColor = isPatientBooking && selectedService
      ? ((form.color || DEFAULT_BOOKING_COLOR) === selectedServiceColor ? null : (form.color || null))
      : ((form.color || DEFAULT_BOOKING_COLOR) === DEFAULT_BOOKING_COLOR ? null : (form.color || null));
    const resolvedServiceName = isPatientBooking
      ? (form.serviceName || selectedService?.name || selectedService?.service_name || formatAppointmentType(resolvedAppointmentType))
      : getNonPatientBookingLabel(form.nonPatientType);
    const resolvedPatientName = isPatientBooking ? (form.patientName || getPatientDisplayName(form.patient)) : '';
    const resolvedTitle = isPatientBooking
      ? `${resolvedPatientName || selectedBooking?.patient_name || 'Patient'} - ${resolvedServiceName}`
      : (form.title || resolvedServiceName);
    
    if (!selectedBooking && resolvedAppointmentType === 'initial_consultation' && !form.assessmentDeliveryMode) {
      setSnackbarMessage('Select full or short-form assessment email for initial consultations');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setIsLoading(false);
      return;
    }

    if (!isPatientBooking && !selectedBooking && ['sick_day', 'paid_leave', 'agreed_absence'].includes(form.nonPatientType)) {
      if (form.nonPatientType === 'sick_day' && !pendingSickDaySubmission) {
        setPendingSickDaySubmission({
          staffMember: form.practitioner,
          startDate: form.date,
          endDate: form.date,
          note: form.notes || getNonPatientBookingLabel(form.nonPatientType),
          sourceAppointmentId: null,
          absenceType: form.nonPatientType,
        });
        setOpenSickDayAutoCancelConfirm(true);
        setIsLoading(false);
        return;
      }

      try {
        await reportStaffVacation({
          staffMember: form.practitioner,
          startDate: form.date,
          endDate: form.date,
          note: form.notes || getNonPatientBookingLabel(form.nonPatientType),
          sourceAppointmentId: null,
          absenceType: form.nonPatientType,
        });
        await Promise.all([
          fetchBookings(),
          fetchAvailabilitySlots(),
          fetchStaffVacations(),
        ]);
        handleDialogClose();
        setSnackbarMessage('Sick day saved successfully');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    const appointmentData = {
      patient: isPatientBooking ? form.patient : null,
      treatment: isPatientBooking ? (form.treatment || null) : null,
      staff_member: form.practitioner,  // UUID string
      start_datetime: startDatetime,
      end_datetime: endDatetime,
      duration_minutes: durationMinutes,
      status: 'scheduled',
      patient_notes: form.notes || '',
      appointment_type: resolvedAppointmentType,
      service: selectedService ? selectedService.id : null,
      title: resolvedTitle,
      booked_price: form.booked_price === '' ? null : Number(form.booked_price),
      booked_currency: (form.booked_currency || 'EUR').toUpperCase(),
      care_provider: user?.id,  // Logged-in user is the care provider
      clinic_location: form.clinic_location || null,
      room: form.room || null,
      absence_type: !isPatientBooking && ['sick_day', 'paid_leave', 'agreed_absence'].includes(form.nonPatientType) ? form.nonPatientType : undefined,
      color: resolvedColor,
      collision_action: normalizedCollisionAction,
      ...payerFields,  // Add payer fields
    };

    if (isPatientBooking && !selectedBooking && resolvedAppointmentType === 'initial_consultation') {
      appointmentData.assessment_delivery_mode = form.assessmentDeliveryMode;
    }
    
    // console.log('[SAVE] Appointment Data:', appointmentData);
    
    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
        },
        body: JSON.stringify(appointmentData),
      });
      
      console.log('[SAVE] Response status:', response.status);
      console.log('[SAVE] Response ok:', response.ok);
      
      if (response.ok) {
        const responseData = await response.json();
        // console.log('[SAVE] Success response:', responseData);
        const savedBooking = responseData?.id ? responseData : responseData?.appointment || responseData?.data || responseData;
        const patientName = resolvedPatientName;
        const serviceName = resolvedServiceName;
        if (savedBooking?.id) {
          upsertBookingInState({
            ...savedBooking,
            patient: isPatientBooking ? (savedBooking.patient || form.patient) : null,
            patient_name: isPatientBooking ? (patientName || savedBooking.patient_name || selectedBooking?.patient_name || '') : '',
            service: isPatientBooking ? (savedBooking.service || selectedService?.id || null) : null,
            service_name: isPatientBooking ? (serviceName || savedBooking.service_name || selectedBooking?.service_name || '') : '',
            appointment_type: resolvedAppointmentType || savedBooking.appointment_type || selectedBooking?.appointment_type || 'follow_up',
            title: resolvedTitle || savedBooking.title || '',
          });
        } else {
          upsertBookingInState(savedBooking);
        }
        if (!isPatientBooking && !selectedBooking && ['vacation', 'sick_day', 'paid_leave', 'agreed_absence'].includes(form.nonPatientType) && savedBooking?.id) {
          void reportStaffVacation({
            staffMember: form.practitioner,
            startDate: form.date,
            endDate: form.date,
            note: form.notes || getNonPatientBookingLabel(form.nonPatientType),
            sourceAppointmentId: savedBooking.id,
            absenceType: form.nonPatientType,
          });
        }
        if (!selectedBooking && repeatSettingsQueued && queuedRepeatSettings && savedBooking?.id) {
          if (finalizeQueuedRepeat) {
            await handleRepeatCreate(repeatCollisionAction, savedBooking, queuedRepeatSettings);
            setIsLoading(false);
            return;
          }
        }
        handleDialogClose();
        setSnackbarMessage('Appointment saved successfully');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } else {
        const errorData = await response.json();
        console.error('[SAVE] Error response:', errorData);
        if (response.status === 409 && Array.isArray(errorData.conflicts) && errorData.conflicts.length > 0) {
          setBookingConflictPreview(errorData);
          setBookingConflictDialogOpen(true);
          setIsLoading(false);
          return;
        }
        setSnackbarMessage('Error saving appointment: ' + formatErrorMessage(errorData));
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (e) {
      console.error('[SAVE] Exception:', e);
      setSnackbarMessage('Error saving appointment: ' + (e?.message || String(e)));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
    setIsLoading(false);
  };

  const handleSaveClassAttendance = async () => {
    if (!selectedClassBooking?.id || !token) {
      return;
    }

    const drafts = Array.isArray(classAttendanceDrafts) ? classAttendanceDrafts : [];
    if (drafts.length === 0) {
      setSnackbarMessage('No class attendees were found to save.');
      setSnackbarSeverity('info');
      setSnackbarOpen(true);
      return;
    }

    const classStart = moment(selectedClassBooking.start_datetime);
    const classEnd = moment(selectedClassBooking.end_datetime);
    const durationMinutes = Math.max(0, classEnd.diff(classStart, 'minutes')) || Number(selectedClassBooking.duration_minutes || 0) || 0;
    const classService = selectedClassBooking.service || null;
    const classRoomId = selectedClassBooking.room?.id || selectedClassBooking.room || null;
    const classStaffId = classAttendanceStaffId || selectedClassBooking.staff_member || '';
    const classTitle = selectedClassBooking.title || selectedClassBooking.service_name || 'Class';
    const classCurrency = classService?.currency || selectedClassBooking.booked_currency || 'EUR';
    const classPrice = classService?.price ?? selectedClassBooking.booked_price ?? null;

    setClassDialogSaving(true);
    try {
      const responses = await Promise.all(drafts.map(async (draft) => {
        const patientId = String(draft.patientId || '').trim();
        if (!patientId) {
          return { patientId, skipped: true };
        }

        const attended = Boolean(draft.attended);
        if (!attended) {
          return { patientId, attended, noteSaved: false, appointmentSaved: false };
        }

        const noteText = String(draft.note || '').trim() || getClassAttendanceDefaultNote(selectedClassBooking, patientId, attended);
        const treatmentId = String(draft.treatmentId || '').trim();
        const journalNoteHtml = buildClassAttendanceJournalNoteHtml(selectedClassBooking, patientId, attended, noteText);

        const patientLabel = getPatientDisplaySummary(patientId) || 'Patient';
        const attendanceResponse = await fetch(`${backendBaseUrl}/api/calendar/appointments/create/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
          },
          body: JSON.stringify({
            patient: patientId,
            staff_member: classStaffId || null,
            start_datetime: classStart.utc().format('YYYY-MM-DDTHH:mm:ss') + 'Z',
            end_datetime: classEnd.utc().format('YYYY-MM-DDTHH:mm:ss') + 'Z',
            duration_minutes: durationMinutes,
            status: 'completed',
            notes: `${CLASS_ATTENDANCE_NOTE_MARKER} ${noteText}`,
            patient_notes: journalNoteHtml,
            treatment: treatmentId || null,
            service: classService?.id || null,
            title: `${patientLabel} - ${classTitle}`,
            booked_price: classPrice == null ? null : Number(classPrice),
            booked_currency: classCurrency,
            care_provider: user?.id,
            clinic_location: selectedClassBooking.clinic_location || null,
            room: classRoomId,
            collision_action: 'book_anyway',
          }),
        });

        if (!attendanceResponse.ok) {
          const errorText = await attendanceResponse.text().catch(() => 'Failed to save attended class appointment');
          throw new Error(errorText || 'Failed to save attended class appointment');
        }

        try {
          const updateDetail = { treatmentId: treatmentId || null, userId: patientId, ts: Date.now() };
          localStorage.setItem('vp_latest_journal_update', JSON.stringify(updateDetail));
          window.dispatchEvent(new CustomEvent('vp-journal-updated', { detail: updateDetail }));
        } catch (eventError) {
          // ignore broadcast failures
        }

        return { patientId, attended, noteSaved: true, appointmentSaved: true };
      }));

      const attendedCount = responses.filter((entry) => entry?.attended && entry?.appointmentSaved).length;
      setSnackbarMessage(attendedCount > 0
        ? `Saved attendance for ${attendedCount} patients.`
        : 'No attended class entries were saved.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setOpenClassDialog(false);
      setSelectedClassBooking(null);
      setClassAttendanceDrafts([]);
      void fetchBookings();
    } catch (error) {
      console.error('[CLASS] Failed to save attendance:', error);
      setSnackbarMessage(`Failed to save class attendance: ${error?.message || error}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setClassDialogSaving(false);
    }
  };

  const renderStripePaymentSummary = useCallback(() => {
    if (!stripePaymentQuote) return null;

    const currency = stripePaymentQuote.currency || stripePaymentInvoice?.currency || selectedBooking?.booked_currency || 'EUR';
    const consultationAmount = stripePaymentQuote.consultation_amount ?? 0;
    const feeAmount = stripePaymentQuote.fee_amount ?? 0;
    const feePercent = stripePaymentQuote.fee_percent ?? 0;
    const chargeAmount = stripePaymentQuote.charge_amount ?? stripePaymentQuote.display_total_amount ?? 0;
    const feeModeLabel = stripePaymentQuote.fee_display_mode === 'patient_surcharge'
      ? 'charged on top of the consultation'
      : stripePaymentQuote.fee_display_mode === 'patient_present_checkout'
        ? 'charged through the patient-present Stripe checkout with no extra Stripe fee added'
        : 'shown as a platform fee deduction';

    return (
      <Box style={{ padding: 14, backgroundColor: '#f5f9ff', border: '1px solid #dde6f2', borderRadius: 10, marginBottom: 16 }}>
        <Typography style={{ color: '#002D55', fontWeight: 700, marginBottom: 6 }}>
          Payment summary
        </Typography>
        <Typography style={{ color: '#002D55', fontSize: 14 }}>
          Consultation amount: {formatMoney(consultationAmount, currency)}
        </Typography>
        <Typography style={{ color: '#002D55', fontSize: 14 }}>
          Stripe fee ({Number(feePercent || 0).toFixed(2)}%): {formatMoney(feeAmount, currency)}
        </Typography>
        <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 15, marginTop: 6 }}>
          Total billed: {formatMoney(chargeAmount, currency)}
        </Typography>
        <Typography style={{ color: '#666', fontSize: 12, marginTop: 6 }}>
          Fee mode: {feeModeLabel}
        </Typography>
      </Box>
    );
  }, [selectedBooking, stripePaymentInvoice, stripePaymentQuote]);

  // Abbreviate a staff name: first letter of each part. Handles multi-part first/last names.
  const abbreviateName = (fullName) => {
    if (!fullName) return '';
    // Split the whole name into tokens (handles spaces in first or last name fields)
    const tokens = fullName.trim().split(/\s+/);
    if (tokens.length === 1) return tokens[0]; // single token, return as-is
    // First token initials except last token which is shown as last name abbreviated
    return tokens.map(t => t.charAt(0).toUpperCase()).join('');
  };

  function getPaymentMethodDisplayLabel(paymentMethod) {
    const normalized = String(paymentMethod || '').trim().toLowerCase();
    const displayMap = {
      mobilepay: 'MobilePay',
      card: 'Card (terminal)',
      'card (terminal)': 'Card (terminal)',
      cardautopay: 'Card (stripe)',
      'card (stripe)': 'Card (stripe)',
      stripe: 'Card (stripe)',
      invoice: 'Invoice',
      bill: 'Invoice',
      'invoice (mail)': 'Invoice (mail)',
      'invoice (ean)': 'Invoice (EAN)',
      'split billing (mail)': 'Split billing (mail)',
      'split billing (ean)': 'Split billing (EAN)',
      'send invoice per mail': 'Invoice',
      'send bill per mail': 'Invoice',
      'bank transfer': 'Bank transfer',
      bank_transfer: 'Bank transfer',
      cash: 'Cash',
    };

    return displayMap[normalized] || String(paymentMethod || '').trim() || 'Payment';
  }

  function normalizePaymentMethodSelectValue(paymentMethod) {
    const normalized = String(paymentMethod || '').trim().toLowerCase();
    const selectMap = {
      mobilepay: 'MobilePay',
      card: 'Card',
      'card (terminal)': 'Card',
      cardautopay: 'CardAutopay',
      'card (stripe)': 'CardAutopay',
      stripe: 'CardAutopay',
      invoice: 'Invoice',
      bill: 'Invoice',
      'invoice (mail)': 'Invoice (mail)',
      'invoice (ean)': 'Invoice (EAN)',
      'split billing (mail)': 'Split billing (mail)',
      'split billing (ean)': 'Split billing (EAN)',
      'send invoice per mail': 'Invoice',
      'send bill per mail': 'Invoice',
      'bank transfer': 'Bank Transfer',
      bank_transfer: 'Bank Transfer',
      cash: 'Cash',
    };

    return selectMap[normalized] || '';
  }

  function getPaymentMethodButtonLabel(paymentMethod, isPaid = false) {
    if (isPaid) {
      const displayLabel = getPaymentMethodDisplayLabel(paymentMethod);
      return displayLabel ? `Paid with ${displayLabel}` : 'Paid';
    }

    const labelMap = {
      'MobilePay': 'Mark paid with MobilePay',
      'Card': 'Mark paid by card',
      'CardAutopay': 'Proceed to payment',
      'Invoice': 'Send invoice per mail',
      'Invoice (mail)': 'Send invoice by mail',
      'Invoice (EAN)': 'Submit e-invoice',
      'Split billing (mail)': 'Send split invoice by mail',
      'Split billing (EAN)': 'Submit split e-invoice',
      'Bank Transfer': 'Mark paid by transfer',
      'Cash': 'Mark paid by cash',
    };
    const label = labelMap[paymentMethod] || 'Select Pay Method';
    console.log('[PAYMENT] Button label for method:', paymentMethod, '=', label);
    return label;
  }

  function getSplitBillingPatientPaymentButtonLabel(paymentMethod) {
    const displayLabel = getPaymentMethodDisplayLabel(paymentMethod);
    return displayLabel ? `Collect patient share with ${displayLabel}` : 'Collect patient share now';
  }

  const closeStripePaymentDialog = useCallback(() => {
    setStripePaymentDialogOpen(false);
    setStripePaymentDialogStage('idle');
    setStripePaymentClientSecret('');
    setStripePaymentInvoice(null);
    setStripePaymentQuote(null);
    setStripePaymentFlowMode('default');
    setStripePaymentConfirmation(null);
    setStripePaymentMethods([]);
    setStripePaymentSelectedMethodId('');
    setStripePaymentRememberCard(true);
    setStripePaymentMakeDefaultCard(true);
    setStripePaymentWorking(false);
    setStripePaymentDialogError('');
  }, []);

  const openPatientPresentStripeCheckout = useCallback((amountOverride = null) => {
    const fallbackAmount = selectedBookingInvoice?.total_amount || selectedBooking?.booked_price || 0;
    const parsedAmountOverride = parseOptionalNumber(amountOverride);
    const amount = parsedAmountOverride != null && parsedAmountOverride > 0 ? parsedAmountOverride : fallbackAmount;
    const currency = selectedBookingInvoice?.currency || selectedBooking?.booked_currency || 'EUR';

    setStripePaymentFlowMode('patient_present_checkout');
    setStripePaymentInvoice(selectedBookingInvoice || null);
    setStripePaymentQuote({
      consultation_amount: amount,
      fee_percent: 0,
      fee_amount: 0,
      charge_amount: amount,
      application_fee_amount: 0,
      display_total_amount: amount,
      fee_display_mode: 'patient_present_checkout',
      currency,
    });
    setStripePaymentConfirmation(null);
    setStripePaymentClientSecret('');
    setStripePaymentMethods([]);
    setStripePaymentSelectedMethodId('');
    setStripePaymentRememberCard(false);
    setStripePaymentMakeDefaultCard(false);
    setStripePaymentWorking(false);
    setStripePaymentDialogError('');
    setStripePaymentDialogStage('overview');
    setStripePaymentDialogOpen(true);
  }, [selectedBooking, selectedBookingInvoice]);

  async function waitForStripeInvoiceConfirmation(invoiceId, paymentIntentId = '', maxAttempts = 12, delayMs = 2000, allowPartialConfirmation = false) {
    if (!invoiceId) return false;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      try {
        const response = await fetch(`${backendBaseUrl}/api/stripe/payment-status/`, {
          method: 'POST',
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            invoice_ids: [invoiceId],
            payment_intent_id: paymentIntentId || undefined,
          }),
        });

        if (response.ok) {
          const responseData = await response.json();
          const invoiceStatus = responseData.statuses ? responseData.statuses[String(invoiceId)] : null;
          if (responseData.all_paid || (allowPartialConfirmation && ['partially_paid', 'paid'].includes(invoiceStatus))) {
            return true;
          }
        }
      } catch (error) {
        console.error('[PAYMENT] Confirmation check failed:', error);
      }

      await new Promise((resolve) => window.setTimeout(resolve, delayMs));
    }

    return false;
  }

  const handleStripePaymentSuccess = useCallback(async (paymentIntent) => {
    const invoiceId = stripePaymentInvoice?.id;
    let confirmed = true;
    const invoiceAmount = Number(stripePaymentInvoice?.total_amount ?? selectedBookingInvoice?.total_amount ?? selectedBooking?.booked_price ?? 0);
    const chargeAmount = Number(stripePaymentQuote?.charge_amount ?? invoiceAmount);
    const allowPartialConfirmation = Boolean(chargeAmount > 0 && invoiceAmount > 0 && chargeAmount < invoiceAmount);

    if (invoiceId) {
      confirmed = await waitForStripeInvoiceConfirmation(invoiceId, paymentIntent?.id || '', 12, 2000, allowPartialConfirmation);
    }

    await fetchBookings();
    setStripePaymentConfirmation({
      confirmed,
      invoiceId,
      invoiceNumber: stripePaymentInvoice?.invoice_number || selectedBookingInvoice?.invoice_number || '',
      amount: stripePaymentQuote?.charge_amount ?? stripePaymentInvoice?.total_amount ?? selectedBooking?.booked_price ?? 0,
      currency: stripePaymentQuote?.currency || stripePaymentInvoice?.currency || selectedBooking?.booked_currency || 'EUR',
      timestamp: new Date().toISOString(),
    });
    setStripePaymentDialogError('');
    setStripePaymentDialogStage('confirmation');
  }, [fetchBookings, selectedBooking, selectedBookingInvoice, stripePaymentInvoice, stripePaymentQuote, waitForStripeInvoiceConfirmation]);

  const handleFinalizeStripePaymentFlow = useCallback(() => {
    const confirmed = stripePaymentConfirmation?.confirmed !== false;
    closeStripePaymentDialog();
    setSnackbarMessage(
      confirmed
        ? 'Stripe payment completed successfully'
        : 'Stripe payment was accepted, but backend confirmation is still syncing'
    );
    setSnackbarSeverity(confirmed ? 'success' : 'warning');
    setSnackbarOpen(true);
    handleDialogClose();
  }, [closeStripePaymentDialog, handleDialogClose, stripePaymentConfirmation]);

  const handleCloseStripePaymentOverlay = useCallback(() => {
    if (stripePaymentDialogStage === 'confirmation') {
      handleFinalizeStripePaymentFlow();
      return;
    }

    closeStripePaymentDialog();
  }, [closeStripePaymentDialog, handleFinalizeStripePaymentFlow, stripePaymentDialogStage]);

  const submitStripeBillingAction = useCallback(async (extraPayload = {}) => {
    const response = await fetch(`${backendBaseUrl}/api/calendar/appointments/${selectedBooking.id}/billing-action/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Token ${token}`,
      },
      body: JSON.stringify({
        payment_method: form.payment_method,
        receiver_address: billingReceiverAddress,
        ...extraPayload,
      }),
    });

    const responseData = await response.json().catch(() => ({}));
    return { response, responseData };
  }, [billingReceiverAddress, form.payment_method, selectedBooking, token]);

  const hydrateStripeDialogFromResponse = useCallback((responseData) => {
    setStripePaymentInvoice(responseData.invoice || null);
    setStripePaymentQuote(responseData.payment_quote || null);
    setStripePaymentMethods(Array.isArray(responseData.payment_methods) ? responseData.payment_methods : []);
    setStripePaymentSelectedMethodId(
      responseData.selected_payment_method_id
      || responseData.default_payment_method?.stripe_payment_method_id
      || (Array.isArray(responseData.payment_methods) && responseData.payment_methods.length > 0 ? responseData.payment_methods[0].stripe_payment_method_id : '')
    );
    setStripePaymentClientSecret(responseData.payment_intent_client_secret || '');
    setStripePaymentDialogError('');
    setStripePaymentDialogOpen(true);

    if (responseData.next_step === 'review_saved_cards') {
      setStripePaymentDialogStage('review_saved_cards');
      return;
    }

    if (responseData.next_step === 'enter_new_card') {
      setStripePaymentDialogStage('new_card_form');
      return;
    }

    if (responseData.next_step === 'confirm_card_entry' && responseData.payment_intent_client_secret) {
      setStripePaymentDialogStage('new_card_form');
    }
  }, []);

  const handleConfirmSavedCard = useCallback(async () => {
    if (!stripePaymentSelectedMethodId) {
      setStripePaymentDialogError('Select a saved card first');
      return;
    }

    setStripePaymentWorking(true);
    setStripePaymentDialogError('');

    try {
      const { response, responseData } = await submitStripeBillingAction({
        card_flow_action: 'confirm_saved_card',
        payment_method_id: stripePaymentSelectedMethodId,
        ...(stripePaymentFlowMode === 'patient_present_checkout' ? {
          payment_flow_mode: stripePaymentFlowMode,
          amount: stripePaymentQuote?.charge_amount,
        } : {}),
      });

      if (response.ok && responseData.success) {
        await handleStripePaymentSuccess({ id: responseData.payment_intent_id });
        return;
      }

      const errorMessage = responseData.error || responseData.detail || 'Saved card payment failed';
      setStripePaymentDialogError(errorMessage);
    } catch (error) {
      setStripePaymentDialogError(`Saved card payment failed: ${error.message}`);
    } finally {
      setStripePaymentWorking(false);
    }
  }, [handleStripePaymentSuccess, stripePaymentFlowMode, stripePaymentQuote?.charge_amount, submitStripeBillingAction, stripePaymentSelectedMethodId]);

  const handlePrepareNewCard = useCallback(async () => {
    setStripePaymentWorking(true);
    setStripePaymentDialogError('');

    try {
      const { response, responseData } = await submitStripeBillingAction({
        card_flow_action: 'prepare_new_card',
        remember_payment_method: stripePaymentRememberCard,
        make_default_payment_method: stripePaymentRememberCard && stripePaymentMakeDefaultCard,
        ...(stripePaymentFlowMode === 'patient_present_checkout' ? {
          payment_flow_mode: stripePaymentFlowMode,
          amount: stripePaymentQuote?.charge_amount,
        } : {}),
      });

      if (response.ok && responseData.payment_intent_client_secret) {
        hydrateStripeDialogFromResponse({
          ...responseData,
          next_step: 'confirm_card_entry',
        });
        setStripePaymentDialogStage('new_card_form');
        return;
      }

      const errorMessage = responseData.error || responseData.detail || 'Unable to prepare card payment';
      setStripePaymentDialogError(errorMessage);
    } catch (error) {
      setStripePaymentDialogError(`Unable to prepare card payment: ${error.message}`);
    } finally {
      setStripePaymentWorking(false);
    }
  }, [hydrateStripeDialogFromResponse, stripePaymentFlowMode, stripePaymentQuote?.charge_amount, submitStripeBillingAction, stripePaymentMakeDefaultCard, stripePaymentRememberCard]);

  const handleSplitBillingPatientPayment = async () => {
    if (!selectedBooking?.id) {
      setSnackbarMessage('Save the booking before collecting the patient share');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (!isInsuranceSplitBilling || selectedSplitBillingPatientAmount == null || selectedSplitBillingPatientAmount <= 0) {
      setSnackbarMessage('No patient share is available for split billing');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (!form.split_payment_method) {
      setSnackbarMessage('Select a patient payment method first');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (form.split_payment_method === 'CardAutopay') {
      openPatientPresentStripeCheckout(selectedSplitBillingPatientAmount);
      return;
    }

    setIsLoading(true);

    try {
      const { response, responseData } = await submitStripeBillingAction({
        payment_method: form.split_payment_method,
        amount: selectedSplitBillingPatientAmount,
      });

      if (response.ok) {
        await fetchBookings();
        setSnackbarMessage(`Patient share recorded with ${getPaymentMethodDisplayLabel(form.split_payment_method)}`);
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        handleDialogClose();
        return;
      }

      const errorMessage = responseData.error || responseData.detail || 'Failed to record patient share';
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage(`Failed to record patient share: ${error.message}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAccreditPaidBooking = async () => {
    if (!selectedBooking?.id) {
      setSnackbarMessage('Save the booking before accrediting a payment');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (!selectedBookingInvoice?.id) {
      setSnackbarMessage('No invoice was found for this paid booking');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    setAccreditDialogOpen(true);
  };

  const handleCloseAccreditDialog = () => {
    if (isLoading) {
      return;
    }

    setAccreditDialogOpen(false);
    setAccreditPatientHasPaidBalance(false);
    setAccreditTransferToPatientBalance(false);
  };

  const handleConfirmAccreditPaidBooking = async () => {
    if (!selectedBooking?.id || !selectedBookingInvoice?.id) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${backendBaseUrl}/api/calendar/billing-actions/invoice-bulk/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
        },
        body: JSON.stringify({
          invoice_ids: [selectedBookingInvoice.id],
          action: 'accredit',
          patient_has_paid_balance: accreditPatientHasPaidBalance,
          transfer_to_patient_balance: accreditTransferToPatientBalance,
        }),
      });

      const responseData = await response.json().catch(() => ({}));

      if (response.ok && responseData.success) {
        await fetchBookings();
        setSnackbarMessage(
          accreditTransferToPatientBalance
            ? `Paid with ${selectedBookingPaymentMethodLabel}. Credit invoice created and kept as patient balance.`
            : `Paid with ${selectedBookingPaymentMethodLabel}. Credit invoice created and the bill was reopened as unpaid.`
        );
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        setAccreditDialogOpen(false);
        setAccreditPatientHasPaidBalance(false);
        setAccreditTransferToPatientBalance(false);
        handleDialogClose();
        return;
      }

      const errorMessage = responseData.error || responseData.detail || 'Failed to accredit payment';
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage(`Failed to accredit payment: ${error.message}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaymentMethodAction = async () => {
    if (!selectedBooking?.id) {
      setSnackbarMessage('Save the booking before sending an invoice or recording payment');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (isSelectedBookingPaid) {
      await handleAccreditPaidBooking();
      return;
    }

    if (!form.payment_method) {
      setSnackbarMessage('Select a payment method first');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (form.payment_method === 'CardAutopay' && !isStripeAutopayAvailable) {
      setSnackbarMessage(stripeAutopayDisabledReason || 'Card (stripe) is not available for this appointment');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    if (form.payment_method === 'CardAutopay') {
      openPatientPresentStripeCheckout();
      return;
    }

    setIsLoading(true);

    try {
      const { response, responseData } = await submitStripeBillingAction();

      if (response.ok) {
        // console.log('[PAYMENT] Billing action success:', responseData);

        if (responseData.next_step === 'stripe_setup_required') {
          setSnackbarMessage(responseData.error || 'Card (stripe) is not available for this appointment');
          setSnackbarSeverity('warning');
          setSnackbarOpen(true);
          return;
        }

        if (responseData.next_step === 'review_saved_cards' || responseData.next_step === 'enter_new_card' || responseData.next_step === 'confirm_card_entry') {
          hydrateStripeDialogFromResponse(responseData);
          if (responseData.next_step === 'review_saved_cards') {
            setStripePaymentDialogStage('review_saved_cards');
          } else if (responseData.next_step === 'enter_new_card') {
            await handlePrepareNewCard();
            return;
          } else {
            setStripePaymentDialogStage('new_card_form');
          }
        } else if (responseData.action === 'send_invoice_per_mail') {
          setSnackbarMessage(responseData.email_sent === false
            ? (responseData.warning || 'Invoice created, but the email could not be sent')
            : 'Invoice sent by email');
          setSnackbarSeverity(responseData.email_sent === false ? 'warning' : 'success');
          handleDialogClose();
        } else if (responseData.action === 'submit_invoice_per_ean') {
          setSnackbarMessage(responseData.warning || 'Invoice submitted by EAN');
          setSnackbarSeverity(responseData.warning ? 'warning' : 'success');
          handleDialogClose();
        } else if (responseData.action === 'stripe_payment') {
          await fetchBookings();
          setSnackbarMessage('Stripe payment completed successfully');
          handleDialogClose();
        } else {
          await fetchBookings();
          setSnackbarMessage(`Payment recorded with ${getPaymentMethodButtonLabel(form.payment_method)}`);
          handleDialogClose();
        }

        if (responseData.action === 'send_invoice_per_mail' || responseData.action === 'submit_invoice_per_ean' || responseData.action === 'stripe_payment' || responseData.action !== 'stripe_payment') {
          setSnackbarSeverity((responseData.action === 'send_invoice_per_mail' && responseData.email_sent === false) || responseData.warning ? 'warning' : 'success');
          if (responseData.next_step !== 'review_saved_cards' && responseData.next_step !== 'enter_new_card' && responseData.next_step !== 'confirm_card_entry') {
            setSnackbarOpen(true);
          }
        }
      } else {
        const errorMessage = responseData.error || responseData.detail || 'Billing action failed';
        console.error('[PAYMENT] Billing action error:', responseData);
        setSnackbarMessage(errorMessage);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('[PAYMENT] Billing action exception:', error);
      setSnackbarMessage(`Billing action failed: ${error.message}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const openTimeAdjust = useCallback((booking, anchorEl) => {
    const startDate = new Date(booking.start_datetime);
    const endDate = new Date(booking.end_datetime);
    setTimeAdjustStart(`${String(startDate.getHours()).padStart(2, '0')}:${String(startDate.getMinutes()).padStart(2, '0')}`);
    setTimeAdjustEnd(`${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`);
    setTimeAdjustBooking(booking);
    setTimeAdjustAnchor(anchorEl);
  }, []);

  const closeTimeAdjust = useCallback(() => {
    setTimeAdjustAnchor(null);
    setTimeAdjustBooking(null);
    setTimeAdjustSaving(false);
  }, []);

  const adjustTimeAdjust = useCallback((field, minutes) => {
    const setter = field === 'start' ? setTimeAdjustStart : setTimeAdjustEnd;
    setter(prev => {
      const [h, m] = (prev || '00:00').split(':').map(Number);
      let total = h * 60 + m + minutes;
      if (total < 0) total = 0;
      if (total >= 24 * 60) total = 23 * 60 + 55;
      return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
    });
  }, []);

  const handleSaveTimeAdjust = useCallback(async () => {
    if (!timeAdjustBooking) return;
    setTimeAdjustSaving(true);
    const dateStr = new Date(timeAdjustBooking.start_datetime).toISOString().split('T')[0];
    const startUtc = moment(`${dateStr}T${timeAdjustStart}`).utc().format('YYYY-MM-DDTHH:mm:ss') + 'Z';
    const endUtc = moment(`${dateStr}T${timeAdjustEnd}`).utc().format('YYYY-MM-DDTHH:mm:ss') + 'Z';
    const duration = moment(`${dateStr}T${timeAdjustEnd}`).diff(moment(`${dateStr}T${timeAdjustStart}`), 'minutes');
    try {
      const response = await fetch(
        `${backendBaseUrl}/api/calendar/appointments/${timeAdjustBooking.id}/`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Token ${token}` },
          body: JSON.stringify({ start_datetime: startUtc, end_datetime: endUtc, duration_minutes: duration }),
        }
      );
      if (response.ok) {
        await fetchBookings();
        setSnackbarMessage('Appointment time updated');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        closeTimeAdjust();
      } else {
        const err = await response.json().catch(() => ({}));
        setSnackbarMessage('Failed to update: ' + (err.detail || response.status));
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (e) {
      setSnackbarMessage('Error: ' + e.message);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setTimeAdjustSaving(false);
    }
  }, [timeAdjustBooking, timeAdjustStart, timeAdjustEnd, token, closeTimeAdjust]);

  // Custom event component — detects long press (600ms hold without drag) to open time-adjust popover
  const EventComponent = useMemo(() => {
    return ({ event }) => {
      if (event.isSickDayOverlaySlot) {
        return null;
      }

      const handleMouseDown = (e) => {
        if (event.isClassService) {
          return;
        }
        longPressMovedRef.current = false;
        longPressTargetRef.current = e.currentTarget;
        longPressTimerRef.current = setTimeout(() => {
          if (!longPressMovedRef.current && event.resource && !event.isSickDayAbsence) {
            openTimeAdjust(event.resource, longPressTargetRef.current);
          }
        }, 600);
      };
      const cancelLongPress = () => {
        longPressMovedRef.current = true;
        clearTimeout(longPressTimerRef.current);
      };
      return (
        <div
          style={{
            height: '100%',
            position: 'relative',
            overflow: 'hidden',
            userSelect: 'none',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 2,
            color: '#002D55',
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={cancelLongPress}
          onMouseUp={() => clearTimeout(longPressTimerRef.current)}
          onTouchStart={(e) => {
            if (event.isClassService) {
              return;
            }
            longPressMovedRef.current = false;
            longPressTargetRef.current = e.currentTarget;
            longPressTimerRef.current = setTimeout(() => {
              if (!longPressMovedRef.current && event.resource && !event.isSickDayAbsence) {
                openTimeAdjust(event.resource, longPressTargetRef.current);
              }
            }, 600);
          }}
          onTouchMove={cancelLongPress}
          onTouchEnd={() => clearTimeout(longPressTimerRef.current)}
        >
          {event.isAllocationWindow ? (
            <>
              <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.72rem', fontWeight: 800, lineHeight: 1.15 }}>
                {event.allocationStaffLabel || 'Staff allocation'}
              </span>
              <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.66rem', fontWeight: 700, lineHeight: 1.15 }}>
                {event.allocationRoomLabel || 'Room'}
              </span>
              <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.64rem', lineHeight: 1.15 }}>
                {event.allocationTimeLabel || event.title}
              </span>
              {event.allocationStatusLabel ? (
                <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.6rem', lineHeight: 1.15, opacity: 0.86 }}>
                  {event.allocationStatusLabel}
                </span>
              ) : null}
            </>
          ) : (
            <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 'inherit' }}>
              {event.title}
            </span>
          )}
        </div>
      );
    };
  }, [openSickDayCancelDialog, openTimeAdjust]);

  const BackgroundEventWrapper = useMemo(() => {
    return ({ event, children }) => {
      if (!event?.isSickDayOverlaySlot) {
        return children;
      }

      return (
        <div
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            openSickDayCancelDialog(event.resource || event);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              openSickDayCancelDialog(event.resource || event);
            }
          }}
          style={{ height: '100%', width: '100%' }}
        >
          {children}
        </div>
      );
    };
  }, [openSickDayCancelDialog]);

  // Keep toolbar state in a ref so the memoized component always reads fresh values
  // without needing to be re-created (which would cause react-big-calendar to reset its internal date).
  const toolbarStateRef = useRef({});
  toolbarStateRef.current = {
    resourceViewMode, setResourceViewMode,
    selectedPractitioner, setSelectedPractitioner,
    selectedLocation, setSelectedLocation,
    selectedRoom, setSelectedRoom,
    selectedCalendar, setSelectedCalendar,
    toolbarRoomFilterIds, setToolbarRoomFilterIds,
    toolbarStaffFilterIds, setToolbarStaffFilterIds,
    clinicLocations, selectedLocationRooms, toolbarLocationCalendars, selectedLocationData,
    calendarDate, setCalendarDate,
    showWeekendDays, setShowWeekendDays,
    practitioners: availablePractitioners, classes, abbreviateName,
  };

  // Stable component reference — never re-created, reads latest state via ref
  const CalendarToolbar = useMemo(() => {
    const Toolbar = ({ label, onNavigate, onView, view, views }) => {
      const s = toolbarStateRef.current;
      const [filterMenuAnchorEl, setFilterMenuAnchorEl] = useState(null);
      const [staffSelectOpen, setStaffSelectOpen] = useState(false);
      const isMdAndDown = useMediaQuery('(max-width: 1024px)');

      const handleViewClick = (viewName) => {
        const hasStaffSelected = Boolean(s.selectedPractitioner) || (Array.isArray(s.toolbarStaffFilterIds) && s.toolbarStaffFilterIds.length > 0);
        const nextWeekView = s.showWeekendDays ? Views.WEEK : Views.WORK_WEEK;
        const normalizeToWeekStart = () => {
          s.setCalendarDate(moment(s.calendarDate || new Date()).startOf('week').toDate());
        };

        if ((viewName === Views.WEEK || viewName === Views.WORK_WEEK) && !hasStaffSelected) {
          suppressStaffWeekAutoSwitchRef.current = false;
          setStaffSelectOpen(true);
          return;
        }

        if (viewName === Views.WEEK || viewName === Views.WORK_WEEK) {
          if (view === Views.DAY) {
            normalizeToWeekStart();
          }
          onView(nextWeekView);
          return;
        }

        onView(viewName);
      };

      const compactCurrentDateLabel = useMemo(() => {
        if (view !== 'day' || !isMdAndDown || !s.calendarDate) {
          return label;
        }

        const dateMoment = moment(s.calendarDate);
        if (!dateMoment.isValid()) {
          return label;
        }

        return dateMoment.format('ddd D/M');
      }, [isMdAndDown, label, s.calendarDate]);

      return (
        <div
          className="rbc-toolbar"
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'nowrap',
            overflowX: 'auto',
            overflowY: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          {/* Views follow the location and staff selectors; date navigation stays centered. */}
          <Box display="flex" alignItems="center" style={{ gap: 6, zIndex: 1, flexShrink: 0 }}>
            <span className="rbc-btn-group" style={{ order: 1 }}>
              {views.map((viewName) => {
                const labelText = viewName === Views.DAY
                  ? 'Day'
                  : viewName === Views.WEEK || viewName === Views.WORK_WEEK
                    ? 'Week'
                    : viewName === Views.MONTH
                      ? 'Month'
                      : viewName.charAt(0).toUpperCase() + viewName.slice(1);
                return (
                  <button key={viewName} type="button" className={view === viewName ? 'rbc-active' : ''} onClick={() => handleViewClick(viewName)}>
                    {labelText}
                  </button>
                );
              })}
            </span>
            <span className="rbc-btn-group" style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', zIndex: 2 }}>
              <button type="button" onClick={() => onNavigate('TODAY')}>Today</button>
              <button type="button" onClick={() => onNavigate('PREV')}>&lt;</button>
              <span
                className="rbc-toolbar-label"
                style={{
                  pointerEvents: 'none',
                  zIndex: 0,
                  flexShrink: 0,
                  margin: '0 clamp(8px, 1vw, 18px)',
                }}
              >
                {compactCurrentDateLabel}
              </span>
              <button type="button" onClick={() => onNavigate('NEXT')}>&gt;</button>
            </span>
          </Box>

          {/* Left: clinic location and staff selectors. */}
          <Box display="flex" alignItems="center" style={{ order: -1, gap: 6, zIndex: 1, flexShrink: 0, marginRight: 6 }}>
            {s.clinicLocations.length > 0 && (
              <Select
                disableUnderline
                displayEmpty
                label="Location"
                value={s.selectedLocation}
                onChange={(e) => {
                  s.setSelectedLocation(e.target.value);
                  s.setSelectedRoom('');
                  s.setToolbarRoomFilterIds([]);
                  s.setToolbarStaffFilterIds([]);
                }}
                className={s.classes.orangeSelect}
                MenuProps={{ getContentAnchorEl: null, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, PaperProps: { style: { borderRadius: 14, marginTop: 8 } }, classes: { list: s.classes.dialogMenuList } }}
              >
                {s.clinicLocations.map((location) => (
                  <MenuItem key={location.id} value={location.id} className={s.classes.staffHeaderMenuItem}>
                    {location.name}
                  </MenuItem>
                ))}
              </Select>
            )}
            {/*{s.selectedLocation && (
              <Select
                disableUnderline
                displayEmpty
                value={s.selectedRoom}
                onChange={(e) => {
                  s.setSelectedRoom(e.target.value);
                  setFilterMenuAnchorEl(null);
                }}
                className={s.classes.orangeSelect}
                MenuProps={{ getContentAnchorEl: null, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, PaperProps: { style: { borderRadius: 14, marginTop: 8 } }, classes: { list: s.classes.dialogMenuList } }}
              >
                <MenuItem value="" className={s.classes.staffHeaderMenuItem}>All Rooms</MenuItem>
                {s.selectedLocationRooms.map((room) => (
                  <MenuItem key={room.id} value={room.id} className={s.classes.staffHeaderMenuItem}>
                    {room.name}
                  </MenuItem>
                ))}
              </Select>
            )}*/}
            <Select
              disableUnderline
              displayEmpty
              value={s.selectedPractitioner}
              open={staffSelectOpen}
              onOpen={() => setStaffSelectOpen(true)}
              onClose={() => setStaffSelectOpen(false)}
              onChange={(e) => {
                s.setSelectedPractitioner(e.target.value);
                setStaffSelectOpen(false);
                setFilterMenuAnchorEl(null);
              }}
              className={s.classes.orangeSelect}
              MenuProps={{ getContentAnchorEl: null, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, PaperProps: { style: { borderRadius: 14, marginTop: 8 } }, classes: { list: s.classes.dialogMenuList } }}
            >
              <MenuItem value="" className={s.classes.staffHeaderMenuItem}>All Staff</MenuItem>
              {s.practitioners.map((practitioner) => (
                <MenuItem key={practitioner.id} value={practitioner.id} className={s.classes.staffHeaderMenuItem}
                  style={{ fontWeight: s.selectedPractitioner === practitioner.id ? 700 : 500 }}>
                  {s.abbreviateName(practitioner.name)}
                </MenuItem>
              ))}
            </Select>
          </Box>
          {/* Right: resource display and filter controls. */}
          <Box display="flex" alignItems="center" style={{ marginLeft: 'auto', gap: 6, zIndex: 1, flexShrink: 0 }}>
            {view === 'day' && (
              <span className="rbc-btn-group">
                <button
                  type="button"
                  className={s.resourceViewMode === 'rooms' ? 'rbc-active' : ''}
                    onClick={() => {
                      suppressStaffWeekAutoSwitchRef.current = false;
                      s.setResourceViewMode('rooms');
                    }}
                >
                  Rooms
                </button>
                <button
                  type="button"
                  className={s.resourceViewMode === 'staff' ? 'rbc-active' : ''}
                    onClick={() => {
                      suppressStaffWeekAutoSwitchRef.current = view === 'day';
                      s.setResourceViewMode('staff');
                    }}
                >
                  Staff
                </button>
              </span>
            )}
            <IconButton
              size="small"
              onClick={(event) => setFilterMenuAnchorEl(event.currentTarget)}
              style={{ color: '#FF7F27', border: '1px solid rgba(255, 127, 39, 0.24)', background: '#fff' }}
              aria-label="Open filters"
            >
              <FilterListIcon fontSize="small" />
            </IconButton>
            <Popover
              open={Boolean(filterMenuAnchorEl)}
              anchorEl={filterMenuAnchorEl}
              onClose={() => setFilterMenuAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ style: { borderRadius: 16, width: 320, marginTop: 10, padding: 16, boxShadow: '0 14px 40px rgba(15, 23, 42, 0.16)' } }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" style={{ gap: 12, marginBottom: 12 }}>
                <Typography style={{ fontWeight: 700, color: '#002D55' }}>Filter resources</Typography>
                <Button
                  size="small"
                  onClick={() => {
                    s.setToolbarRoomFilterIds([]);
                    s.setToolbarStaffFilterIds([]);
                    s.setSelectedCalendar('');
                    s.setShowWeekendDays(false);
                  }}
                  style={{ color: '#e53935' }}
                >
                  Clear all
                </Button>
              </Box>

              <Box style={{ marginBottom: 12 }}>
                <Typography variant="caption" style={{ color: '#6f7b87', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  Added Calendars
                </Typography>
                <Typography variant="body2" style={{ color: '#6f7b87', marginTop: 4 }}>
                  Pre-defined staff calendar groups from Locations &amp; Rooms.
                </Typography>
                <Box display="flex" flexDirection="column" style={{ marginTop: 8, gap: 8, maxHeight: 180, overflowY: 'auto' }}>
                  {Array.isArray(s.toolbarLocationCalendars) && s.toolbarLocationCalendars.length > 0 ? s.toolbarLocationCalendars.map((calendar) => {
                    const calendarStaffIds = Array.isArray(calendar.allowed_staff_ids)
                      ? calendar.allowed_staff_ids.map((staffId) => String(staffId)).filter(Boolean)
                      : Array.isArray(calendar.allowed_staff_detail)
                        ? calendar.allowed_staff_detail.map((staff) => String(staff.id)).filter(Boolean)
                        : [];
                    const activeStaffFilterIds = Array.isArray(s.toolbarStaffFilterIds)
                      ? s.toolbarStaffFilterIds.map((staffId) => String(staffId)).filter(Boolean)
                      : [];
                    const isCalendarPresetActive = String(s.selectedCalendar || '') === String(calendar.id)
                      && calendarStaffIds.length === activeStaffFilterIds.length
                      && calendarStaffIds.every((staffId) => activeStaffFilterIds.includes(staffId));

                    return (
                      <Button
                        key={calendar.id}
                        fullWidth
                        variant="outlined"
                        onClick={() => {
                          s.setSelectedCalendar(String(calendar.id));
                          s.setSelectedPractitioner('');
                          s.setToolbarStaffFilterIds(calendarStaffIds);
                          setFilterMenuAnchorEl(null);
                        }}
                        style={{
                          justifyContent: 'flex-start',
                          alignItems: 'flex-start',
                          textTransform: 'none',
                          borderRadius: 12,
                          padding: '8px 12px',
                          borderColor: isCalendarPresetActive ? '#FF7F27' : 'rgba(0, 45, 85, 0.14)',
                          backgroundColor: isCalendarPresetActive ? 'rgba(255, 127, 39, 0.08)' : '#fff',
                          color: '#002D55',
                        }}
                      >
                        <Box display="flex" flexDirection="column" alignItems="flex-start" style={{ width: '100%', gap: 2 }}>
                          <Typography style={{ fontWeight: 700, color: '#002D55', lineHeight: 1.2 }}>
                            {calendar.name || 'Calendar group'}
                          </Typography>
                          <Typography variant="caption" style={{ color: '#6f7b87' }}>
                            {calendarStaffIds.length > 0
                              ? `${calendarStaffIds.length} staff member${calendarStaffIds.length === 1 ? '' : 's'}`
                              : 'No staff assigned'}
                          </Typography>
                        </Box>
                      </Button>
                    );
                  }) : (
                    <Typography variant="body2" style={{ color: '#6f7b87', marginTop: 4 }}>
                      No calendars available for this location.
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box style={{ marginBottom: 12 }}>
                <Typography variant="caption" style={{ color: '#6f7b87', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  Rooms
                </Typography>
                <Box display="flex" flexDirection="column" style={{ marginTop: 6, maxHeight: 180, overflowY: 'auto' }}>
                  {s.selectedLocationRooms.length > 0 ? s.selectedLocationRooms.map((room) => (
                    <FormControlLabel
                      key={room.id}
                      control={(
                        <Checkbox
                          checked={s.toolbarRoomFilterIds.includes(String(room.id))}
                          onChange={() => {
                            s.setToolbarRoomFilterIds((prev) => {
                              const nextValue = String(room.id);
                              const current = Array.isArray(prev) ? prev.map((item) => String(item)) : [];
                              return current.includes(nextValue)
                                ? current.filter((item) => item !== nextValue)
                                : [...current, nextValue];
                            });
                          }}
                          style={{ color: '#FF7F27' }}
                        />
                      )}
                      label={room.name}
                      style={{ marginRight: 0 }}
                    />
                  )) : (
                    <Typography variant="body2" style={{ color: '#6f7b87', marginTop: 4 }}>No rooms available for this location.</Typography>
                  )}
                </Box>
              </Box>

              <Box style={{ marginBottom: 12 }}>
                <Typography variant="caption" style={{ color: '#6f7b87', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  Calendar days
                </Typography>
                <FormControlLabel
                  style={{ marginTop: 4, marginRight: 0, alignItems: 'flex-start' }}
                  control={(
                    <Checkbox
                      checked={s.showWeekendDays}
                      onChange={(event) => {
                        const nextValue = event.target.checked;
                        s.setShowWeekendDays(nextValue);
                        if (s.resourceViewMode === 'staff' && (s.calendarView === Views.WEEK || s.calendarView === Views.WORK_WEEK)) {
                          onView(nextValue ? Views.WEEK : Views.WORK_WEEK);
                        }
                      }}
                      style={{ color: '#FF7F27' }}
                    />
                  )}
                  label={(
                    <Box display="flex" flexDirection="column" style={{ marginTop: 2 }}>
                      <Typography style={{ fontWeight: 600, color: '#002D55', lineHeight: 1.2 }}>
                        Show weekend days
                      </Typography>
                      <Typography variant="caption" style={{ color: '#6f7b87' }}>
                        Include Sunday and Saturday in week schedule view.
                      </Typography>
                    </Box>
                  )}
                />
              </Box>

              <Box>
                <Typography variant="caption" style={{ color: '#6f7b87', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  Staff
                </Typography>
                <Box display="flex" flexDirection="column" style={{ marginTop: 6, maxHeight: 180, overflowY: 'auto' }}>
                  {(s.selectedLocationData?.staff_members_detail || s.practitioners).length > 0 ? (s.selectedLocationData?.staff_members_detail || s.practitioners).map((staff) => (
                    <FormControlLabel
                      key={staff.id}
                      control={(
                        <Checkbox
                          checked={s.toolbarStaffFilterIds.includes(String(staff.id))}
                          onChange={() => {
                            s.setToolbarStaffFilterIds((prev) => {
                              const nextValue = String(staff.id);
                              const current = Array.isArray(prev) ? prev.map((item) => String(item)) : [];
                              return current.includes(nextValue)
                                ? current.filter((item) => item !== nextValue)
                                : [...current, nextValue];
                            });
                          }}
                          style={{ color: '#FF7F27' }}
                        />
                      )}
                      label={s.abbreviateName(staff.name || `${staff.first_name || ''} ${staff.last_name || ''}`.trim() || staff.email || 'Unnamed staff')}
                      style={{ marginRight: 0 }}
                    />
                  )) : (
                    <Typography variant="body2" style={{ color: '#6f7b87', marginTop: 4 }}>No staff available for this location.</Typography>
                  )}
                </Box>
              </Box>
            </Popover>
          </Box>
        </div>
      );
    };
    return Toolbar;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Stable components object for react-big-calendar — prevents internal state reset on re-renders
  const calendarComponents = useMemo(() => ({
    toolbar: CalendarToolbar,
    event: EventComponent,
    backgroundEventWrapper: BackgroundEventWrapper,
    header: CalendarHeader,
    resourceHeader: ResourceHeader,
  }), [BackgroundEventWrapper, CalendarHeader, CalendarToolbar, EventComponent, ResourceHeader]);

  const calendarGridViews = useMemo(() => (
    showWeekendDays ? [Views.MONTH, Views.WEEK, Views.DAY] : [Views.MONTH, Views.WORK_WEEK, Views.DAY]
  ), [showWeekendDays]);

  const calendarGridView = showWeekendDays && calendarView === Views.WORK_WEEK
    ? Views.WEEK
    : !showWeekendDays && calendarView === Views.WEEK
      ? Views.WORK_WEEK
      : calendarView;

  return (
    <div className={classes.root} {...rest}>
        <Card className={classes.card}>
          {tabIndex === 0 && (
            <>
            <CardHeader className={classes.cardHeaderRoot} title={<span className={classes.cardHeaderTitle}>Calendar</span>} />
            <CardContent className={classes.cardContent}>
              <BookingCalendarGrid
                calendarBodyClassName={classes.calendarBody}
                events={events}
                backgroundEvents={combinedBackgroundEvents}
                calendarComponents={calendarComponents}
                calendarView={calendarGridView}
                calendarViews={calendarGridViews}
                onView={handleCalendarViewChange}
                calendarDate={calendarDate}
                onNavigate={setCalendarDate}
                eventPropGetter={eventStyleGetter}
                backgroundEventPropGetter={calendarBackgroundEventPropGetter}
                tooltipAccessor={calendarEventTooltipAccessor}
                calendarFormats={calendarFormats}
                onSelectEvent={handleSelectEvent}
                onDoubleClickEvent={handleDoubleClickEvent}
                onSelectSlot={handleSelectSlot}
                onEventDrop={handleEventDrop}
                onEventResize={handleEventDrop}
                calendarResources={calendarResources}
              />
            </CardContent>
            </>
          )}
        </Card>
      {/* Booking Dialog */}
      <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="sm" fullWidth PaperProps={{ className: classes.dialogPaper }}>
                <DialogTitle className={classes.dialogHeader}>
                  <Box className={classes.dialogTitleRow}>
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        <span className={classes.dialogTitleText}>
                          {selectedBooking ? 'Edit Booking' : 'New Booking'}
                        </span>
                        {selectedBooking && isVideoConsultationBooking(selectedBooking) && (
                          <Tooltip title={selectedBookingVideoSession?.waiting_room ? 'Patient is waiting in the videocall' : 'Open videocall'}>
                            <IconButton
                              aria-label="Open videocall"
                              onClick={handleOpenSelectedBookingVideoCall}
                              className={classes.closeButton}
                              size="small"
                              style={{ padding: 6 }}
                            >
                              <Badge
                                color="secondary"
                                variant="dot"
                                overlap="circular"
                                invisible={!selectedBookingVideoSession?.waiting_room}
                              >
                                <VideoCallIcon fontSize="small" />
                              </Badge>
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    <Box className={classes.dialogTitleActions}>
                      <Box className={classes.dialogModeToggle}>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => handleFormChange({ target: { name: 'bookingMode', value: 'patient' } })}
                          style={{ fontWeight: form.bookingMode === 'patient' ? 600 : 600 }}
                          className={`${classes.dialogModeButton} ${classes.dialogModeButtonLeft} ${form.bookingMode === 'patient' ? classes.dialogModeButtonActive : classes.dialogModeButtonInactive}`}
                        >
                          Patient
                        </Button>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => handleFormChange({ target: { name: 'bookingMode', value: 'non_patient' } })}
                          style={{ fontWeight: form.bookingMode === 'non_patient' ? 600 : 600 }}
                          className={`${classes.dialogModeButton} ${classes.dialogModeButtonRight} ${form.bookingMode === 'non_patient' ? classes.dialogModeButtonActive : classes.dialogModeButtonInactive}`}
                        >
                          Event
                        </Button>
                      </Box>
                      <IconButton aria-label="Close booking dialog" onClick={handleDialogClose} className={classes.closeButton}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </DialogTitle>
                <DialogContent className={classes.dialogContent}>
                  <Paper elevation={0} className={classes.dialogPanel}>
                  {selectedBooking?.status === 'cancelled' && (
                    <Box 
                      style={{ 
                        padding: '12px', 
                        marginBottom: '16px', 
                        background: 'rgba(244, 67, 54, 0.1)', 
                        borderLeft: '4px solid #f44336',
                        borderRadius: '4px'
                      }}
                    >
                      <Typography variant="body2" style={{ color: '#d32f2f', fontWeight: 600 }}>
                        {selectedBookingCancelledBannerText}
                      </Typography>
                    </Box>
                  )}
                  <Grid container spacing={1} style={{ rowGap: 'clamp(2px, 0.35vw, 6px)' }}>
                    {form.bookingMode === 'patient' ? (
                      <>
                        <Grid item xs={12} md={6}>
                          <Autocomplete
                            options={bookingPatientOptions}
                            getOptionLabel={(option) => option && option.first_name && option.last_name ? `${option.first_name} ${option.last_name}` : ''}
                            value={bookingPatientOptions.find((patient) => String(patient.user_id) === String(form.patient)) || null}
                            onInputChange={(event, value, reason) => {
                              if (reason === 'input') setPatientSearchInput(value);
                            }}
                            onChange={(event, newValue) => {
                              const patientId = newValue ? newValue.user_id : '';
                              const patientName = newValue
                                ? `${newValue.first_name || ''} ${newValue.last_name || ''}`.trim() || newValue.email || ''
                                : '';
                              setForm((prev) => ({ ...prev, patient: patientId, patientName, treatment: '' }));
                              if (patientId) {
                                fetchPayerReferralOptions(patientId);
                                setPatientSearchInput('');
                              } else {
                                setPayerReferralOptions([]);
                                setPatientTreatmentOptions([]);
                                setSelectedPayer('patient');
                              }
                            }}
                            getOptionSelected={(option, value) => option.user_id === value.user_id}
                            loading={patientSearchLoading}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Patient"
                                variant="outlined"
                                required
                                className={`${classes.orangeFocused} ${classes.textfieldColor}`}
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: (
                                    <>
                                      <InputAdornment position="start">
                                        <PersonIcon style={{ color: '#FF7F27', fontSize: 'clamp(14px, 1.2vw, 18px)', marginLeft: '6px', marginRight: '6px' }} />
                                      </InputAdornment>
                                      {params.InputProps.startAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                            fullWidth
                            disableClearable={false}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Date"
                            name="date"
                            type="date"
                            value={form.date}
                            onChange={handleFormChange}
                            fullWidth
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <EventIcon style={{ color: '#FF7F27', fontSize: 'clamp(14px, 1.2vw, 18px)', marginLeft: '2px'  }} />
                                </InputAdornment>
                              ),
                            }}
                            required
                            className={`${classes.orangeFocused} ${classes.textfieldColor}`}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Patient email"
                            value={form.patientEmail || ''}
                            fullWidth
                            variant="outlined"
                            InputProps={{ readOnly: true }}
                            className={`${classes.orangeFocused} ${classes.textfieldColor}`}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Patient phone"
                            value={form.patientPhone || ''}
                            fullWidth
                            variant="outlined"
                            InputProps={{ readOnly: true }}
                            className={`${classes.orangeFocused} ${classes.textfieldColor}`}
                          />
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <TextField
                            select
                            label="Service"
                            name="appointmentType"
                            value={form.appointmentType}
                            onChange={handleFormChange}
                            fullWidth
                            variant="outlined"
                            size="normal"
                            required
                            className={`${classes.orangeFocused} ${classes.textfieldColor}`}
                            SelectProps={{ MenuProps: bookingDialogMenuProps }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <ArrowDropDownIcon style={{ color: '#FF7F27', fontSize: 'clamp(14px, 1.2vw, 18px)' }} />
                                </InputAdornment>
                              ),
                            }}
                          >
                            {filteredServices.length > 0 ? (
                              filteredServices.map(svc => (
                                <MenuItem key={svc.id} value={svc.id}>
                                  {svc.name} ({svc.duration_minutes} min — {svc.price} {svc.currency})
                                </MenuItem>
                              ))
                            ) : (
                              <MenuItem value="" disabled>
                                No services configured — add them in Clinic Details → Services
                              </MenuItem>
                            )}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            select
                            label="Treatment Course"
                            name="treatment"
                            value={form.treatment}
                            onChange={handleFormChange}
                            fullWidth
                            variant="outlined"
                            size="normal"
                            required
                            disabled={!form.patient || patientTreatmentsLoading}
                            className={`${classes.orangeFocused} ${classes.textfieldColor}`}
                            SelectProps={{ MenuProps: bookingDialogMenuProps }}
                            FormHelperTextProps={{ style: { fontSize: 'clamp(0.58rem, 0.6vw, 0.75rem)' } }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <ArrowDropDownIcon style={{ color: '#FF7F27', fontSize: 'clamp(14px, 1.2vw, 18px)' }} />
                                </InputAdornment>
                              ),
                            }}
                          >
                            {patientTreatmentOptions.length > 0 ? (
                              patientTreatmentOptions.map((treatment) => (
                                <MenuItem key={treatment.id} value={treatment.id}>
                                  {getTreatmentDisplayName(treatment)}
                                </MenuItem>
                              ))
                            ) : (
                              <MenuItem value="" disabled>
                                {patientTreatmentsLoading ? 'Loading courses...' : 'No saved courses found'}
                              </MenuItem>
                            )}
                          </TextField>
                        </Grid>
                      </>
                    ) : (
                      <>
                        <Grid item xs={12} md={6}>
                          <TextField
                            select
                            label="Block type"
                            name="nonPatientType"
                            value={form.nonPatientType}
                            onChange={handleFormChange}
                            fullWidth
                            variant="outlined"
                            className={`${classes.orangeFocused} ${classes.textfieldColor}`}
                            SelectProps={{ MenuProps: bookingDialogMenuProps }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <ArrowDropDownIcon style={{ color: '#FF7F27', fontSize: 'clamp(14px, 1.2vw, 18px)' }} />
                                </InputAdornment>
                              ),
                            }}
                          >
                            {NON_PATIENT_BOOKING_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Date"
                            name="date"
                            type="date"
                            value={form.date}
                            onChange={handleFormChange}
                            fullWidth
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <EventIcon style={{ color: '#FF7F27', fontSize: 'clamp(14px, 1.2vw, 18px)', marginLeft: '2px'  }} />
                                </InputAdornment>
                              ),
                            }}
                            required
                            className={`${classes.orangeFocused} ${classes.textfieldColor}`}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            label="Title"
                            name="title"
                            value={form.title}
                            onChange={handleFormChange}
                            fullWidth
                            variant="outlined"
                            required
                            helperText="Use this title for the blocked time on the calendar"
                            className={`${classes.orangeFocused} ${classes.textfieldColor}`}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <PersonIcon style={{ color: '#FF7F27', fontSize: 'clamp(14px, 1.2vw, 18px)' }} />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                      </>
                    )}
                    <Grid item xs={12} md={6}>
                      <TextField
                        select
                        label="Practitioner"
                        name="practitioner"
                        value={String(form.practitioner || '')}
                        onChange={handleFormChange}
                        fullWidth
                        variant="outlined"
                        size="normal"
                        required
                        className={`${classes.orangeFocused} ${classes.textfieldColor}`}
                        SelectProps={{ MenuProps: bookingDialogMenuProps }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PersonIcon style={{ color: '#FF7F27', fontSize: 'clamp(14px, 1.2vw, 18px)' }} />
                            </InputAdornment>
                          ),
                        }}
                      >
                        {bookingAvailablePractitioners.map((pr) => (
                          <MenuItem key={pr.id} value={String(pr.id)}>{pr.name}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    {form.bookingMode === 'patient' && (
                      <Grid item xs={12} md={6}>
                        <TextField
                          select
                          label="Billing To"
                          value={selectedPayer}
                          onChange={(e) => setSelectedPayer(e.target.value)}
                          fullWidth
                          variant="outlined"
                          size="normal"
                          className={`${classes.orangeFocused} ${classes.textfieldColor}`}
                          helperText={payerReferralOptions.length === 0 && clinicOtherPayers.length === 0 ? "No insurance/referral letters or other payers found" : ""}
                          SelectProps={{ MenuProps: bookingDialogMenuProps }}
                          FormHelperTextProps={{ style: { fontSize: 'clamp(0.58rem, 0.6vw, 0.75rem)' } }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <ArrowDropDownIcon style={{ color: '#FF7F27', fontSize: 'clamp(14px, 1.2vw, 18px)' }} />
                              </InputAdornment>
                            ),
                          }}
                        >
                          <MenuItem value="patient">Patient (Direct Pay)</MenuItem>
                          {payerReferralOptions.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                              {option.label}
                            </MenuItem>
                          ))}
                          {clinicOtherPayers.length > 0 && (
                            <MenuItem disabled value="__other_header__">
                              Other payers
                            </MenuItem>
                          )}
                          {clinicOtherPayers.map((option) => (
                            <MenuItem key={`other:${option.id}`} value={`other:${option.id}`}>
                              {option.label}
                              {option.billingEmail ? ` · ${option.billingEmail}` : ''}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    )}
                    <Grid item xs={6}>
                      <TextField
                        label="Start Time"
                        name="time"
                        type="time"
                        value={form.time}
                        onChange={handleFormChange}
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 300 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AccessTimeIcon style={{ color: '#FF7F27', fontSize: 'clamp(14px, 1.2vw, 18px)' }} />
                            </InputAdornment>
                          ),
                        }}
                        required
                        className={`${classes.orangeFocused} ${classes.textfieldColor}`}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        label="End Time"
                        name="endTime"
                        type="time"
                        value={form.endTime}
                        onChange={handleFormChange}
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 300 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AccessTimeIcon style={{ color: '#FF7F27', fontSize: 'clamp(14px, 1.2vw, 18px)' }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => adjustEndTime(-5)}
                                edge="end"
                                style={{ marginRight: 4 }}
                              >
                                <Remove fontSize="small" style={{ color: '#FF7F27' }} />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => adjustEndTime(5)}
                                edge="end"
                              >
                                <Add fontSize="small" style={{ color: '#FF7F27' }} />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        required
                        className={`${classes.orangeFocused} ${classes.textfieldColor}`}
                      />
                    </Grid>
                    
                    {showAssessmentDeliveryModeField && (
                      <Grid item xs={12} md={6}>
                        <TextField
                          select
                          label="Assessment Email"
                          name="assessmentDeliveryMode"
                          value={form.assessmentDeliveryMode}
                          onChange={handleFormChange}
                          fullWidth
                          variant="outlined"
                          size="normal"
                          required
                          helperText="Choose which intake email should be sent for this initial consultation"
                          className={`${classes.orangeFocused} ${classes.textfieldColor}`}
                          SelectProps={{ MenuProps: bookingDialogMenuProps }}
                          FormHelperTextProps={{ style: { fontSize: 'clamp(0.58rem, 0.6vw, 0.75rem)' } }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <ArrowDropDownIcon style={{ color: '#FF7F27', fontSize: 'clamp(14px, 1.2vw, 18px)' }} />
                              </InputAdornment>
                            ),
                          }}
                        >
                          <MenuItem value="full_assessment">Full assessment link</MenuItem>
                          <MenuItem value="short_form_assessment">Short-form assessment link</MenuItem>
                        </TextField>
                      </Grid>
                    )}
                    
                    {form.bookingMode === 'patient' && (
                      <>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Price"
                            name="booked_price"
                            type="number"
                            value={form.booked_price}
                            onChange={handleFormChange}
                            fullWidth
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ min: 0, step: '0.01' }}
                            required
                            className={`${classes.orangeFocused} ${classes.textfieldColor}`}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            select
                            label="Currency"
                            name="booked_currency"
                            value={form.booked_currency}
                            onChange={handleFormChange}
                            fullWidth
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            required
                            className={`${classes.orangeFocused} ${classes.textfieldColor}`}
                            SelectProps={{ MenuProps: bookingDialogMenuProps }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <ArrowDropDownIcon style={{ color: '#FF7F27', fontSize: 'clamp(14px, 1.2vw, 18px)' }} />
                                </InputAdornment>
                              ),
                            }}
                          >
                            <MenuItem value="EUR">EUR</MenuItem>
                            <MenuItem value="USD">USD</MenuItem>
                            <MenuItem value="GBP">GBP</MenuItem>
                            <MenuItem value="DKK">DKK</MenuItem>
                            <MenuItem value="NOK">NOK</MenuItem>
                            <MenuItem value="SEK">SEK</MenuItem>
                          </TextField>
                        </Grid>
                        {selectedBooking?.id ? (
                          <>
                            <Grid item xs={12} md={6}>
                              <TextField
                                select
                                label="Pay Method"
                                name="payment_method"
                                value={isSelectedBookingPaid ? (form.payment_method || selectedBookingPaymentSelectValue) : form.payment_method}
                                onChange={handleFormChange}
                                fullWidth
                                variant="outlined"
                                InputLabelProps={{ shrink: true }}
                                required
                                className={`${classes.orangeFocused} ${classes.textfieldColor}`}
                                disabled={isSelectedBookingPaid}
                                SelectProps={{ MenuProps: bookingDialogMenuProps }}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <ArrowDropDownIcon style={{ color: '#FF7F27', fontSize: 'clamp(14px, 1.2vw, 18px)' }} />
                                    </InputAdornment>
                                  ),
                                }}
                              >
                                {bookingPaymentMethodOptions.map((option) => (
                                  <MenuItem
                                    key={option.value}
                                    value={option.value}
                                    disabled={option.value === 'CardAutopay' && !isStripeAutopayAvailable}
                                  >
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </TextField>
                              {!isStripeAutopayAvailable && stripeAutopayDisabledReason && (
                                <Typography variant="caption" style={{ color: '#6f7c8d', display: 'block', marginTop: 6 }}>
                                  {stripeAutopayDisabledReason}
                                </Typography>
                              )}
                              {selectedPayer !== 'patient' && selectedInsuranceBillingOption && selectedInsuranceCoveragePerAppointment != null && (
                                <Typography variant="caption" style={{ color: '#6f7c8d', display: 'block', marginTop: 6 }}>
                                  {isInsuranceSplitBilling
                                    ? `Split billing detected: coverage per appointment is ${selectedInsuranceCoveragePerAppointment.toFixed(2)}, so the invoice will be split.`
                                    : `Insurance coverage per appointment is ${selectedInsuranceCoveragePerAppointment.toFixed(2)}.`}
                                </Typography>
                              )}
                              {selectedPayer !== 'patient' && isInsuranceSplitBilling && selectedSplitBillingPatientAmount != null && selectedSplitBillingPatientAmount > 0 && (
                                <SplitBillingPatientPaymentBox
                                  amount={selectedSplitBillingPatientAmount}
                                  currency={selectedSplitBillingCurrency}
                                  paymentMethod={form.split_payment_method}
                                  onPaymentMethodChange={handleFormChange}
                                  onSubmit={handleSplitBillingPatientPayment}
                                  disabled={isLoading}
                                  paymentOptions={PATIENT_PAYMENT_METHOD_OPTIONS}
                                  buttonLabel={getSplitBillingPatientPaymentButtonLabel(form.split_payment_method)}
                                  classes={classes}
                                  bookingDialogMenuProps={bookingDialogMenuProps}
                                />
                              )}
                              {isSelectedBookingPaid && (
                                <Box style={{ marginTop: 10, padding: '10px 12px', borderRadius: 8, backgroundColor: '#f5f9ff', border: '1px solid #dce8f5' }}>
                                  <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={handleAccreditPaidBooking}
                                    disabled={isLoading}
                                    className={classes.PayButton}
                                    startIcon={<CheckCircleIcon style={{ color: '#fff' }} />}
                                  >
                                    Accredit {selectedBookingPaymentMethodLabel}
                                  </Button>
                                  <Typography variant="caption" style={{ color: '#666', display: 'block', marginTop: 6 }}>
                                    Ask whether the patient has already paid the balance before reopening the bill.
                                  </Typography>
                                </Box>
                              )}
                              {/*{isSelectedBookingUnpaid && (
                                <Box style={{ marginTop: 10, padding: '10px 12px', borderRadius: 8, backgroundColor: '#fff7f2', border: '1px solid #ffd7bf' }}>
                                  <Typography variant="body2" style={{ color: '#8a4b15', fontWeight: 600, lineHeight: 1.4 }}>
                                    Bill is unpaid
                                  </Typography>
                                  <Typography variant="caption" style={{ color: '#8a4b15', display: 'block', marginTop: 2 }}>
                                    This payment was accredited, so the invoice is open again.
                                  </Typography>
                                </Box>
                              )}*/}
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Button
                                fullWidth
                                variant="contained"
                                onClick={handlePaymentMethodAction}
                                className={classes.PayButton}
                                disabled={!selectedBooking?.id || isLoading || isSelectedBookingPaid || (!isSelectedBookingPaid && !form.payment_method)}
                                startIcon={isSelectedBookingPaid ? <CheckCircleIcon style={{ color: '#fff' }} /> : undefined}
                              >
                                {getPaymentMethodButtonLabel(form.payment_method || selectedBookingPaymentSelectValue, isSelectedBookingPaid)}
                              </Button>
                            </Grid>
                          </>
                        ) : null}
                      </>
                    )}
                    {clinicLocations.length > 0 && (
                      <Grid item xs={12} sm={6}>
                        <TextField
                          select
                          label="Location"
                          value={form.clinic_location || ''}
                          onChange={(e) => setForm((prev) => ({
                            ...prev,
                            clinic_location: e.target.value,
                            room: '',
                          }))}
                          fullWidth
                          variant="outlined"
                          className={`${classes.orangeFocused} ${classes.textfieldColor}`}
                          SelectProps={{ MenuProps: bookingDialogMenuProps }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <ArrowDropDownIcon style={{ color: '#FF7F27', fontSize: 'clamp(14px, 1.2vw, 18px)' }} />
                              </InputAdornment>
                            ),
                          }}
                        >
                          <MenuItem value="">None</MenuItem>
                          {clinicLocations.map((loc) => (
                            <MenuItem key={loc.id} value={loc.id}>{loc.name}</MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    )}
                    {form.clinic_location && (() => {
                      const loc = clinicLocations.find(l => l.id === form.clinic_location);
                      return loc && loc.rooms && loc.rooms.length > 0 ? (
                        <Grid item xs={12} sm={6}>
                          <TextField
                            select
                            label="Room"
                            value={form.room || ''}
                            onChange={(e) => setForm((prev) => ({ ...prev, room: e.target.value }))}
                            fullWidth
                            variant="outlined"
                            className={`${classes.orangeFocused} ${classes.textfieldColor}`}
                            SelectProps={{
                              displayEmpty: true,
                              MenuProps: bookingDialogMenuProps,
                              renderValue: (selected) => {
                                const selectedRoom = loc.rooms.find((room) => String(room.id) === String(selected || ''));
                                if (!selectedRoom) {
                                  return <span>None</span>;
                                }

                                return (
                                  <Box display="flex" alignItems="center" style={{ gap: 8, minWidth: 0 }}>
                                    <Box style={{ width: 12, height: 12, borderRadius: 3, background: selectedRoom.color || '#3174ad', flexShrink: 0 }} />
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedRoom.name}</span>
                                  </Box>
                                );
                              },
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <ArrowDropDownIcon style={{ color: '#FF7F27', fontSize: 'clamp(14px, 1.2vw, 18px)' }} />
                                </InputAdornment>
                              ),
                            }}
                          >
                            <MenuItem value="">None</MenuItem>
                            {loc.rooms.map((room) => (
                              <MenuItem key={room.id} value={room.id}>
                                <Box display="flex" alignItems="center" style={{ gap: 8, minWidth: 0 }}>
                                  <Box style={{ width: 12, height: 12, borderRadius: 3, background: room.color || '#3174ad', flexShrink: 0 }} />
                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{room.name}</span>
                                </Box>
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      ) : null;
                    })()}
                    <Grid item xs={12}>
                      <TextField
                        label="Notes"
                        name="notes"
                        value={form.notes}
                        onChange={handleFormChange}
                        fullWidth
                        variant="outlined"
                        multiline
                        rows={2}
                        className={`${classes.orangeFocused} ${classes.textfieldColor} ${classes.notesField}`}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <Typography variant="caption" style={{ color: '#002D55', fontWeight: 600, fontSize: 'clamp(0.65rem, 0.7vw, 0.8rem)', minWidth: 80 }}>
                          Event Color
                        </Typography>
                        {['#3174ad','#002D55','#FF7F27','#4caf50','#f44336','#9c27b0','#00bcd4','#ff9800'].map((swatch) => (
                          <Box
                            key={swatch}
                            onClick={() => setForm(f => ({ ...f, color: swatch }))}
                            style={{
                              width: 22, height: 22, borderRadius: '50%',
                              backgroundColor: swatch, cursor: 'pointer', flexShrink: 0,
                              border: form.color === swatch ? '2.5px solid #002D55' : '2px solid transparent',
                              boxShadow: form.color === swatch ? '0 0 0 1.5px #fff inset' : 'none',
                              transition: 'border 0.15s',
                            }}
                          />
                        ))}
                        <input
                          type="color"
                          value={form.color || '#3174ad'}
                          onChange={(e) => setForm(f => ({ ...f, color: e.target.value }))}
                          style={{ width: 26, height: 26, padding: 0, border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', backgroundColor: 'transparent' }}
                          title="Custom color"
                        />
                      </Box>
                    </Grid>
                  </Grid>
                  </Paper>
                </DialogContent>
                <DialogActions className={classes.dialogActions}>
                  <Box flex={1} display="flex" justifyContent="flex-start">
                    {selectedBooking && (
                      <Box style={{ display: 'flex', gap: 8 }}>
                        <Button
                          onClick={() => openRepeatBookingDialog(selectedBooking)}
                          variant="outlined"
                          disabled={isLoading}
                          className={classes.repeatActionButton}
                        >
                          Repeat
                        </Button>
                        <Button
                          onClick={handleDeleteClick}
                          variant="outlined"
                          disabled={isLoading}
                          className={classes.deleteActionButton}
                        >
                          Delete
                        </Button>
                      </Box>
                    )}
                    {!selectedBooking && (
                      <Box style={{ display: 'flex', gap: 8 }}>
                        <Button
                          onClick={openDraftRepeatBookingDialog}
                          variant="outlined"
                          disabled={isLoading}
                          className={classes.repeatActionButton}
                        >
                          Repeat
                        </Button>
                      </Box>
                    )}
                  </Box>
                  <Button onClick={handleDialogClose} className={classes.cancelButton} variant="contained" disabled={isLoading}>Cancel</Button>
                  <Button onClick={() => handleSaveBooking()} className={classes.confirmButton} variant="contained" disabled={isLoading}>
                    Save
                  </Button>
                </DialogActions>
      </Dialog>

        {bookingVideoCallOpen && bookingVideoCallAppointment && (
          <VideoCallModal
            open={bookingVideoCallOpen}
            appointment={bookingVideoCallAppointment}
            authToken={token}
            onClose={handleCloseSelectedBookingVideoCall}
          />
        )}

      <Dialog
        open={openClassDialog}
        onClose={() => {
          setOpenClassDialog(false);
          setSelectedClassBooking(null);
          setClassAttendanceDrafts([]);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ className: classes.dialogPaper }}
      >
        <DialogTitle className={classes.dialogHeader}>
          <Box className={classes.dialogTitleRow}>
            <span className={classes.dialogTitleText}>Class booking</span>
            <Box className={classes.dialogTitleActions}>
              <IconButton
                aria-label="Close class dialog"
                onClick={() => {
                  setOpenClassDialog(false);
                  setSelectedClassBooking(null);
                  setClassAttendanceDrafts([]);
                }}
                className={classes.closeButton}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Paper elevation={0} className={classes.dialogPanel}>
            <Box style={{ padding: 14, backgroundColor: '#eef4fb', borderRadius: 12, border: '1px solid #d7e3ef', marginBottom: 14 }}>
              <Typography style={{ color: '#002D55', fontWeight: 800, fontSize: 16, marginBottom: 4 }}>
                {selectedClassBooking?.title || selectedClassBooking?.service_name || 'Class'}
              </Typography>
              <Typography style={{ color: '#46607a', fontSize: 13, marginBottom: 2 }}>
                {selectedClassBooking?.start_datetime ? moment(selectedClassBooking.start_datetime).format('dddd, DD MMM YYYY HH:mm') : '—'}
                {selectedClassBooking?.end_datetime ? ` - ${moment(selectedClassBooking.end_datetime).format('HH:mm')}` : ''}
              </Typography>
              <Typography style={{ color: '#46607a', fontSize: 13 }}>
                Room: {selectedClassBooking?.classRoomName || selectedClassBooking?.room?.name || '—'}
              </Typography>
              <Typography style={{ color: '#46607a', fontSize: 13 }}>
                Staff: {selectedClassBooking?.assignedStaff || 'All staff'}
              </Typography>
              <Box style={{ marginTop: 10 }}>
                <TextField
                  select
                  size="small"
                  fullWidth
                  variant="outlined"
                  label="Class staff / vacancy"
                  value={classAttendanceStaffId}
                  onChange={(event) => setClassAttendanceStaffId(event.target.value)}
                  className={classes.orangeFocused}
                  SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                >
                  {classAttendanceStaffOptions.length > 0 ? classAttendanceStaffOptions.map((staff) => (
                    <MenuItem key={staff.id} value={String(staff.id)} className={classes.menuItem}>
                      {staff.name || staff.email || 'Staff'}
                    </MenuItem>
                  )) : (
                    <MenuItem value="" className={classes.menuItem}>No staff options available</MenuItem>
                  )}
                </TextField>
              </Box>
            </Box>

            <Typography className={classes.dialogSectionLabel}>Weekly attendees</Typography>
            <Box style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              
              <Box style={{ border: '1px solid #d7e3ef', borderRadius: 10, overflow: 'hidden', backgroundColor: '#fff' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ fontWeight: 700, color: '#002D55' }}>Attended</TableCell>
                      <TableCell style={{ fontWeight: 700, color: '#002D55' }}>Patient</TableCell>
                      <TableCell style={{ fontWeight: 700, color: '#002D55' }}>Journal note</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {classAttendanceDrafts.length > 0 ? classAttendanceDrafts.map((draft) => {
                      const patientLabel = getPatientDisplaySummary(draft.patientId) || draft.patientId;
                      return (
                        <TableRow key={draft.patientId}>
                          <TableCell style={{ verticalAlign: 'top', width: 96 }}>
                            <Checkbox
                              color="primary"
                              style={{ color: '#FF7F27' }}
                              checked={Boolean(draft.attended)}
                              onChange={(event) => {
                                const checked = event.target.checked;
                                setClassAttendanceDrafts((current) => current.map((entry) => (
                                  entry.patientId === draft.patientId
                                    ? {
                                        ...entry,
                                        attended: checked,
                                        note: checked
                                          ? (entry.note || getClassAttendanceDefaultNote(selectedClassBooking, draft.patientId, true))
                                          : '',
                                      }
                                    : entry
                                )));
                              }}
                            />
                          </TableCell>
                          <TableCell style={{ verticalAlign: 'top', minWidth: 170 }}>
                            <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 13, whiteSpace: 'pre-line' }}>
                              {patientLabel}
                            </Typography>
                            <Typography style={{ color: '#46607a', fontSize: 13, marginTop: 4 }}>
                              Treatment course: {draft.treatmentLabel || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell style={{ verticalAlign: 'top', minWidth: 470, paddingTop: 4, paddingBottom: 4 }}>
                            <TextField
                              fullWidth
                              multiline
                              rows={2}
                              variant="outlined"
                              className={`${classes.orangeFocused} ${classes.notesField}`}
                              style={{fontSize: 13}}
                              value={draft.note}
                              onChange={(event) => {
                                const value = event.target.value;
                                setClassAttendanceDrafts((current) => current.map((entry) => (
                                  entry.patientId === draft.patientId
                                    ? { ...entry, note: value }
                                    : entry
                                )));
                              }}
                              placeholder="Journal note"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    }) : (
                      <TableRow>
                        <TableCell colSpan={3}>
                          <Typography style={{ color: '#46607a', fontSize: 13 }}>
                            No patients are assigned to this class.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          </Paper>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button
            onClick={() => {
              setOpenClassDialog(false);
              setSelectedClassBooking(null);
              setClassAttendanceDrafts([]);
            }}
            className={classes.cancelButton}
            variant="contained"
            disabled={classDialogSaving}
          >
            Close
          </Button>
          <Button
            onClick={handleSaveClassAttendance}
            className={classes.confirmButton}
            variant="contained"
            disabled={classDialogSaving || classAttendanceDrafts.length === 0}
          >
            Save attendance
          </Button>
        </DialogActions>
      </Dialog>

        <Dialog
          open={bookingConflictDialogOpen}
          onClose={() => {
            setBookingConflictDialogOpen(false);
            setBookingConflictPreview(null);
          }}
          fullWidth
          maxWidth="md"
          PaperProps={{ className: classes.dialogPaper }}
        >
          <DialogTitle className={classes.dialogHeader}>
            <Box className={classes.dialogTitleRow}>
              <span className={classes.dialogTitleText}>Booking conflicts</span>
              <Box className={classes.dialogTitleActions}>
                <IconButton
                  aria-label="Close conflict dialog"
                  onClick={() => {
                    setBookingConflictDialogOpen(false);
                    setBookingConflictPreview(null);
                  }}
                  className={classes.closeButton}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent className={classes.dialogContent}>
            <Paper elevation={0} className={classes.dialogPanel}>
              <Typography className={classes.dialogSectionLabel}>Conflicting bookings</Typography>
              <Typography style={{ color: '#002D55', marginTop: 6, marginBottom: 14, fontSize: '0.9rem' }}>
                {bookingConflictPreview?.summary?.conflicting > 0
                  ? `${bookingConflictPreview.summary.conflicting} conflicting booking${bookingConflictPreview.summary.conflicting === 1 ? '' : 's'} were found for this time.`
                  : 'One or more conflicting bookings were found for this time.'}
              </Typography>
              <TableContainer style={{ maxHeight: 320 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ fontWeight: 600, color: '#002D55' }}>Booking</TableCell>
                      <TableCell style={{ fontWeight: 600, color: '#002D55' }}>Time</TableCell>
                      <TableCell style={{ fontWeight: 600, color: '#002D55' }}>Staff</TableCell>
                      <TableCell style={{ fontWeight: 600, color: '#002D55' }}>Room</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(bookingConflictPreview?.conflicts || []).map((conflict) => {
                      const startMoment = moment(conflict.start_datetime);
                      const endMoment = moment(conflict.end_datetime);
                      return (
                        <TableRow key={conflict.id || `${conflict.start_datetime}-${conflict.staff_member || conflict.room || ''}`}>
                          <TableCell>
                            {conflict.patient_name || conflict.title || 'Existing appointment'}
                          </TableCell>
                          <TableCell>
                            {startMoment.isValid() && endMoment.isValid()
                              ? `${startMoment.format('ddd, MMM D, YYYY h:mm A')} - ${endMoment.format('h:mm A')}`
                              : '—'}
                          </TableCell>
                          <TableCell>
                            {conflict.staff_member_name || '—'}
                          </TableCell>
                          <TableCell>
                            {conflict.room_name || '—'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </DialogContent>
          <DialogActions className={classes.dialogActions}>
            <Button
              onClick={() => {
                setBookingConflictDialogOpen(false);
                setBookingConflictPreview(null);
              }}
              className={classes.cancelButton}
              variant="contained"
              disabled={isLoading}
            >
              Skip
            </Button>
            <Button
              onClick={() => {
                setBookingConflictDialogOpen(false);
                setBookingConflictPreview(null);
                void handleSaveBooking('book_anyway');
              }}
              className={classes.confirmButton}
              variant="contained"
              disabled={isLoading}
            >
              Book anyway
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={repeatDialogOpen}
          onClose={() => {
            setRepeatDialogOpen(false);
            if (repeatDialogMode === 'draft' || repeatSettingsQueued || queuedRepeatSettings) {
              setRepeatSettingsQueued(false);
              setQueuedRepeatSettings(null);
            }
            setRepeatDialogMode('saved');
            setRepeatSourceDraft(null);
            setRepeatPreview(null);
            setRepeatError('');
            setRepeatLoading(false);
          }}
          fullWidth
          maxWidth="md"
          PaperProps={{ className: classes.dialogPaper }}
        >
          <DialogTitle className={classes.dialogHeader}>
            <Box className={classes.dialogTitleRow}>
              <span className={classes.dialogTitleText}>{repeatDialogMode === 'review' ? 'Review booking' : 'Repeat booking'}</span>
              <Box className={classes.dialogTitleActions}>
                <IconButton
                  aria-label="Close repeat dialog"
                  onClick={() => {
                    setRepeatDialogOpen(false);
                    if (repeatDialogMode === 'draft' || repeatSettingsQueued || queuedRepeatSettings) {
                      setRepeatSettingsQueued(false);
                      setQueuedRepeatSettings(null);
                    }
                    setRepeatDialogMode('saved');
                    setRepeatSourceDraft(null);
                    setRepeatPreview(null);
                    setRepeatError('');
                    setRepeatLoading(false);
                  }}
                  className={classes.closeButton}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent className={classes.dialogContent}>
            <Paper elevation={0} className={classes.dialogPanel}>
              <Typography className={classes.dialogSectionLabel}>Repeat settings</Typography>
              <Typography style={{ color: '#002D55', marginTop: 6, marginBottom: 14, fontSize: '0.9rem' }}>
                {repeatDialogMode === 'draft' || (repeatDialogMode === 'review' && !selectedBooking?.id)
                  ? `${repeatSourceDraft?.bookingMode === 'patient' ? (repeatSourceDraft?.patientName || 'Patient booking') : (repeatSourceDraft?.title || 'Block / meeting')} · repeat queue`
                  : `${(selectedBooking?.patient_name || selectedBooking?.title || 'Booking')} · ${(selectedBooking?.service_name || selectedBooking?.title || 'Appointment')}`}
              </Typography>
              <Typography style={{ color: '#64748B', fontSize: '0.8rem', marginBottom: 12 }}>
                {repeatDialogMode === 'draft' || (repeatDialogMode === 'review' && !selectedBooking?.id)
                  ? 'These repeat settings will be queued. Save the booking to review conflicts and prepare the batch.'
                  : 'Repeated bookings will copy the current booking details, practitioner, service, location, room, notes, and payment settings from this booking.'}
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    label="Repeat pattern"
                    value={repeatSettings.repeatPattern}
                    onChange={(event) => {
                      const nextPattern = event.target.value;
                      setRepeatSettings((current) => ({
                        ...current,
                        repeatPattern: nextPattern,
                        weekdays: nextPattern === 'daily'
                          ? []
                          : nextPattern === 'weekly_same_day'
                            ? [repeatSourceWeekday]
                            : (current.weekdays && current.weekdays.length > 0 ? current.weekdays : [repeatSourceWeekday]),
                      }));
                      setRepeatPreview(null);
                      setRepeatError('');
                    }}
                    fullWidth
                    variant="outlined"
                    className={`${classes.orangeFocused} ${classes.textfieldColor}`}
                    SelectProps={{ MenuProps: { classes: { list: classes.dialogMenuList } } }}
                  >
                    <MenuItem value="daily">Every N days</MenuItem>
                    <MenuItem value="weekly_same_day">Weekly on the same day</MenuItem>
                    <MenuItem value="multiple_weekdays">Multiple weekdays each week</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label={repeatSettings.repeatPattern === 'daily' ? 'Repeat every (days)' : 'Repeat every (weeks)'}
                    type="number"
                    value={repeatSettings.repeatEvery}
                    onChange={(event) => {
                      const nextValue = Number(event.target.value) || 1;
                      setRepeatSettings((current) => ({ ...current, repeatEvery: Math.max(1, nextValue) }));
                      setRepeatPreview(null);
                      setRepeatError('');
                    }}
                    fullWidth
                    variant="outlined"
                    inputProps={{ min: 1, step: 1 }}
                    className={`${classes.orangeFocused} ${classes.textfieldColor}`}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    label="End repeat by"
                    value={repeatSettings.endMode}
                    onChange={(event) => {
                      setRepeatSettings((current) => ({ ...current, endMode: event.target.value }));
                      setRepeatPreview(null);
                      setRepeatError('');
                    }}
                    fullWidth
                    variant="outlined"
                    className={`${classes.orangeFocused} ${classes.textfieldColor}`}
                    SelectProps={{ MenuProps: { classes: { list: classes.dialogMenuList } } }}
                  >
                    <MenuItem value="count">
                      {repeatSettings.repeatPattern === 'daily' ? 'After N days' : 'After N weeks'}
                    </MenuItem>
                    <MenuItem value="until_date">Until a date</MenuItem>
                  </TextField>
                </Grid>

                {repeatSettings.endMode === 'count' ? (
                  <Grid item xs={12} md={4}>
                    <TextField
                      label={repeatSettings.repeatPattern === 'daily' ? 'Number of days' : 'Number of weeks'}
                      type="number"
                      value={repeatSettings.repeatCount}
                      onChange={(event) => {
                        const nextValue = Number(event.target.value) || 1;
                        setRepeatSettings((current) => ({ ...current, repeatCount: Math.max(1, nextValue) }));
                        setRepeatPreview(null);
                        setRepeatError('');
                      }}
                      fullWidth
                      variant="outlined"
                      inputProps={{ min: 1, step: 1 }}
                      className={`${classes.orangeFocused} ${classes.textfieldColor}`}
                    />
                  </Grid>
                ) : (
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Until date"
                      type="date"
                      value={repeatSettings.untilDate}
                      onChange={(event) => {
                        setRepeatSettings((current) => ({ ...current, untilDate: event.target.value }));
                        setRepeatPreview(null);
                        setRepeatError('');
                      }}
                      fullWidth
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      className={`${classes.orangeFocused} ${classes.textfieldColor}`}
                    />
                  </Grid>
                )}

                {repeatSettings.repeatPattern === 'multiple_weekdays' && (
                  <Grid item xs={12}>
                    <Typography style={{ color: '#002D55', fontWeight: 600, marginBottom: 8, fontSize: '0.85rem' }}>
                      Repeat on these weekdays
                    </Typography>
                    <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {WEEKDAY_OPTIONS.map((weekday) => {
                        const checked = (repeatSettings.weekdays || []).includes(weekday.value);
                        return (
                          <FormControlLabel
                            key={weekday.value}
                            control={(
                              <Checkbox
                                checked={checked}
                                onChange={(event) => {
                                  const nextChecked = event.target.checked;
                                  setRepeatSettings((current) => {
                                    const nextWeekdays = new Set(current.weekdays || []);
                                    if (nextChecked) {
                                      nextWeekdays.add(weekday.value);
                                    } else {
                                      nextWeekdays.delete(weekday.value);
                                    }
                                    return { ...current, weekdays: Array.from(nextWeekdays).sort((a, b) => a - b) };
                                  });
                                  setRepeatPreview(null);
                                  setRepeatError('');
                                }}
                                color="primary"
                              />
                            )}
                            label={weekday.label}
                          />
                        );
                      })}
                    </Box>
                  </Grid>
                )}

                {repeatSettings.repeatPattern === 'weekly_same_day' && (
                  <Grid item xs={12}>
                    <Typography style={{ color: '#64748B', fontSize: '0.8rem' }}>
                      This repeats on the same weekday as the current booking: {selectedBookingStartMoment && selectedBookingStartMoment.isValid() ? selectedBookingStartMoment.format('dddd') : 'selected day'}.
                    </Typography>
                  </Grid>
                )}
              </Grid>

              {repeatError ? (
                <Box style={{ marginTop: 14, padding: '10px 12px', borderRadius: 8, backgroundColor: 'rgba(244, 67, 54, 0.08)', border: '1px solid rgba(244, 67, 54, 0.24)' }}>
                  <Typography style={{ color: '#b71c1c', fontSize: '0.8rem' }}>{repeatError}</Typography>
                </Box>
              ) : null}

              {repeatPreview ? (
                <Box style={{ marginTop: 16 }}>
                  <Box style={{ padding: 12, borderRadius: 10, marginBottom: 12, backgroundColor: repeatPreview.summary?.conflicting ? '#FFF4E5' : '#F1F8E9', border: '1px solid rgba(0,0,0,0.06)' }}>
                    <Typography style={{ fontWeight: 700, color: '#002D55' }}>
                      Preview: {repeatPreview.summary?.requested || 0} occurrences · {repeatPreview.summary?.conflicting || 0} conflicts
                    </Typography>
                    <Typography style={{ color: '#64748B', fontSize: '0.8rem', marginTop: 4 }}>
                      {repeatPreview.summary?.conflicting
                        ? 'Review the collisions below. You can skip those dates or book anyway.'
                        : 'No collisions were found for the selected repeat settings.'}
                    </Typography>
                  </Box>
                  <TableContainer style={{ maxHeight: 320 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell style={{ fontWeight: 600, color: '#002D55' }}>Date</TableCell>
                          <TableCell style={{ fontWeight: 600, color: '#002D55' }}>Time</TableCell>
                          <TableCell style={{ fontWeight: 600, color: '#002D55' }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(repeatPreview.occurrences || []).map((occurrence) => {
                          const startMoment = moment(occurrence.start_datetime);
                          const endMoment = moment(occurrence.end_datetime);
                          const firstConflict = Array.isArray(occurrence.conflicts) && occurrence.conflicts.length > 0
                            ? occurrence.conflicts[0]
                            : null;

                          return (
                            <TableRow
                              key={`${occurrence.start_datetime}-${occurrence.index}`}
                              style={{ backgroundColor: occurrence.conflict_count ? 'rgba(244, 67, 54, 0.04)' : 'transparent' }}
                            >
                              <TableCell>
                                {startMoment.isValid() ? startMoment.format('ddd, MMM D, YYYY') : occurrence.date}
                              </TableCell>
                              <TableCell>
                                {startMoment.isValid() && endMoment.isValid()
                                  ? `${startMoment.format('h:mm A')} - ${endMoment.format('h:mm A')}`
                                  : '—'}
                              </TableCell>
                              <TableCell>
                                {occurrence.conflict_count > 0 ? (
                                  <Box>
                                    <Typography style={{ color: '#b71c1c', fontWeight: 600, fontSize: '0.78rem' }}>
                                      {occurrence.conflict_count} conflict(s)
                                    </Typography>
                                    {firstConflict ? (
                                      <Typography style={{ color: '#7f1d1d', fontSize: '0.72rem', marginTop: 2 }}>
                                        {firstConflict.title || firstConflict.patient_name || 'Existing appointment'}
                                        {firstConflict.start_datetime ? ` · ${moment(firstConflict.start_datetime).format('h:mm A')}` : ''}
                                      </Typography>
                                    ) : null}
                                  </Box>
                                ) : (
                                  <Typography style={{ color: '#2E7D32', fontWeight: 600, fontSize: '0.78rem' }}>
                                    Available
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ) : null}
            </Paper>
          </DialogContent>
          <DialogActions className={classes.dialogActions}>
            <Button
              onClick={() => {
                setRepeatDialogOpen(false);
                if (repeatDialogMode === 'draft' || repeatSettingsQueued || queuedRepeatSettings) {
                  setRepeatSettingsQueued(false);
                  setQueuedRepeatSettings(null);
                }
                setRepeatPreview(null);
                setRepeatError('');
                setRepeatLoading(false);
              }}
              className={classes.cancelButton}
              variant="contained"
              disabled={repeatLoading}
            >
              Cancel
            </Button>
            {repeatDialogMode === 'draft' ? (
              <Button
                onClick={() => {
                  setQueuedRepeatSettings({ ...repeatSettings });
                  setRepeatSettingsQueued(true);
                  setRepeatDialogMode('review');
                  setRepeatPreview(buildDraftRepeatPreview());
                  setRepeatError('');
                  setRepeatLoading(false);
                }}
                className={classes.confirmButton}
                variant="contained"
                disabled={repeatLoading}
              >
                Review booking
              </Button>
            ) : repeatDialogMode === 'review' && !selectedBooking?.id ? repeatPreview?.summary?.conflicting > 0 ? (
              <>
                <Button
                  onClick={() => handleSaveBooking('book_anyway', { finalizeQueuedRepeat: true, repeatCollisionAction: 'skip' })}
                  className={classes.cancelButton}
                  variant="contained"
                  disabled={repeatLoading}
                >
                  Skip conflicting dates
                </Button>
                <Button
                  onClick={() => handleSaveBooking('book_anyway', { finalizeQueuedRepeat: true, repeatCollisionAction: 'book_anyway' })}
                  className={classes.confirmButton}
                  variant="contained"
                  disabled={repeatLoading}
                >
                  {repeatLoading ? 'Creating...' : 'Create repeated bookings'}
                </Button>
              </>
            ) : (
              <Button
                onClick={() => handleSaveBooking('book_anyway', { finalizeQueuedRepeat: true, repeatCollisionAction: 'book_anyway' })}
                className={classes.confirmButton}
                variant="contained"
                disabled={repeatLoading}
              >
                {repeatLoading ? 'Creating...' : 'Create repeated bookings'}
              </Button>
            ) : !repeatPreview ? (
              <Button
                onClick={handleRepeatPreview}
                className={classes.confirmButton}
                variant="contained"
                disabled={repeatLoading}
              >
                {repeatLoading ? 'Previewing...' : 'Preview repeat plan'}
              </Button>
            ) : repeatPreview.summary?.conflicting > 0 ? (
              <>
                <Button
                  onClick={() => (repeatDialogMode === 'review' && !selectedBooking?.id
                    ? handleSaveBooking('book_anyway', { finalizeQueuedRepeat: true, repeatCollisionAction: 'skip' })
                    : handleRepeatCreate('skip'))}
                  className={classes.cancelButton}
                  variant="contained"
                  disabled={repeatLoading}
                >
                  Skip conflicting dates
                </Button>
                <Button
                  onClick={() => (repeatDialogMode === 'review' && !selectedBooking?.id
                    ? handleSaveBooking('book_anyway', { finalizeQueuedRepeat: true, repeatCollisionAction: 'book_anyway' })
                    : handleRepeatCreate('book_anyway'))}
                  className={classes.confirmButton}
                  variant="contained"
                  disabled={repeatLoading}
                >
                  {repeatLoading ? 'Creating...' : 'Book anyway'}
                </Button>
              </>
            ) : (
              <Button
                onClick={() => (repeatDialogMode === 'review' && !selectedBooking?.id
                  ? handleSaveBooking('book_anyway', { finalizeQueuedRepeat: true, repeatCollisionAction: 'book_anyway' })
                  : handleRepeatCreate('book_anyway'))}
                className={classes.confirmButton}
                variant="contained"
                disabled={repeatLoading}
              >
                {repeatLoading ? 'Creating...' : 'Create repeated bookings'}
              </Button>
            )}
          </DialogActions>
        </Dialog>

      <Dialog open={openSickDayCancelConfirm} onClose={() => setOpenSickDayCancelConfirm(false)} PaperProps={{ className: classes.dialogPaper }}>
        <DialogTitle className={classes.dialogHeader}>
          <Box className={classes.dialogTitleRow}>
            <span className={classes.dialogTitleText}>Cancel Sick Day</span>
            <Box className={classes.dialogTitleActions}>
              <IconButton aria-label="Close sick day dialog" onClick={() => setOpenSickDayCancelConfirm(false)} className={classes.closeButton}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Paper elevation={0} className={classes.dialogPanel}>
            <Typography className={classes.deleteDialogText}>
              Are you sure you want to cancel this sick day? The staff allocations will be restored.
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={() => setOpenSickDayCancelConfirm(false)} className={classes.cancelButton} variant="contained" disabled={isLoading}>
            Keep sick day
          </Button>
          <Button onClick={handleSickDayCancelConfirm} variant="contained" disabled={isLoading} className={classes.deleteConfirmButton}>
            Cancel sick day
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteConfirm} onClose={() => setOpenDeleteConfirm(false)} PaperProps={{ className: classes.dialogPaper }}>
        <DialogTitle className={classes.dialogHeader}>
          <Box className={classes.dialogTitleRow}>
            <span className={classes.dialogTitleText}>Confirm Delete</span>
            <Box className={classes.dialogTitleActions}>
              <IconButton aria-label="Close delete dialog" onClick={() => setOpenDeleteConfirm(false)} className={classes.closeButton}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Paper elevation={0} className={classes.dialogPanel}>
          <Typography className={classes.deleteDialogText} style={{ marginBottom: 12 }}>
            What should happen with this appointment?
          </Typography>
          <Typography className={classes.deleteDialogText}>
            Showing it as a cancellation keeps it in the calendar as a red cancelled booking. Deleting removes it from the calendar entirely.
          </Typography>
          </Paper>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={() => setOpenDeleteConfirm(false)} className={classes.cancelButton} variant="contained">
            Cancel
          </Button>
          {selectedBookingIsPatientAppointment ? (
            <>
              <Button
                onClick={() => handleDeleteConfirm(false, 'cancel')}
                variant="contained"
                disabled={isLoading}
                className={classes.confirmButton}
              >
                Show as cancellation
              </Button>
              <Button
                onClick={() => handleDeleteConfirm(false, 'delete')}
                variant="contained"
                disabled={isLoading}
                className={classes.deleteConfirmButton}
              >
                Delete from calendar
              </Button>
            </>
          ) : (
            <Button onClick={() => handleDeleteConfirm(false, 'delete')} variant="contained" disabled={isLoading} className={classes.deleteConfirmButton}>
              Delete
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={openSickDayAutoCancelConfirm} onClose={() => setOpenSickDayAutoCancelConfirm(false)} PaperProps={{ className: classes.dialogPaper }}>
        <DialogTitle className={classes.dialogHeader}>
          <Box className={classes.dialogTitleRow}>
            <span className={classes.dialogTitleText}>Handle Sick Day Patients</span>
            <Box className={classes.dialogTitleActions}>
              <IconButton aria-label="Close sick day auto-cancel dialog" onClick={() => setOpenSickDayAutoCancelConfirm(false)} className={classes.closeButton}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Paper elevation={0} className={classes.dialogPanel}>
            <Typography className={classes.deleteDialogText} style={{ marginBottom: 10 }}>
              Do you want to automatically cancel affected patient appointments for this sick day?
            </Typography>
            <Typography className={classes.deleteDialogText}>
              Auto-cancelled patients will be notified by email and in-app message, without a late cancellation fee, and they will be prompted to reschedule.
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={() => handleConfirmSickDaySubmission(false)} className={classes.cancelButton} variant="contained" disabled={isLoading}>
            Handle manually
          </Button>
          <Button onClick={() => handleConfirmSickDaySubmission(true)} variant="contained" disabled={isLoading} className={classes.deleteConfirmButton}>
            Auto-cancel patients
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={accreditDialogOpen}
        onClose={handleCloseAccreditDialog}
        fullWidth
        PaperProps={{ className: classes.dialogPaper }}
      >
        <DialogTitle className={classes.dialogHeader}>
          <Box className={classes.dialogTitleRow}>
            <span className={classes.dialogTitleText}>Accredit payment</span>
            <Box className={classes.dialogTitleActions}>
              <IconButton aria-label="Close accredit dialog" onClick={handleCloseAccreditDialog} className={classes.closeButton}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Paper elevation={0} className={classes.dialogPanel}>
            <Typography className={classes.dialogSectionLabel}>Confirmation</Typography>
            <Typography style={{ marginTop: 8, marginBottom: 12 }}>
              {selectedBookingIsPatientAppointment && selectedBookingIsWithinLateCancellationWindow
                ? 'This appointment is inside the late cancellation window. Choose whether to bill the patient a late cancellation fee.'
                : selectedBookingIsPatientAppointment
                  ? 'Are you sure you want to cancel this appointment?'
                  : 'Are you sure you want to delete this appointment?'}
            </Typography>
            {selectedBookingIsPatientAppointment && selectedBookingIsWithinLateCancellationWindow ? (
              <>
                <Button onClick={() => handleDeleteConfirm(false)} variant="contained" disabled={isLoading} className={classes.confirmButton}>
                  Cancel without fee
                </Button>
                <Button onClick={() => handleDeleteConfirm(true)} variant="contained" disabled={isLoading} className={classes.deleteConfirmButton}>
                  Cancel and bill fee
                </Button>
              </>
            ) : (
              <Button onClick={() => handleDeleteConfirm(false)} variant="contained" disabled={isLoading} className={classes.deleteConfirmButton}>
                {selectedBookingIsPatientAppointment ? 'Cancel appointment' : 'Delete appointment'}
              </Button>
            )}
            <FormControlLabel
              control={(
                <Checkbox
                  checked={accreditPatientHasPaidBalance}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setAccreditPatientHasPaidBalance(checked);
                    if (!checked) {
                      setAccreditTransferToPatientBalance(false);
                    }
                  }}
                  color="primary"
                />
              )}
              label="Patient has already paid this balance"
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={accreditTransferToPatientBalance}
                  onChange={(event) => setAccreditTransferToPatientBalance(event.target.checked)}
                  color="primary"
                  disabled={!accreditPatientHasPaidBalance}
                />
              )}
              label="Keep the amount as patient balance instead of reopening the invoice"
            />
            <Typography variant="caption" style={{ display: 'block', marginTop: 4, color: '#666' }}>
              Leave both unchecked to reopen the invoice as unpaid so it can be edited or resent.
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={handleCloseAccreditDialog} className={classes.cancelButton} variant="contained" disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAccreditPaidBooking}
            variant="contained"
            disabled={isLoading}
            className={classes.PayButton}
          >
            {isLoading ? 'Processing...' : 'Accredit'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={stripePaymentDialogOpen}
        onClose={handleCloseStripePaymentOverlay}
        fullWidth
        maxWidth="sm"
        PaperProps={{ className: classes.dialogPaper }}
      >
        <DialogTitle className={classes.dialogHeader}>
          <Box className={classes.dialogTitleRow}>
            <span className={classes.dialogTitleText}>
              {stripePaymentDialogStage === 'overview'
                ? 'Unpaid Billed Appointments Overview'
                : stripePaymentDialogStage === 'new_card_form' || stripePaymentDialogStage === 'review_saved_cards' || stripePaymentDialogStage === 'new_card_options'
                  ? 'Stripe Payment Checkout'
                  : stripePaymentDialogStage === 'confirmation'
                    ? 'Payment Confirmation'
                    : 'Stripe payment'}
            </span>
            <Box className={classes.dialogTitleActions}>
              <IconButton aria-label="Close Stripe payment dialog" onClick={handleCloseStripePaymentOverlay} className={classes.closeButton}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Paper elevation={0} className={classes.dialogPanel}>
            {stripePaymentDialogStage === 'overview' && (
              <Box>
                <Typography style={{ color: '#666', marginBottom: 12 }}>
                  Review this billed appointment before opening Stripe checkout.
                </Typography>
                <Box style={{ marginBottom: 12, padding: 12, backgroundColor: '#FFF8E1', borderRadius: 8 }}>
                  <Typography style={{ fontWeight: 600, color: '#F57C00' }}>
                    1 appointment(s) · 1 invoice(s)
                  </Typography>
                  <Typography style={{ fontSize: 16, fontWeight: 700, color: '#002D55' }}>
                    Total Due: {formatMoney(stripePaymentQuote?.charge_amount ?? selectedBooking?.booked_price ?? 0, stripePaymentQuote?.currency || selectedBookingInvoice?.currency || selectedBooking?.booked_currency || 'EUR')}
                  </Typography>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow style={{ backgroundColor: '#F0F8FF' }}>
                        <TableCell style={{ fontWeight: 600, color: '#002D55' }}>Date</TableCell>
                        <TableCell style={{ fontWeight: 600, color: '#002D55' }}>Service</TableCell>
                        <TableCell style={{ fontWeight: 600, color: '#002D55' }}>Invoice</TableCell>
                        <TableCell style={{ fontWeight: 600, color: '#002D55' }} align="right">Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          {selectedBookingStartMoment && selectedBookingStartMoment.isValid()
                            ? `${selectedBookingStartMoment.toDate().toLocaleDateString()} ${selectedBookingStartMoment.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                            : '—'}
                        </TableCell>
                        <TableCell>{selectedBooking?.title || selectedBooking?.service_name || 'Appointment'}</TableCell>
                        <TableCell>{selectedBookingInvoice?.invoice_number ? `#${selectedBookingInvoice.invoice_number}` : 'Will be created at checkout'}</TableCell>
                        <TableCell align="right">
                          {formatMoney(selectedBookingInvoice?.total_amount ?? selectedBooking?.booked_price ?? 0, selectedBookingInvoice?.currency || selectedBooking?.booked_currency || 'EUR')}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                {stripePaymentDialogError ? (
                  <Typography style={{ color: '#d32f2f', marginTop: 12, fontSize: 13 }}>
                    {stripePaymentDialogError}
                  </Typography>
                ) : null}
                <Box style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 16 }}>
                  <Button
                    onClick={closeStripePaymentDialog}
                    variant="outlined"
                    disabled={stripePaymentWorking}
                    className={classes.cancelButton}
                    style={{ flex: 1 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handlePrepareNewCard}
                    variant="contained"
                    disabled={stripePaymentWorking}
                    className={classes.PayButton}
                    style={{ flex: 1 }}
                  >
                    {stripePaymentWorking ? 'Preparing...' : 'Continue to checkout'}
                  </Button>
                </Box>
              </Box>
            )}

            {stripePaymentDialogStage === 'review_saved_cards' && (
              <Box>
                {renderStripePaymentSummary()}
                {stripePaymentDialogError ? (
                  <Typography style={{ color: '#d32f2f', marginBottom: 12, fontSize: 13 }}>
                    {stripePaymentDialogError}
                  </Typography>
                ) : null}
                <Typography style={{ color: '#002D55', fontWeight: 700, marginBottom: 8 }}>
                  Select a saved card to charge
                </Typography>
                <Box style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {stripePaymentMethods.map((method) => {
                    const isSelected = stripePaymentSelectedMethodId === method.stripe_payment_method_id;
                    return (
                      <Button
                        key={method.id || method.stripe_payment_method_id}
                        variant={isSelected ? 'contained' : 'outlined'}
                        onClick={() => setStripePaymentSelectedMethodId(method.stripe_payment_method_id)}
                        disabled={stripePaymentWorking}
                        style={{
                          justifyContent: 'space-between',
                          textTransform: 'none',
                          borderRadius: 10,
                          padding: '10px 14px',
                          backgroundColor: isSelected ? '#002D55' : '#fff',
                          color: isSelected ? '#fff' : '#002D55',
                        }}
                      >
                        <Box style={{ textAlign: 'left' }}>
                          <Typography style={{ fontWeight: 700, color: isSelected ? '#fff' : '#002D55' }}>
                            {formatSavedCardLabel(method)}
                          </Typography>
                          <Typography style={{ fontSize: 12, color: isSelected ? '#fff' : '#666' }}>
                            Expires {method.card_exp_month}/{method.card_exp_year} {method.is_default ? '(default)' : ''}
                          </Typography>
                        </Box>
                        <Typography style={{ fontSize: 12, color: isSelected ? '#fff' : '#002D55' }}>
                          {isSelected ? 'Selected' : 'Choose'}
                        </Typography>
                      </Button>
                    );
                  })}
                </Box>
                <Box style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 16 }}>
                  <Button
                    onClick={() => setStripePaymentDialogStage('new_card_options')}
                    variant="outlined"
                    disabled={stripePaymentWorking}
                    className={classes.cancelButton}
                    style={{ flex: 1 }}
                  >
                    Use another card
                  </Button>
                  <Button
                    onClick={handleConfirmSavedCard}
                    variant="contained"
                    disabled={stripePaymentWorking || !stripePaymentSelectedMethodId}
                    className={classes.PayButton}
                    style={{ flex: 1 }}
                  >
                    {stripePaymentWorking ? 'Charging...' : 'Confirm payment'}
                  </Button>
                </Box>
              </Box>
            )}

            {stripePaymentDialogStage === 'new_card_options' && (
              <Box>
                {renderStripePaymentSummary()}
                {stripePaymentDialogError ? (
                  <Typography style={{ color: '#d32f2f', marginBottom: 12, fontSize: 13 }}>
                    {stripePaymentDialogError}
                  </Typography>
                ) : null}
                <Typography style={{ color: '#002D55', fontWeight: 700, marginBottom: 8 }}>
                  Add a new card for this payment
                </Typography>
                <Typography style={{ color: '#555', marginBottom: 12, lineHeight: 1.5 }}>
                  Stripe will create a new tokenized payment method after the payment succeeds. No raw card data is stored locally.
                </Typography>
                <Box style={{ backgroundColor: '#fff', border: '1px solid #dde6f2', borderRadius: 10, padding: 12 }}>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={stripePaymentRememberCard}
                        onChange={(event) => {
                          const checked = event.target.checked;
                          setStripePaymentRememberCard(checked);
                          if (!checked) {
                            setStripePaymentMakeDefaultCard(false);
                          }
                        }}
                        color="primary"
                      />
                    )}
                    label="Remember this card for future payments"
                  />
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={stripePaymentMakeDefaultCard}
                        onChange={(event) => setStripePaymentMakeDefaultCard(event.target.checked)}
                        color="primary"
                        disabled={!stripePaymentRememberCard}
                      />
                    )}
                    label="Make this the default card"
                  />
                </Box>
                <Box style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 16 }}>
                  <Button
                    onClick={closeStripePaymentDialog}
                    variant="outlined"
                    disabled={stripePaymentWorking}
                    className={classes.cancelButton}
                    style={{ flex: 1 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => setStripePaymentDialogStage(stripePaymentMethods.length > 0 ? 'review_saved_cards' : 'new_card_options')}
                    variant="outlined"
                    disabled={stripePaymentWorking || stripePaymentMethods.length === 0}
                    className={classes.cancelButton}
                    style={{ flex: 1 }}
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handlePrepareNewCard}
                    variant="contained"
                    disabled={stripePaymentWorking}
                    className={classes.PayButton}
                    style={{ flex: 1 }}
                  >
                    {stripePaymentWorking ? 'Preparing...' : 'Continue to card entry'}
                  </Button>
                </Box>
              </Box>
            )}

            {stripePaymentDialogStage === 'new_card_form' && stripePaymentClientSecret ? (
              <Elements key={stripePaymentClientSecret} stripe={getStripePromise()} options={{ clientSecret: stripePaymentClientSecret }}>
                <StripeBookingPaymentForm
                  invoiceNumber={stripePaymentInvoice?.invoice_number || 'Invoice'}
                  currency={stripePaymentInvoice?.currency || selectedBooking?.booked_currency || 'EUR'}
                  paymentQuote={stripePaymentQuote}
                  rememberPaymentMethod={stripePaymentRememberCard}
                  makeDefaultPaymentMethod={stripePaymentMakeDefaultCard}
                  onSuccess={handleStripePaymentSuccess}
                  onCancel={closeStripePaymentDialog}
                />
              </Elements>
            ) : stripePaymentDialogStage === 'confirmation' && stripePaymentConfirmation ? (
              <Box>
                <Box style={{ padding: 20, textAlign: 'center', backgroundColor: '#F1F8E9', borderRadius: 8, marginBottom: 16 }}>
                  <CheckCircleIcon style={{ fontSize: 40, color: '#2E7D32', marginBottom: 8 }} />
                  <Typography style={{ fontSize: 18, fontWeight: 600, color: '#2E7D32', marginBottom: 4 }}>
                    Payment Successful!
                  </Typography>
                  <Typography style={{ color: '#666', fontSize: 14 }}>
                    {stripePaymentConfirmation.confirmed
                      ? 'The appointment invoice has been confirmed as paid.'
                      : 'Stripe accepted the payment, but backend confirmation is still syncing.'}
                  </Typography>
                </Box>
                <Box style={{ padding: 12, backgroundColor: '#F5F9FF', borderRadius: 8, marginBottom: 16 }}>
                  <Typography style={{ fontWeight: 600, color: '#002D55', marginBottom: 8 }}>Transaction Details</Typography>
                  <Typography style={{ fontSize: 13, color: '#002D55' }}>
                    Invoice: {stripePaymentConfirmation.invoiceNumber ? `#${stripePaymentConfirmation.invoiceNumber}` : 'Pending invoice reference'}
                  </Typography>
                  <Typography style={{ fontSize: 13, color: '#002D55' }}>
                    Amount charged: {formatMoney(stripePaymentConfirmation.amount, stripePaymentConfirmation.currency)}
                  </Typography>
                  <Typography style={{ fontSize: 13, color: '#002D55' }}>
                    Completed at: {stripePaymentConfirmation.timestamp ? new Date(stripePaymentConfirmation.timestamp).toLocaleString() : '—'}
                  </Typography>
                </Box>
                <Box style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button onClick={handleFinalizeStripePaymentFlow} variant="contained" className={classes.PayButton}>
                    Done
                  </Button>
                </Box>
              </Box>
            ) : stripePaymentDialogStage === 'new_card_form' ? (
              <Box style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
                <Typography>Loading payment form...</Typography>
              </Box>
            ) : (
              <Box style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
                <Typography>Select a payment option to continue</Typography>
              </Box>
            )}
          </Paper>
        </DialogContent>
      </Dialog>

      {/* Long-press time adjustment popover */}
      <Popover
        open={Boolean(timeAdjustAnchor)}
        anchorEl={timeAdjustAnchor}
        onClose={closeTimeAdjust}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{ style: { borderRadius: 14, padding: '16px 20px', minWidth: 240, boxShadow: '0 6px 32px rgba(0,45,85,0.18)' } }}
      >
        <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: '0.85rem', marginBottom: 12 }}>
          Adjust Time
        </Typography>
        <Box style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[{ label: 'Start', value: timeAdjustStart, field: 'start' }, { label: 'End', value: timeAdjustEnd, field: 'end' }].map(({ label, value, field }) => (
            <Box key={field} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Typography style={{ color: '#002D55', fontSize: '0.78rem', fontWeight: 600, minWidth: 36 }}>{label}</Typography>
              <IconButton size="small" onClick={() => adjustTimeAdjust(field, -5)} style={{ backgroundColor: '#e6eaf0', borderRadius: 8, padding: 4 }}>
                <Remove fontSize="small" style={{ color: '#002D55' }} />
              </IconButton>
              <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: '0.95rem', minWidth: 44, textAlign: 'center' }}>{value}</Typography>
              <IconButton size="small" onClick={() => adjustTimeAdjust(field, 5)} style={{ backgroundColor: '#e6eaf0', borderRadius: 8, padding: 4 }}>
                <Add fontSize="small" style={{ color: '#002D55' }} />
              </IconButton>
            </Box>
          ))}
        </Box>
        <Box style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
          <Button onClick={closeTimeAdjust} size="small" style={{ backgroundColor: '#e6eaf0', color: '#002D55', borderRadius: 10, textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', boxShadow: 'none' }}>Cancel</Button>
          <Button onClick={handleSaveTimeAdjust} disabled={timeAdjustSaving} size="small" style={{ backgroundColor: '#002D55', color: '#fff', borderRadius: 10, textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', boxShadow: 'none' }}>
            {timeAdjustSaving ? 'Saving…' : 'Save'}
          </Button>
        </Box>
      </Popover>

      {/* Snackbar Notification */}
      {snackbarOpen && (
        <Box
          style={{
            position: 'fixed',
            top: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 99999,
            width: 'min(92vw, 420px)',
          }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            style={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Box>
      )}
    </div>
  );
};

export default EHRBookingCalendar;